(function(){
  'use strict';

  const CFG = window.SacrasoulCartConfig;
  if (!CFG) return;

  /* Everything this engine listens to ends with destroy(): one signal for
     every listener, so a second engine on the same page (a re-injected
     script) never answers a tap twice. */
  const _life = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let _destroyed = false;
  const _observers = [];
  function keepObserver(observer) { _observers.push(observer); return observer; }
  function listen(target, type, fn, opts) {
    if (!target) return;
    let options = opts;
    if (_life) options = (opts && typeof opts === 'object') ? Object.assign({}, opts, { signal: _life.signal }) : { capture: opts === true, signal: _life.signal };
    target.addEventListener(type, fn, options);
  }

  /* Provider-neutral behavioral events. Analytics tools may listen after consent;
     the cart engine only reports confirmed domain state and never calls a vendor. */
  const BEHAVIOR_EVENT = 'sacrasoul:behavior';
  function emitBehavior(name, properties, meta) {
    try {
      /* On the live page this path is the one taken: sacrasoul-cart-boot.js
         installs SSBehavior.emit before the engine loads, and it forwards
         meta.leaving into the event it dispatches. */
      if (window.SSBehavior && typeof window.SSBehavior.emit === 'function') {
        window.SSBehavior.emit(name, properties || {}, meta || null);
        return;
      }
      /* meta.leaving: raised from inside pagehide. The analytics bridge reads
         it off the event rather than keeping its own leaving flag, because
         listener order on window is registration order and this engine can
         register before the bridge does. */
      document.dispatchEvent(new CustomEvent(BEHAVIOR_EVENT, {
        detail: { name: name, properties: properties || {}, leaving: !!(meta && meta.leaving) }
      }));
    } catch (e) { /* analytics must never affect the cart */ }
  }

  /* ═══════════════════════════════════════
     CART ERROR TELEMETRY

     One provider-neutral cart_error event per failed cart request, capped at
     ten a session and deduped by stage, substage and reason, so a retry
     loop cannot flood a session. It carries the stage, the HTTP status when a
     response came back, why the request ended when none did, and how long it
     was on the wire. `fatal` is false for everything the shopper did not feel:
     background work, a departure, a refusal we recovered from.

     Why a request ended ('abort_reason'):
       'timeout'    our own abort timer fired, the shopper still here
       'navigation' the document was leaving (pagehide). WebKit fails a request
                    the moment a navigation STARTS and fires pagehide only when
                    the next document commits, so a statusless death waits
                    NAVIGATION_GRACE_MS before it is filed: 27 fatal refresh rows
                    in 14 days to 2026-09-09 each had a $pageleave inside the
                    next second (cart-abort-reason-proof.mjs, M and N).
       'abort'      cancelled through the signal by something else
       'network'    the wire failed with the page alive
       'timeout_recovered'  a cart read overran the timer, the one retry
                    returned and the shopper got their cart (fatal:false)
       'status_recovered'   a cart read was refused with a 502, 503 or 504
                    (http_status on the row), the one retry
                    returned and the shopper got their cart (fatal:false;
                    fetchCart, cart-abort-reason-proof.mjs V)
  ═══════════════════════════════════════ */
  var _pageLeaving = false;
  var NAVIGATION_GRACE_MS = 1500;
  var _wireDeaths = [];
  var _cartErrorsSent = 0;
  var _cartErrorsSeen = {};

  function commitCartError(props, reason) {
    try {
      var dedupeKey = props.stage + (props.substage ? ':' + props.substage : '') + (reason ? '@' + reason : '');
      if (_cartErrorsSent >= 10 || _cartErrorsSeen[dedupeKey]) return;
      _cartErrorsSeen[dedupeKey] = true;
      _cartErrorsSent += 1;
      if (reason) props.abort_reason = reason;
      /* leaving is the checkout handoff's own success path, so a death there
         can be a stranded shopper: the money path never under-counts */
      if (reason === 'navigation' && props.stage !== 'checkout_handoff') props.fatal = false;
      emitBehavior('cart_error', props, { leaving: _pageLeaving });
    } catch (e) { /* analytics must never affect the cart */ }
  }

  function flushWireDeaths(reason) {
    var held = _wireDeaths;
    _wireDeaths = [];
    held.forEach(function(entry) {
      clearTimeout(entry.timer);
      commitCartError(entry.props, reason || entry.reason);
    });
  }

  try {
    listen(window, 'pagehide', function() { _pageLeaving = true; flushWireDeaths('navigation'); }, true);
    listen(window, 'pageshow', function() { _pageLeaving = false; }, true);
  } catch (e) { /* telemetry must never affect the cart */ }

  function stampCartRequestFailure(error, startedAt, timedOut) {
    try {
      if (!error || typeof error !== 'object') return error;
      if (error.cartDurationMs == null && startedAt) error.cartDurationMs = Date.now() - startedAt;
      if (!error.cartAbortReason && error.status == null) {
        /* leaving is asked first: a stalled read that our own timer ends on a
           document already going away is a departure, not a slow Shopify */
        error.cartAbortReason = _pageLeaving ? 'navigation'
          : timedOut ? 'timeout'
          : error.name === 'AbortError' ? 'abort'
          : 'network';
      }
    } catch (e) { /* telemetry must never affect the cart */ }
    return error;
  }

  function isNavigationCancellation(error) {
    return !!(error && typeof error === 'object' && error.cartAbortReason === 'navigation');
  }

  function reportCartError(stage, error, extra) {
    try {
      var message = '';
      if (error && typeof error === 'object') message = error.message || error.name || '';
      else if (error) message = String(error);
      var props = {
        stage: stage,
        message: String(message || 'unknown').slice(0, 200),
        online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
        visibility: document.visibilityState,
        item_count: state && state.cart ? Number(state.cart.item_count || 0) : 0
      };
      if (extra && extra.substage) props.substage = String(extra.substage);
      var durationMs = extra && extra.duration_ms != null ? Number(extra.duration_ms)
        : (error && typeof error === 'object' && error.cartDurationMs != null ? Number(error.cartDurationMs) : null);
      if (durationMs != null && isFinite(durationMs)) props.duration_ms = Math.round(durationMs);
      if (extra && extra.http_status != null) props.http_status = Number(extra.http_status) || 0;
      else if (error && error.status != null) props.http_status = Number(error.status) || 0;
      if (extra && extra.line_key) props.line_key = String(extra.line_key).slice(0, 12);
      if (extra && extra.product_handle) props.product_handle = String(extra.product_handle).slice(0, 60);
      if (extra && extra.inventory_reason) props.inventory_reason = String(extra.inventory_reason);
      /* fatal=false: the failure was absorbed and the shopper's action went on */
      props.fatal = !(extra && extra.fatal === false);
      var reason = extra && extra.abort_reason ? String(extra.abort_reason)
        : (error && typeof error === 'object' && error.cartAbortReason ? String(error.cartAbortReason) : '');
      if (reason === 'network' && props.http_status == null && stage !== 'checkout_handoff') {
        if (_pageLeaving) { commitCartError(props, 'navigation'); return; }
        var entry = { props: props, reason: reason, timer: null };
        entry.timer = setTimeout(function() {
          var at = _wireDeaths.indexOf(entry);
          if (at < 0) return;
          _wireDeaths.splice(at, 1);
          commitCartError(props, reason);
        }, NAVIGATION_GRACE_MS);
        if (_wireDeaths.length >= 10) flushWireDeaths();
        _wireDeaths.push(entry);
        return;
      }
      commitCartError(props, reason);
    } catch (e) { /* analytics must never affect the cart */ }
  }

  function cartBehaviorProperties(source) {
    return {
      source: source,
      item_count: state && state.cart ? Number(state.cart.item_count || 0) : 0,
      value_cents: state && state.cart ? Number(state.cart.total_price || 0) : 0,
      currency: CFG.currency || null,
      page: window.location.pathname
    };
  }

  function cartItemBehaviorProperties(cart, variantId, quantity, source, properties, sellingPlan) {
    var expected = linePropsFingerprint({
      properties: properties || {},
      selling_plan_allocation: sellingPlan ? { selling_plan: { id: Number(sellingPlan) || sellingPlan } } : null
    });
    var item = cart && cart.items && cart.items.find(function(candidate) {
      return Number(candidate.variant_id) === Number(variantId) && linePropsFingerprint(candidate) === expected;
    });
    return {
      source: source,
      variant_id: Number(variantId) || null,
      product_id: item && item.product_id ? Number(item.product_id) : null,
      product_handle: item && item.handle ? item.handle : null,
      quantity: Number(quantity) || 1,
      unit_value_cents: item ? Number(item.final_price != null ? item.final_price : (item.price || 0)) : null,
      currency: CFG.currency || null,
      page: window.location.pathname
    };
  }

  // Free-shipping threshold and wording must follow the shopper's actual country,
  // not a cache-stale server render. Any country absent from the positive threshold
  // map is explicitly a no-free-shipping market.
  function syncShippingCopyForCountry(cc) {
    if (!cc || !CFG.strings) return;
    var eu = ['DE','ES','PT','RO','SI','HR','LV','LT','AT','BE','NL','FR','LU','IT','HU','SK','EE','DK','SE','IE','BG','GR','FI','CY','MT'];
    var region = cc === 'GB' ? 'uk' : cc === 'PL' ? 'poland' : cc === 'CZ' ? 'czechia' : cc === 'CH' ? 'switzerland' : eu.indexOf(cc) >= 0 ? 'eu' : 'generic';
    var eligible = Number(CFG.thresholdShipping) > 0;
    var vatMarket = region === 'uk' || region === 'poland' || region === 'czechia' || region === 'eu';
    var locale = ['pl', 'de', 'cs', 'nl'].indexOf(CFG.locale) >= 0 ? CFG.locale : 'en';
    /* Per locale, per region: [shippingOnUs base, toFreeShipping, freeShippingNote,
       giftIncluded, unlockFreeShipping]. Mirrors the server-rendered strings in
       snippets/cart-drawer.liquid exactly — a contract test keeps the two in sync. */
    var COPY = {
      nl: {
        uk: ['Gratis bezorging in het VK', 'tot gratis bezorging in het VK', 'Gratis verzending in het VK · incl. btw', 'Gratis bezorging in het VK en je cadeau inbegrepen', 'Hiermee is de verzending in het VK gratis'],
        poland: ['Gratis bezorging in Polen', 'tot gratis bezorging in Polen', 'Gratis verzending in Polen · incl. btw', 'Gratis bezorging in Polen en je cadeau inbegrepen', 'Hiermee is de verzending in Polen gratis'],
        czechia: ['Gratis bezorging in Tsjechië', 'tot gratis bezorging in Tsjechië', 'Gratis verzending in Tsjechië · incl. btw', 'Gratis bezorging in Tsjechië en je cadeau inbegrepen', 'Hiermee is de verzending in Tsjechië gratis'],
        switzerland: ['Gratis bezorging naar Zwitserland', 'tot gratis bezorging naar Zwitserland', 'Gratis verzending naar Zwitserland', 'Gratis bezorging naar Zwitserland en je cadeau inbegrepen', 'Hiermee is de verzending naar Zwitserland gratis'],
        eu: ['Gratis bezorging in de EU', 'tot gratis bezorging in de EU', 'Gratis verzending in de EU · incl. btw', 'Gratis bezorging in de EU en je cadeau inbegrepen', 'Hiermee is de verzending in de EU gratis'],
        generic: ['Gratis bezorging', 'tot gratis bezorging', 'Gratis verzending', 'Gratis bezorging en je cadeau inbegrepen', 'Hiermee is de verzending gratis']
      },
      en: {
        uk: ['Free UK delivery', 'to free UK delivery', 'Free UK shipping · VAT included', 'Free UK delivery and your gift, included', 'Unlocks free UK shipping'],
        poland: ['Free delivery in Poland', 'to free delivery in Poland', 'Free shipping in Poland · VAT included', 'Free delivery in Poland and your gift, included', 'Unlocks free shipping in Poland'],
        czechia: ['Free delivery in Czechia', 'to free delivery in Czechia', 'Free shipping in Czechia · VAT included', 'Free delivery in Czechia and your gift, included', 'Unlocks free shipping in Czechia'],
        switzerland: ['Free delivery to Switzerland', 'to free delivery to Switzerland', 'Free shipping to Switzerland', 'Free delivery to Switzerland and your gift, included', 'Unlocks free shipping to Switzerland'],
        eu: ['Free EU delivery', 'to free EU delivery', 'Free EU shipping · VAT included', 'Free EU delivery and your gift, included', 'Unlocks free EU shipping'],
        generic: ['Free delivery', 'to free delivery', 'Free shipping · VAT included', 'Free delivery and your gift, included', 'Unlocks free shipping']
      },
      pl: {
        uk: ['Darmowa dostawa do Wielkiej Brytanii', 'do darmowej dostawy do Wielkiej Brytanii', 'Darmowa dostawa do Wielkiej Brytanii · VAT wliczony', 'Darmowa dostawa do Wielkiej Brytanii i prezent w zestawie', 'Z tym masz darmową dostawę do Wielkiej Brytanii'],
        poland: ['Darmowa dostawa w Polsce', 'do darmowej dostawy w Polsce', 'Darmowa dostawa w Polsce · VAT wliczony', 'Darmowa dostawa w Polsce i prezent w zestawie', 'Z tym masz darmową dostawę w Polsce'],
        czechia: ['Darmowa dostawa w Czechach', 'do darmowej dostawy w Czechach', 'Darmowa dostawa w Czechach · VAT wliczony', 'Darmowa dostawa w Czechach i prezent w zestawie', 'Z tym masz darmową dostawę w Czechach'],
        switzerland: ['Darmowa dostawa do Szwajcarii', 'do darmowej dostawy do Szwajcarii', 'Darmowa dostawa do Szwajcarii', 'Darmowa dostawa do Szwajcarii i prezent w zestawie', 'Z tym masz darmową dostawę do Szwajcarii'],
        eu: ['Darmowa dostawa w UE', 'do darmowej dostawy w UE', 'Darmowa dostawa w UE · VAT wliczony', 'Darmowa dostawa w UE i prezent w zestawie', 'Z tym masz darmową dostawę w UE'],
        generic: ['Darmowa dostawa', 'do darmowej dostawy', 'Darmowa dostawa · VAT wliczony', 'Darmowa dostawa i prezent w zestawie', 'Z tym masz darmową dostawę']
      },
      de: {
        uk: ['Kostenlose Lieferung nach Großbritannien', 'bis zur kostenlosen Lieferung nach Großbritannien', 'Kostenloser Versand nach Großbritannien · MwSt. enthalten', 'Kostenlose Lieferung nach Großbritannien und dein Geschenk sind dabei', 'Schaltet kostenlosen Versand nach Großbritannien frei'],
        poland: ['Kostenlose Lieferung nach Polen', 'bis zur kostenlosen Lieferung nach Polen', 'Kostenloser Versand nach Polen · MwSt. enthalten', 'Kostenlose Lieferung nach Polen und dein Geschenk sind dabei', 'Schaltet kostenlosen Versand nach Polen frei'],
        czechia: ['Kostenlose Lieferung nach Tschechien', 'bis zur kostenlosen Lieferung nach Tschechien', 'Kostenloser Versand nach Tschechien · MwSt. enthalten', 'Kostenlose Lieferung nach Tschechien und dein Geschenk sind dabei', 'Schaltet kostenlosen Versand nach Tschechien frei'],
        switzerland: ['Kostenlose Lieferung in die Schweiz', 'bis zur kostenlosen Lieferung in die Schweiz', 'Kostenloser Versand in die Schweiz', 'Kostenlose Lieferung in die Schweiz und dein Geschenk sind dabei', 'Schaltet kostenlosen Versand in die Schweiz frei'],
        eu: ['Kostenlose EU-Lieferung', 'bis zur kostenlosen EU-Lieferung', 'Kostenloser EU-Versand · MwSt. enthalten', 'Kostenlose EU-Lieferung und dein Geschenk sind dabei', 'Schaltet kostenlosen EU-Versand frei'],
        generic: ['Kostenlose Lieferung', 'bis zur kostenlosen Lieferung', 'Kostenloser Versand', 'Dein Geschenk ist enthalten', 'Schaltet kostenlosen Versand frei']
      },
      cs: {
        uk: ['Doprava zdarma do Velké Británie', 'do dopravy zdarma do Velké Británie', 'Doprava zdarma do Velké Británie · DPH zahrnuto', 'Doprava zdarma do Velké Británie a dárek jsou zahrnuty', 'Díky tomu máš dopravu zdarma do Velké Británie'],
        poland: ['Doprava zdarma do Polska', 'do dopravy zdarma do Polska', 'Doprava zdarma do Polska · DPH zahrnuto', 'Doprava zdarma do Polska a dárek jsou zahrnuty', 'Díky tomu máš dopravu zdarma do Polska'],
        czechia: ['Doprava zdarma po Česku', 'do dopravy zdarma po Česku', 'Doprava zdarma po Česku · DPH zahrnuto', 'Doprava zdarma po Česku a dárek jsou zahrnuty', 'Díky tomu máš dopravu zdarma po Česku'],
        switzerland: ['Doprava zdarma do Švýcarska', 'do dopravy zdarma do Švýcarska', 'Doprava zdarma do Švýcarska', 'Doprava zdarma do Švýcarska a dárek jsou zahrnuty', 'Díky tomu máš dopravu zdarma do Švýcarska'],
        eu: ['Doprava zdarma v EU', 'do dopravy zdarma v EU', 'Doprava zdarma v EU · DPH zahrnuto', 'Doprava zdarma v EU a dárek jsou zahrnuty', 'Díky tomu máš dopravu zdarma v EU'],
        generic: ['Doprava zdarma', 'do dopravy zdarma', 'Doprava zdarma', 'Dárek je zahrnut', 'Díky tomu máš dopravu zdarma']
      }
    };
    var NOTE = {
      en: ['Shipping calculated at checkout', 'Shipping calculated at checkout · VAT included'],
      pl: ['Koszt dostawy liczony przy kasie', 'Koszt dostawy liczony przy kasie · VAT wliczony'],
      de: ['Versand wird an der Kasse berechnet', 'Versand wird an der Kasse berechnet · MwSt. enthalten'],
      cs: ['Doprava se vypočítá u pokladny', 'Doprava se vypočítá u pokladny · DPH zahrnuto'],
      nl: ['Verzendkosten bij het afrekenen', 'Verzendkosten bij het afrekenen · incl. btw']
    };
    var GIFT_ONLY = { en: 'Your gift is included', pl: 'Prezent w zestawie', de: 'Dein Geschenk ist enthalten', cs: 'Dárek je zahrnut', nl: 'Je cadeau is inbegrepen' };
    var c = COPY[locale][region];
    CFG.strings.shippingOnUs = c[0] + ' ·';
    CFG.strings.toFreeShipping = c[1];
    CFG.strings.freeShippingNote = c[2];
    CFG.strings.giftIncluded = eligible ? c[3] : GIFT_ONLY[locale];
    CFG.strings.unlockFreeShipping = c[4];
    CFG.strings.shippingCalcNote = locale === 'pl' && region === 'poland' ? 'Dostawa od 12,99 zł · VAT wliczony' : NOTE[locale][vatMarket ? 1 : 0];
  }

  try {
    var _serverCC = CFG.country;
    var _cc = (window.Shopify && window.Shopify.country) || _serverCC;
    if (_cc && CFG.thresholdMap) {
      /* thresholdMap values are denominated in each country's presentment
         currency, but every number this file paints wears the CART's currency
         symbol (the Liquid render derived it from cart.currency). If the two
         diverge — a market switch with a sticky cart currency — the override
         would pair a GBP threshold with a zł symbol and tell a 60 zł cart it
         earned £99 free shipping. Only override when the currencies agree;
         otherwise the server-rendered pair stays, which is at least always
         internally consistent. */
      var _ccCurrency = { GB: 'GBP', PL: 'PLN', CZ: 'CZK' }[_cc] || 'EUR';
      if (!CFG.currency || CFG.currency === _ccCurrency) {
        CFG.thresholdShipping = Object.prototype.hasOwnProperty.call(CFG.thresholdMap, _cc) ? CFG.thresholdMap[_cc] : 0;
        CFG.country = _cc;
        if (_serverCC !== _cc) syncShippingCopyForCountry(_cc);
      }
    }
  } catch (e) {}
  const INITIAL_CART = normalizeCart(window.SacrasoulCartInitialCart || {
    items: [],
    item_count: 0,
    total_price: 0,
    cart_level_discount_applications: []
  });

  /* ── localStorage cart cache for instant render on return visits ── */
  const _CART_CACHE_KEY = 'sacrasoul_cart_cache';
  function saveCartCache(cart) {
    try { localStorage.setItem(_CART_CACHE_KEY, JSON.stringify(cart)); } catch (e) {}
  }
  function loadCartCache() {
    try {
      const raw = localStorage.getItem(_CART_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && parsed.items ? normalizeCart(parsed) : null;
    } catch (e) { return null; }
  }

  /* ═══════════════════════════════════════
     DOM REFS
  ═══════════════════════════════════════ */
  const $ = (s, p) => (p || document).querySelector(s);
  const $$ = (s, p) => [...(p || document).querySelectorAll(s)];

  function localeText(en, pl, cs, de, nl) {
    if (CFG.locale === 'pl') return pl;
    if (CFG.locale === 'cs') return cs || en;
    if (CFG.locale === 'de') return de || en;
    if (CFG.locale === 'nl') return nl || en;
    return en;
  }

  function catalogText(key, name) {
    const template = CFG.strings && CFG.strings[key];
    if (!template) return name || '';
    return String(template).replace('{name}', name || '');
  }

  /* Human label for a subscription line. Appstle's legacy plans use "30 Days";
     Keep It Coming uses "Every month" / "Every N months". Mirrors the Liquid
     first paint in snippets/cart-drawer.liquid.
     Takes the selling_plan object (a bare name still works). The NAME is
     translatable, so the interval is read first from what does not change with
     language: a preview's months (data-plan-months on the r45 radio), else the
     option value Keep It Coming encodes as "1MONTH1MONTH…" (see
     snippets/selling-plan-months.liquid). The English name is the fallback. */
  function planDeliveryLabel(plan) {
    var p = plan && typeof plan === 'object' ? plan : { name: plan };
    var raw = String(p.name || '').trim();
    var n = Number(p.months) || 0;
    var opt = p.options && p.options[0] && p.options[0].value;
    var optMonths = /^\s*(\d+)\s*MONTH/.exec(String(opt || ''));
    if (!n && optMonths) n = Number(optMonths[1]) || 0;
    var days = n ? null : /^(\d+)\s+Days?$/i.exec(raw);
    if (days) {
      var d = days[1];
      if (CFG.locale === 'pl') return 'Dostawa co ' + d + ' dni';
      /* Czech counts 1 / 2-4 / 5+ (den, dny, dní) and the "every" word agrees with it */
      if (CFG.locale === 'cs') return Number(d) === 1 ? 'Doručení každý den' : (Number(d) >= 2 && Number(d) <= 4 ? 'Doručení každé ' + d + ' dny' : 'Doručení každých ' + d + ' dní');
      if (CFG.locale === 'de') return 'Lieferung alle ' + d + ' Tage';
      if (CFG.locale === 'nl') return Number(d) === 1 ? 'Levering elke dag' : 'Levering elke ' + d + ' dagen';
      return 'Delivers every ' + d + ' days';
    }
    var months = n ? null : /^Every(?:\s+(\d+))?\s+months?$/i.exec(raw);
    if (!n && !months) return raw;
    if (!n) n = Number(months[1] || 1);
    if (CFG.locale === 'pl') return n === 1 ? 'Dostawa co miesiąc' : 'Dostawa co ' + n + (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? ' miesiące' : ' miesięcy');
    if (CFG.locale === 'cs') return n === 1 ? 'Doručení každý měsíc' : (n >= 2 && n <= 4 ? 'Doručení každé ' + n + ' měsíce' : 'Doručení každých ' + n + ' měsíců');
    if (CFG.locale === 'de') return n === 1 ? 'Lieferung jeden Monat' : 'Lieferung alle ' + n + ' Monate';
    if (CFG.locale === 'nl') return n === 1 ? 'Levering elke maand' : 'Levering elke ' + n + ' maanden';
    return n === 1 ? 'Delivers every month' : 'Delivers every ' + n + ' months';
  }

  function getCheckoutUrl() {
    if (window.__sacrasoulEU) {
      /* a third-party tag could set this global; only a same-origin path may steer checkout */
      var eu = window.__sacrasoulEUCheckoutUrl;
      return (typeof eu === 'string' && /^\/(?![\/\\])/.test(eu)) ? eu : '/pages/checkout';
    }
    return CFG.checkoutUrl;
  }

  function addedToCartText(name) {
    return catalogText('addedToCart', name);
  }

  function removedFromCartText(name) {
    return catalogText('removedFromCart', name);
  }

  const drawer    = $('#sacrasoul-cart-drawer');
  const overlay   = $('#sacrasoul-overlay');
  const body      = $('#sacrasoul-body');
  /* self-heal: a stale cached boot shim once injected its skeleton by wiping
     #sacrasoul-body, destroying this node before we could bind it — recreate
     it so the cart still renders (gift/upsell UI degrades for the session) */
  let itemsWrap = $('#sacrasoul-cart-items');
  if (!itemsWrap && body) {
    itemsWrap = document.createElement('div');
    itemsWrap.id = 'sacrasoul-cart-items';
    itemsWrap.className = 'cart-items';
    body.textContent = '';
    body.appendChild(itemsWrap);
  }
  /* The store-wide pre-order catalog and this engine load in parallel. Keep
     Liquid's product-truth note through any early JS repaint; once the catalog
     lands, its complete handle list takes over and the rows repaint once. */
  const _liquidDispatchHandles = new Set();
  if (itemsWrap) {
    $$('.item[data-handle]', itemsWrap).forEach(function(row) {
      if (row.querySelector('.item__dispatch-note')) _liquidDispatchHandles.add(row.getAttribute('data-handle'));
    });
  }
  const emptyEl   = $('#sacrasoul-empty');
  const footerEl  = $('#sacrasoul-footer');
  const headerCt  = $('#sacrasoul-header-count');
  const ctaBadge  = $('#sacrasoul-cta-badge');
  const subtotalEl= $('#sacrasoul-subtotal');
  const journeyMsg= $('#sacrasoul-journey-msg');
  const journeyFill=$('#sacrasoul-journey-fill');
  const journeyEl = $('#sacrasoul-journey');
  const journeyTicks=$('#sacrasoul-journey-ticks');
  const giftPick  = $('#sacrasoul-gift-pick');
  const upsellTrack=$('#sacrasoul-upsell-track');
  const upsellTitle=$('#sacrasoul-upsell-title');
  const undoToast = $('#sacrasoul-undo-toast');
  const undoMsg   = $('#sacrasoul-undo-msg');
  const undoBtn   = $('#sacrasoul-undo-btn');
  const undoProg  = $('#sacrasoul-undo-progress');
  const dispatchMsg=$('#sacrasoul-dispatch-msg');
  const shippingNote=$('#sacrasoul-shipping-note');
  const discountWrap=$('#sacrasoul-discount');
  const welcomeDiscount=$('#sacrasoul-welcome-discount');
  const checkoutBtn=$('#sacrasoul-checkout');
  const ctaLabel  = drawer ? drawer.querySelector('.cta__label') : null;
  const ctaStatus = $('#sacrasoul-cta-status');
  const giftLabel = giftPick ? $('.gift-pick__label', giftPick) : null;
  const giftHint  = $('#sacrasoul-gift-hint');
  const closeBtn  = $('#sacrasoul-close');
  const divider   = $('#sacrasoul-divider');
  const cartDiscountsJs = $('#sacrasoul-cart-discounts-js');
  const cartDiscountsLiquid = $('#sacrasoul-cart-discounts');

  if (!drawer) return;

  /* ── Screen reader announcement element ──
     Lives on document.body so it remains audible even when the drawer
     is aria-hidden. Inside the drawer some screen readers silently drop
     live-region updates from aria-hidden subtrees. */
  let _announceEl = null;
  function initAnnounce() {
    _announceEl = document.createElement('div');
    _announceEl.setAttribute('aria-live', 'polite');
    _announceEl.setAttribute('aria-atomic', 'true');
    _announceEl.setAttribute('role', 'status');
    _announceEl.className = 'sr-only sacrasoul-cart-announce';
    _announceEl.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
    document.body.appendChild(_announceEl);
  }
  function announce(text) {
    if (!_announceEl) return;
    _announceEl.textContent = '';
    requestAnimationFrame(function() { _announceEl.textContent = text; });
  }

  /* ═══════════════════════════════════════
     DISCOUNT CODE (cart drawer)
  ═══════════════════════════════════════ */
  const WELCOME_CODE = 'SACRA10';
  const WELCOME_CODE_KEY = 'ss_welcome_code';
  const WELCOME_REMOVED_KEY = 'ss_welcome_discount_removed';
  const WELCOME_MEMORY_MS = 30 * 24 * 60 * 60 * 1000;
  let welcomeApplyAttempted = false;

  function rememberedWelcomeCode() {
    try {
      var stored = localStorage.getItem(WELCOME_CODE_KEY);
      if (stored) {
        try {
          var remembered = JSON.parse(stored);
          if (remembered && remembered.expiresAt && remembered.expiresAt <= Date.now()) {
            forgetRememberedWelcomeCode();
          } else if (remembered && remembered.code) {
            return String(remembered.code).toUpperCase();
          }
        } catch (legacyValue) {
          /* Migrate the original plain marker onto the same 30-day lifetime. */
          if (stored.toUpperCase() === WELCOME_CODE) {
            try {
              localStorage.setItem(WELCOME_CODE_KEY, JSON.stringify({
                code: WELCOME_CODE, expiresAt: Date.now() + WELCOME_MEMORY_MS
              }));
            } catch (e) {}
          }
          return stored.toUpperCase();
        }
      }
    } catch (e) {}
    var match = document.cookie.match(new RegExp('(?:^|; )' + WELCOME_CODE_KEY + '=([^;]+)'));
    return match ? decodeURIComponent(match[1]).toUpperCase() : '';
  }

  function forgetRememberedWelcomeCode() {
    try { localStorage.removeItem(WELCOME_CODE_KEY); } catch (e) {}
    document.cookie = WELCOME_CODE_KEY + '=;Max-Age=0;path=/;SameSite=Lax';
  }

  function welcomeCodeRemovedThisSession() {
    try { return sessionStorage.getItem(WELCOME_REMOVED_KEY) === '1'; }
    catch (e) { return false; }
  }

  function cartHasWelcomeCode(cart) {
    if (!cart) return false;
    if ((cart.discount_codes || []).some(function(d) {
      return d && d.code && d.code.toUpperCase() === WELCOME_CODE && d.applicable !== false;
    })) return true;
    if ((cart.cart_level_discount_applications || []).some(function(d) {
      return d && d.title && d.title.toUpperCase() === WELCOME_CODE;
    })) return true;
    return (cart.items || []).some(function(item) {
      return (item.line_level_discount_allocations || []).some(function(a) {
        var application = a && a.discount_application;
        return application && application.title && application.title.toUpperCase() === WELCOME_CODE;
      });
    });
  }

  function syncWelcomeDiscountOffer(cart) {
    /* The cart email ask (assets/sacrasoul-cart-email.js) keys its visibility
       off the same authoritative cart. It must never break a render. */
    try { if (window.SSCartEmail && typeof window.SSCartEmail.sync === 'function') window.SSCartEmail.sync(cart); } catch (e) {}
    if (!welcomeDiscount) return;
    var eligible = rememberedWelcomeCode() === WELCOME_CODE
      && cart && Number(cart.item_count || 0) > 0
      && !hasActiveDiscount(cart)
      && !cartIsOnlySets(cart)
      && !welcomeCodeRemovedThisSession()
      && !welcomeApplyAttempted;
    welcomeDiscount.hidden = !eligible;
  }

  (function initDiscountToggle() {
    const toggle = $('#sacrasoul-discount-toggle');
    const form   = $('#sacrasoul-discount-form');
    const input  = $('#sacrasoul-discount-input');
    const btn    = $('#sacrasoul-discount-btn');
    const msg    = $('#sacrasoul-discount-msg');
    const welcome = $('#sacrasoul-welcome-discount');
    if (!toggle || !form || !input || !btn) return;

    listen(toggle, 'click', function() {
      var expanded = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', !expanded);
      form.classList.toggle('is-visible');
      if (!expanded) input.focus();
    });

    function codeApplied(cart, codeUpper) {
      if ((cart.cart_level_discount_applications || []).some(function(d) {
        return d && d.title && d.title.toUpperCase() === codeUpper;
      })) return true;
      return (cart.items || []).some(function(item) {
        return (item.line_level_discount_allocations || []).some(function(a) {
          return a && a.discount_application && a.discount_application.title && a.discount_application.title.toUpperCase() === codeUpper;
        });
      });
    }

    /* A code is the one change the drawer cannot compute, so it is the one
       place that waits: the write joins the cart queue like every other, and
       only the code row is busy while it does. The total simply updates when
       Shopify's answer lands. */
    function applyDiscount() {
      var code = (input.value || '').trim().toUpperCase();
      if (!code) { input.focus(); return; }
      /* a code must be judged against the cart the customer meant */
      commitFold();
      flushQty();
      clearStatus('error');
      noteCustomerEdit();
      btn.disabled = true;
      input.disabled = true;
      input.value = code;
      msg.style.display = 'none';
      var task = writeCartUpdate({ discount: code }, 'discount').then(function(cart) {
        /* Shopify returns the code in discount_codes regardless of validity.
           applicable === false means: known code exists but doesn't apply to this cart
           (e.g. minimum spend not met, expired, customer-specific, products excluded).
           absent means: code is wholly unrecognised. */
        var codeEntry = (cart.discount_codes || []).find(function(d) {
          return d && d.code && d.code.toUpperCase() === code;
        });
        if (codeApplied(cart, code)) {
          if (code === WELCOME_CODE) {
            forgetRememberedWelcomeCode();
            if (welcome) welcome.hidden = true;
            try { sessionStorage.removeItem(WELCOME_REMOVED_KEY); } catch (e) {}
          }
          msg.className = 'drawer-discount__msg drawer-discount__msg--success';
          msg.textContent = localeText('Code applied', 'Kod zastosowany', 'Kód použit', 'Code angewendet', 'Code toegepast');
          msg.style.display = '';
          setStatus(msg.textContent, 'success', 'discount');
          input.value = '';
          /* Auto-collapse, but cancel if the user starts entering a second code. */
          if (form._collapseTimer) clearTimeout(form._collapseTimer);
          form._collapseTimer = setTimeout(function() {
            if (document.activeElement === input) return;
            toggle.setAttribute('aria-expanded', 'false');
            form.classList.remove('is-visible');
            msg.style.display = 'none';
            form._collapseTimer = null;
          }, 1500);
          return null;
        }
        msg.className = 'drawer-discount__msg drawer-discount__msg--error';
        var clear = null;
        if (codeEntry && codeEntry.applicable === false) {
          /* code exists but doesn't apply to this cart: a cart of sets only
             (see SETS AND CODES), else usually a minimum spend not met */
          msg.textContent = cartIsOnlySets(cart) ? localeText(
            'Sets are already discounted, so codes don\u2019t apply.',
            'Zestawy są już przecenione, więc kody ich nie obejmują.',
            'Sady jsou už zlevněné, takže se na ně kódy nevztahují.',
            'Sets sind bereits reduziert, darum gelten keine Codes.',
            'Sets zijn al afgeprijsd, dus codes gelden er niet voor.'
          ) : localeText(
            'Code does not apply to this cart',
            'Kod nie obejmuje tego koszyka',
            'Kód se na tento košík nevztahuje',
            'Der Code gilt nicht für diesen Warenkorb',
            'De code geldt niet voor deze winkelwagen'
          );
          /* clear the inapplicable code through the same queue, so the next
             attempt can never race it */
          clear = writeCartUpdate({ discount: '' }, 'discount').catch(function() {});
        } else {
          msg.textContent = localeText('Invalid code', 'Nieprawidłowy kod', 'Neplatný kód', 'Ungültiger Code', 'Ongeldige code');
        }
        if (code === WELCOME_CODE) {
          forgetRememberedWelcomeCode();
          if (welcome) welcome.hidden = true;
          if (welcomeApplyAttempted) {
            toggle.setAttribute('aria-expanded', 'true');
            form.classList.add('is-visible');
          }
        }
        msg.style.display = '';
        /* the message already sits under the input the customer just typed
           into. Mirroring it above the checkout button printed the same
           sentence twice, ~120px apart. Mirror only when the panel has
           collapsed and the inline copy is no longer on screen. */
        if (!form.classList.contains('is-visible')) setStatus(msg.textContent, 'error', 'discount');
        return clear;
      }, function(error) {
        msg.className = 'drawer-discount__msg drawer-discount__msg--error';
        msg.textContent = localeText('Something went wrong', 'Coś poszło nie tak', 'Něco se nepovedlo', 'Etwas ist schiefgelaufen', 'Er ging iets mis');
        if (code === WELCOME_CODE && welcomeApplyAttempted) {
          if (welcome) welcome.hidden = true;
          toggle.setAttribute('aria-expanded', 'true');
          form.classList.add('is-visible');
        }
        msg.style.display = '';
        /* a shopper's failed code attempt must leave a trace somewhere */
        reportCartError('discount_apply', error);
      }).then(function() {
        btn.disabled = false;
        input.disabled = false;
        if (welcome) welcome.disabled = false;
      });
      task.catch(function() {});
    }

    if (welcome) {
      listen(welcome, 'click', function() {
        welcomeApplyAttempted = true;
        input.value = WELCOME_CODE;
        welcome.disabled = true;
        welcome.hidden = true;
        applyDiscount();
      });
    }

    listen(btn, 'click', applyDiscount);
    listen(input, 'keypress', function(e) {
      if (e.key === 'Enter') applyDiscount();
    });
    /* If the user starts typing a second code, kill the auto-collapse timer. */
    listen(input, 'focus', function() {
      if (form._collapseTimer) {
        clearTimeout(form._collapseTimer);
        form._collapseTimer = null;
      }
    });
  })();

  /* The popup applies through Shopify's discount URL. Read the cart so the
     SACRA10 badge and total appear without the shopper touching anything. */
  listen(document, 'sacrasoul:discount-applied', function(event) {
    var code = event && event.detail && String(event.detail.code || '').toUpperCase();
    if (code !== WELCOME_CODE) return;
    if (welcomeDiscount) welcomeDiscount.hidden = true;
    readCart({}).catch(function() {});
  });

  /* Remove a discount code by sending an empty string to Shopify */
  function removeDiscount() {
    commitFold();
    flushQty();
    noteCustomerEdit();
    var removingWelcomeCode = cartHasWelcomeCode(state && state.cart);
    clearStatus('error');
    writeCartUpdate({ discount: '' }, 'discount').then(function() {
      if (removingWelcomeCode) {
        try { sessionStorage.setItem(WELCOME_REMOVED_KEY, '1'); } catch (e) {}
      }
      setStatus(localeText('Discount code removed', 'Kod rabatowy usunięty', 'Slevový kód odstraněn', 'Rabattcode entfernt', 'Kortingscode verwijderd'), 'success', 'discount');
    }, function(e) {
      reportCartError('discount_remove', e);
      if (!isNavigationCancellation(e)) setStatus(CFG.strings.cartError, 'error', 'error');
    });
  }


  /* ═══════════════════════════════════════
     STATE
  ═══════════════════════════════════════ */
  let state = {
    /* The cart the drawer shows: the last server cart with every pending
       intent applied (see buildView). Never a promise about the future — it
       is what the customer asked for, drawn now, synced behind. */
    cart: null,
    undoTimer: null,
    undoData: null,
    selectedGift: -1,
    /* Which tier-2 reward the customer is taking: 'amber' (the tier-2 gift on its
       own) or 'both' (the two tier-1 gifts together). Only consulted above the
       tier-2 threshold; below it the tier-1 selection above is the whole story. */
    selectedGift2: (CFG.gift2Chosen === 'both' ? 'both' : 'amber'),
    /* true once the customer has tapped a tier-2 chip this session: from then on the
       server's gift line may not overwrite the choice, or the tap would revert
       itself before the swap is written. */
    gift2Explicit: false,
    /* the same, for the tier-1 chips */
    giftExplicit: false,
    giftInCart: false,
    checkoutPending: false,
    status: { source: null, tone: null, text: '' }
  };

  let _previewId = 0;
  let _upsellRenderTimer = null;
  let _upsellSignature = '';
  let _upsellOrder = [];
  let _upsellError = null;
  let _upsellRows = 2;
  /* What the free-delivery nudge on screen is offering, so a confirmed add
     from its row can be told apart from any other rail add. Set when the row
     is painted; the key dedupes the shown event within one open. */
  let _nudgeLive = null;
  let _nudgeShownKey = '';
  let _upsellPaintFrame = 0;
  const MAX_UPSELL_ROWS = 4;
  const UPSELL_ROW_H = 77; /* measured: a pairing row with a two-line note */
  let _offsetSyncFrame = null;
  let _undoHideTimer = null;
  let _onOffline = null;
  let _onOnline = null;
  let _suppressItemClickUntil = 0;
  /* A +/− tap names its line and direction so the digit rolls the way it
     went; a server repaint carries no hint and redraws in silence. */
  let _rollHint = null;

  /* ═══════════════════════════════════════
     UTILITIES
  ═══════════════════════════════════════ */
  function esc(s) { return s ? String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;') : ''; }

  /* Format cents in cart currency. Polish and Czech place the symbol after
     the amount with a comma decimal and a non-breaking thousands space.
     English-style locales keep prefix symbol with comma thousands separator. */
  /* Money speaks the cart's own currency. The page's market and the cart's
     currency can diverge (observed live, 2026-07-26: a localization=GB page
     over a cart_currency=PLN cart rendered "£498.00" for a 498 zł cart — a
     four-times lie in the largest figure on screen). The amounts always come
     from the cart, so the SYMBOL must too. A background re-localization
     (maybeHealCurrency) heals the divergence; this keeps every
     painted figure true in the meantime. */
  var CURRENCY_PRESENT = {
    GBP: { symbol: '\u00a3' }, EUR: { symbol: '\u20ac' }, USD: { symbol: '$' },
    CHF: { symbol: 'CHF\u00a0' },
    CAD: { symbol: 'CA$' },
    PLN: { suffix: 'z\u0142' }, CZK: { suffix: 'K\u010d' }
  };
  /* ONE MONEY SHAPE PER LANGUAGE (Sho, 2026-09-23: "German prices print '€69'
     where German convention is '69 €'"). The four translated languages write
     money their own way whatever the currency; English and any other language keep
     the currency's own Shopify shape below, unchanged. This table is the twin of
     snippets/money.liquid, the Liquid door every server-rendered price goes
     through, and tests/test_one_price_shape.py holds the two to the same rows:
       de  69 €   69,90 €   1.890 €        nl  € 69   € 69,90   € 1.890
       cs  69 €   1 890 Kč            pl  108 zł   1234 zł   12 345 zł
     The gap before or after the sign and the Czech/Polish thousands gap are
     U+00A0. Polish groups from five digits (CLDR minimum grouping 2). */
  var MONEY_BY_LANGUAGE = {
    de: { group: '.', signFirst: false, groupFrom: 1000 },
    nl: { group: '.', signFirst: true, groupFrom: 1000 },
    cs: { group: '\u00a0', signFirst: false, groupFrom: 1000 },
    pl: { group: '\u00a0', signFirst: false, groupFrom: 10000 }
  };
  var MONEY_SIGN = { EUR: '\u20ac', GBP: '\u00a3', USD: '$', PLN: 'z\u0142', CZK: 'K\u010d', CHF: 'CHF' };
  /* shapeMoney(cents, currency, locale, pageSymbol) -- the one JS money
     formatter. Exposed as window.SacrasoulMoney for the PDP scripts.
     pageSymbol is the symbol the page was rendered with, used only for the
     page's own currency. */
  function shapeMoney(cents, cur, locale, pageSymbol) {
    var n = Math.round(Number(cents) || 0);
    var neg = n < 0 ? '-' : '';
    n = Math.abs(n);
    var whole = Math.floor(n / 100);
    var frac = String(n % 100).padStart(2, '0');
    var native = MONEY_BY_LANGUAGE[String(locale || 'en').slice(0, 2).toLowerCase()];
    if (native) {
      var digits = whole >= native.groupFrom ? String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, native.group) : String(whole);
      var amount = neg + digits + (frac === '00' ? '' : ',' + frac);
      var sign = String(MONEY_SIGN[cur] || pageSymbol || cur || '').trim();
      return native.signFirst ? sign + '\u00a0' + amount : amount + '\u00a0' + sign;
    }
    var present = CURRENCY_PRESENT[cur] || {};
    /* a currency this table does not know, on a page whose symbol belongs to
       another currency: never borrow the page symbol (the 2026-07-26 fault) */
    if (!CURRENCY_PRESENT[cur] && !pageSymbol && typeof Intl !== 'undefined' && Intl.NumberFormat) {
      try { return new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: cur }).format((neg ? -n : n) / 100); } catch (e) {}
    }
    /* SEPARATORS FOLLOW THE CURRENCY, NOT THE LANGUAGE -- for English (corrected
       2026-09-20). Fetched from live, sacrasoul.com/en-eu/products/amber-hojari
       declares lang="en" and renders EUR 0,97 and EUR 19,00 -- comma decimal,
       thirteen occurrences to zero. Shopify formats money per market, so an
       English page on the euro market uses euro separators. Every currency the
       store sells in, read off live on an English page:
         EUR  -> EUR 1.245,00   period thousands, comma decimal, symbol first
         GBP  -> GBP 1,245.00   comma thousands, period decimal, symbol first
         PLN  -> 1.245,00 zl    period thousands, comma decimal, symbol last
         CZK  -> 1.245,00 Kc    period thousands, comma decimal, symbol last
       USD, CAD and CHF keep the English form; none is a published market today,
       and inventing a convention for a currency nobody has seen would be a guess
       dressed as a fix. */
    var COMMA_DECIMAL = { EUR: 1, PLN: 1, CZK: 1, CHF: 1 };
    var commaDecimal = !!COMMA_DECIMAL[cur];
    var thousands = commaDecimal ? '.' : ',';
    var decimal = commaDecimal ? ',' : '.';
    var grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    /* WHOLE AMOUNTS CARRY NO ",00" (2026-09-23). Every page prints
       money_without_trailing_zeros ("108 zł", "£22") while this drawer printed
       "108,00 zł" and "£22.00" for the same line a tap later. The fraction now
       shows only when it is not zero ("12,99 zł"), the Liquid filter's rule. */
    var tail = frac === '00' ? '' : decimal + frac;

    if (present.suffix) {
      return neg + grouped + tail + '\u00a0' + present.suffix;
    }
    var symbol = pageSymbol || present.symbol || '';
    return String(symbol).trim() + neg + grouped + tail;
  }
  window.SacrasoulMoney = shapeMoney;

  function formatMoney(cents) {
    var cur = (state.cart && state.cart.currency) || CFG.currency;
    return shapeMoney(Math.max(0, Number(cents) || 0), cur, CFG.locale, cur === CFG.currency ? CFG.symbol : '');
  }

  function pluralize(n, s, p) {
    /* Polish count strings follow the last digit except for the 12–14
       genitive-plural exception. Czech keeps its cardinal 1 / 2–4 / 5+ rule. */
    if (CFG.locale === 'pl') {
      const mod10 = Math.abs(n) % 10;
      const mod100 = Math.abs(n) % 100;
      if (mod10 === 1 && mod100 !== 11) return CFG.strings.item;
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return CFG.strings.items24;
      return CFG.strings.items5;
    }
    if (CFG.locale === 'cs') {
      if (n === 1) return CFG.strings.item;
      if (n >= 2 && n <= 4) return CFG.strings.items24;
      return CFG.strings.items5;
    }
    /* German has no branch here and fell through to the English default, so a
       German drawer read "3 items" under German headings -- and the header was
       server-rendered "3 Artikel" first, which this then overwrote on hydration.
       Artikel is invariant in the plural, so one form serves every count. */
    if (CFG.locale === 'de') return CFG.strings.item;
    /* Dutch is not invariant the way German is — artikel / artikelen — so it
       needs the singular and one plural, not one form. Without a branch it
       fell to the English default and appended an s: "3 items" on a Dutch
       drawer, under a header the Liquid had server-rendered in Dutch. */
    if (CFG.locale === 'nl') return n === 1 ? CFG.strings.item : CFG.strings.items5;
    return n === 1 ? s : (p || s + 's');
  }

  const _reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const DUR_FEEDBACK = 120;
  const DUR_TRANSITION = 220;
  const DUR_REVEAL = 420;
  function haptic(ms) { if (_reducedMotion) return; try { navigator.vibrate && navigator.vibrate(ms || 8); } catch(e) {} }

  /* The body is read inside the request's own abort budget (readBody): a
     response whose headers arrive and whose body then stalls ends at the
     budget, lock released, instead of holding the queue for ever. */
  async function readBody(res) {
    if (res && res.__ssText === undefined && typeof res.text === 'function') res.__ssText = await res.text();
    return res;
  }

  async function parseJsonResponse(res) {
    let data = null;
    try {
      data = res.__ssText !== undefined ? JSON.parse(res.__ssText) : await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok || (data && data.status && data.status >= 400)) {
      const err = new Error((data && (data.description || data.message || data.errors)) || 'Cart request failed');
      err.data = data;
      err.status = res.status || (data && data.status) || 0;
      /* The window a 429 declared rides the error to whoever catches it —
         per response, never through shared state a later queued 429 could
         overwrite before the catch runs. */
      if (res.__ssDeclaredThrottleMs != null) err.declaredThrottleMs = res.__ssDeclaredThrottleMs;
      throw err;
    }
    return data;
  }

  /* Shopify answers 422 when a line can't be added (sold out, unpublished,
     stock cap). For the drawer's one-tap adds this is the stale-page case:
     the rendered catalog predates the sellout. */
  function isUnprocessableAdd(err) {
    if (!err) return false;
    return Number(err.status) === 422 || Number(err.data && err.data.status) === 422;
  }

  /* Shopify writes the "already sold out" 422 body in the STOREFRONT's language,
     not in English: "The product '…' is already sold out." on sacrasoul.com, but
     "Produkt … zostal juz wyprzedany" on sacrasoul.pl, "Produkt … je jiz
     vyprodan" on CS, "Das Produkt … ist bereits ausverkauft" on DE and "Het
     product … is al uitverkocht" on NL. Only the
     English form was ever matched, and addInventoryVerdict's other sellout limb
     needs a live re-check that CONTRADICTS the page — which an oversell-policy
     bundle never gives, because /products/<handle>.js keeps answering
     available:true at zero stock while /cart/add.js refuses the line.

     So on the PL storefront the whole sellout limb was unreachable for exactly
     the products this shop sells that way. The verdict came back null, nothing
     set err.ssSoldOut, pdp-atc.js never retired the buy control, the
     recommendation rail kept the row addable on the next page, and the shopper
     tapped a dead button until they gave up — while the refusal was counted as a
     transport fault (PostHog 2026-09-19: ss_add_to_cart_failed, 5 on trinity in
     one session, 2 on first-flame, 1 on sacred-myrrh). Those events are paired
     with the localized 422s by page and session, not by payload — the repeat
     count is what says the control never retired. cart_add_unavailable exists
     for precisely this news.

     Diacritics are folded before the test so "vyprodan"/"vyprodano" land on one
     stem. The cap branches still run FIRST, on state rather than on this string,
     so a quantity cap is no more likely to be read as a sellout on PL than it
     already is on EN. */
  function isSoldOutMessage(message) {
    var text = String(message || '');
    try { text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (e) { /* older engine: test the raw string */ }
    return /sold\s*out|wyprzedan|vyprodan|ausverkauft|uitverkocht/i.test(text);
  }

  /* A 422 the drawer has ALREADY resolved into a stock verdict: 'cap' (every
     remaining unit is in the cart) or 'sold_out'. Nothing broke — the rendered
     page was simply older than the stock, and the drawer has an honest line to
     say about it. Anything else stays null and keeps the ordinary failure
     ending, including a 422 we cannot explain.

     This exists so the two endings can be told apart BEFORE anything is
     reported. reportCartError feeds ss_cart_error, and the shopper who taps a
     dead button taps it again: PostHog, week to 2026-08-03 on the Inner Sanctum
     PDP, 4 ss_cart_error and 8 ss_add_to_cart_failed carrying "The product '…'
     is already sold out." An inventory answer is not a fault to triage, and
     burying it in the same counters hid the transport failures that are.

     The verdict is derived from exactly what the failure branch below already
     used to pick its message, so the copy a customer reads and the event that
     leaves the page can never disagree.

     healAvailability is what the live product re-check said about THIS variant
     (from _availabilityCache, which freshProductState fills from /products/x.js)
     — undefined when no check completed. It has to be evidence, not the mere
     fact that a check ran: runAdd's heal runs on EVERY 422 carrying a
     handle, which on a PDP is every 422 there is. Keying the verdict off "a
     check happened" would rename an unpublished variant, a rejected line
     property, a selling-plan mismatch or a cart-level rule 'sold_out' — and
     since the verdict now gates reporting and retires the buy control, that
     would hide a real fault behind a dead button. Unexplained stays unexplained. */
  function addInventoryVerdict(err, healChecked, variantId, healAvailability, cartForHold) {
    if (!isUnprocessableAdd(err)) return null;
    const vid = Number(variantId);
    /* Holdings default to this tab's view. A caller about to make a
       SESSION-persistent retirement (the rail) passes a freshly fetched cart
       instead, because a stale tab can read held=false while another tab of
       the same shopper holds every remaining unit — the one state where
       Shopify's sellout sentence means a cap, not a sellout. */
    const holdCart = cartForHold || state.cart;
    /* The live product still sells the TAPPED variant → the 422 was a cap.
       Read from the re-check's answer about THAT variant, not from
       healChecked.vid: vid is the product's FIRST AVAILABLE variant, which is
       the tapped one only on a single-variant product. The sized cards that
       fill the collection grid are all multi-variant (the 25/50/100 g and
       30/60/90 g resins, the attar's bottle+sample, Jade's bottle counts), so a
       cap on any size but the product's first available variant fell past this
       test and was read as a sellout by the line below — Shopify
       answers a cap in the same words as a sellout ("The product '…' is already
       sold out."). That mislabel is not cosmetic: it tells a shopper who holds
       the last unit that the size is gone, marks the variant dead in
       _availabilityCache, and hands browse.js a sold_out verdict it writes into
       sessionStorage, retiring a size the shop still sells for the rest of the
       visit.

       Two things have to be true before an availability flag may speak, and
       neither of them is "a check ran":

       1. THIS response listed the variant. _availabilityCache outlives any one
          fetch, so a variant deleted or unpublished mid-session keeps the `true`
          an earlier fetch wrote and would read as a cap forever after.
       2. The shopper is actually HOLDING it. The sentence a cap prints is "all
          available stock is already in your cart", which is simply false of a
          variant that is not in the cart. Availability alone would rename every
          other 422 a live variant can draw — a rejected line property, a
          selling-plan mismatch, a cart-level rule — into a stock answer,
          silencing the report and retiring the buy control over a fault nobody
          would ever get to see. The optimistic row this very tap drew is not
          stock the shopper holds, so previews are excluded, the same rule
          runAdd's heal keeps.

       Both are read from state, not from the response body, so they hold on
       every storefront regardless of the language Shopify answers in. That
       matters more now than it did: isSoldOutMessage below reads the localized
       body too, so the cap evidence has to be the thing that decides first. */
    const answered = !!(healChecked && healChecked.ids && healChecked.ids.indexOf(vid) >= 0);
    const held = !!(holdCart && holdCart.items.some(function(i) {
      return Number(i.variant_id) === vid && !i.__preview && i.quantity > 0;
    }));
    if (answered && healAvailability === true && held) return 'cap';
    /* Shopify's own words — stock for certain, but WHICH stock answer only when
       the shopper is not already holding the variant. Holding it means the one
       sentence covers both readings: every remaining unit is theirs (a cap), or
       the line is truly gone. The limb above settles that with a re-check; when
       no re-check answered for this variant — a 422 that carried no handle, so
       runAdd's heal never ran — nothing settles it, and guessing 'sold_out'
       is the expensive guess: it retires the control and hands browse.js a
       verdict it writes into sessionStorage, killing a size the shop may still
       sell for the rest of the visit. Unexplained stays unexplained. (The PDP
       and rail paths of the 2026-09-19 finding are unaffected: every 422 there
       carries a handle, so the re-check always answers.) */
    if (isSoldOutMessage(err.message) && !(held && !answered)) return 'sold_out';
    /* or a completed re-check that came back saying this variant cannot sell */
    if (healChecked !== undefined && healAvailability === false) return 'sold_out';
    return null;
  }

  function cloneCart(cart) {
    return cart ? JSON.parse(JSON.stringify(cart)) : null;
  }

  function stableProperties(properties) {
    const entries = Object.entries(properties || {}).sort(function(a, b) {
      return a[0].localeCompare(b[0]);
    });
    return JSON.stringify(entries);
  }

  function sellingPlanId(item) {
    if (!item) return null;
    if (item.selling_plan != null && item.selling_plan !== '') return String(item.selling_plan);
    const allocation = item.selling_plan_allocation;
    const plan = allocation && allocation.selling_plan;
    return plan && plan.id != null ? String(plan.id) : null;
  }


  /* Order-independent fingerprint of a line's properties and selling plan —
     used to re-identify a line when Shopify rewrites its key. variant_id alone
     is NOT unique: two lines of one variant can differ by properties, or by a
     subscription (one-time vs selling plan) — Shopify keeps them separate, and
     so must every write that re-resolves a line. */
  function linePropsFingerprint(item) {
    var props = (item && item.properties) || {};
    var plan = sellingPlanId(item);
    return JSON.stringify([plan, Object.keys(props).sort().map(function(k) { return [k, props[k]]; })]);
  }

  /* A line's identity: variant, properties and selling plan (the fingerprint)
     and whether it is a gift by its own answer. Never the key — a key is one
     cart's name for a line, and Shopify renames lines when composition
     changes. Every write resolves its line by identity against the latest
     server cart at the head of the queue. */
  function lineIdentity(item) {
    return item ? { variant: Number(item.variant_id), gift: hasGiftProperty(item), props: linePropsFingerprint(item) } : null;
  }
  function identityKey(identity) {
    return identity ? identity.variant + '|' + (identity.gift ? 'g' : 'p') + '|' + identity.props : '';
  }
  function lineHasIdentity(item, identity) {
    return !!(item && identity) && identityKey(lineIdentity(item)) === identityKey(identity);
  }
  function findLine(cart, identity) {
    if (!cart || !cart.items || !identity) return null;
    return cart.items.find(function(i) { return lineHasIdentity(i, identity); }) || null;
  }

  function hasGiftProperty(item) {
    if (isSubscriptionReward(item)) return false;
    if (item.properties && item.properties._gift === 'true') return true;
    /* fallback: detect free gift-variant lines if Shopify dropped the private
       property. Paid purchases of the same product must stay normal cart lines. */
    if (Number(item.final_line_price || 0) !== 0) return false;
    var vid = Number(item.variant_id);
    return !!(CFG.giftVariants && CFG.giftVariants.some(function(gv) { return gv != null && Number(gv) === vid; }))
      || isRetiredGiftVariant(vid);
  }

  /* A £0 gift variant the drawer no longer OFFERS but must still RECOGNISE (tier 2 paused:
     CFG.giftRetiredVariants, written by snippets/cart-drawer.liquid). Recognised, a stray line
     of it is a gift the basket no longer earns, so the planner retires it instead of adding a
     second gift beside it that the checkout Function would refuse. */
  function isRetiredGiftVariant(variantId) {
    var vid = Number(variantId);
    return !!(CFG.giftRetiredVariants && CFG.giftRetiredVariants.some(function(gv) { return gv != null && Number(gv) === vid; }));
  }

  /* True when a variant id is one of the configured £0 free-gift variants
     (e.g. hojari-blend "Gift 30g", quick-lighting-charcoal "Gift 1 Roll"). Used to
     refuse any CUSTOMER add of a gift variant: the gift is added only by the reward
     mechanism (addGiftToCart), which never routes through addAndOpen. */
  function isGiftVariantId(variantId) {
    if (SUBSCRIPTION_REWARDS.variants.indexOf(String(variantId)) >= 0) return true;
    var vid = Number(variantId);
    return !!(CFG.giftVariants && CFG.giftVariants.some(function(gv) { return gv != null && Number(gv) === vid; }))
      || isRetiredGiftVariant(vid);
  }

  /* Drawn and counted as a gift by the line's OWN identity. It used to be
     `key === state.giftCartKey`, one tracked key, so the second line of a
     tier-2 "both" reward was drawn as a paid row — a stepper, Remove and
     "0 zł" — on every full rebuild (cart-optimistic-proof tier2_rows,
     2026-09-26), and a re-keyed gift read as no gift at all. */
  function isGiftItem(item) {
    return !!item && hasGiftProperty(item);
  }

  /* A cart's counts and money, from its lines. A folded line (in its undo
     window) has left as far as the customer can see: its money and count go
     at the fold and come back with Undo. A cart-level percentage discount is
     recomputed from the lines (discountAmount); any other kind keeps
     Shopify's own amount. */
  function recalcCartTotals(cart) {
    let count = 0;
    let lines = 0;
    cart.items.forEach(function(item) {
      if (item.__folded) return;
      count += Number(item.quantity || 0);
      lines += Number(item.final_line_price != null ? item.final_line_price : (item.line_price || 0));
    });
    cart.item_count = count;
    cart.items_subtotal_price = lines;
    let off = 0;
    (cart.cart_level_discount_applications || []).forEach(function(app) {
      const amount = discountAmount(app, cart);
      app.total_allocated_amount = amount;
      off += amount;
    });
    cart.total_discount = off + cart.items.reduce(function(sum, item) {
      return sum + (item.__folded ? 0 : Math.max(0, Number(item.original_line_price || 0) - Number(item.final_line_price || 0)));
    }, 0);
    cart.total_price = Math.max(0, lines - off);
    return cart;
  }

  /* SACRA10 and every percentage order code: Shopify's allocation, measured
     against real carts (see discountRuleHolds). A code whose shape we have
     not verified keeps the amount Shopify last gave. */
  function discountAmount(app, cart) {
    const pct = app && app.value_type === 'percentage' ? Number(app.value) : NaN;
    if (!isFinite(pct) || !(pct > 0)) return Number(app && app.total_allocated_amount) || 0;
    let base = 0;
    cart.items.forEach(function(item) {
      if (item.__folded || hasGiftProperty(item)) return;
      base += Number(item.final_line_price || 0);
    });
    return Math.round(base * pct / 100);
  }

  function visibleItemCount(cart) {
    return Math.max(Number(cart && cart.item_count || 0), 0);
  }

  function regularTotalCents(cart) {
    let total = Number(cart && cart.total_price ? cart.total_price : 0);
    (cart && cart.items || []).forEach(function(item) {
      if (!item.__folded && isGiftItem(item)) total -= Number(item.final_line_price || 0);
    });
    return Math.max(total, 0);
  }

  function formatThresholdAmount(amount) {
    /* Same rounding (ceil) and symbol position as the Liquid server render in
       snippets/cart-drawer.liquid — the two must never disagree on first paint. */
    const rounded = Math.ceil(Math.max(Number(amount) || 0, 0));
    return shapeMoney(rounded * 100, CFG.currency, CFG.locale, CFG.symbol);
  }

  /* ═══════════════════════════════════════
     TWO GIFT TIERS

     CFG.giftVariants carries FOUR slots. 0-2 are the tier-1 choices; slot 3 is
     the tier-2 gift. They live in one array on purpose: hasGiftProperty,
     isGiftVariantId and giftIsAvailable then recognise the tier-2 £0 line with
     no second code path, so a tier-2 gift can never be bought for £0 by a
     customer add or counted as a paid line in a threshold.

     CFG.thresholdGift2 is the only switch. Zero — which is what the Liquid emits
     while the tier-2 variant id is blank — and every function below stands down:
     no chip, no hint, no journey goal, no correction, nothing changes.
  ═══════════════════════════════════════ */
  const TIER1_SLOTS = 3;
  const GIFT2_INDEX = 3;

  function tier2Configured() {
    return Number(CFG.thresholdGift2) > 0
      && !!(CFG.giftVariants && CFG.giftVariants[GIFT2_INDEX] != null);
  }

  /* A PAID line of the tier-2 gift's own product changes the offer. Amber is in
     roughly one order in eight, and handing it free to someone already buying it
     does not win a sale, it refunds one. So the Amber choice is withheld and the
     pair of tier-1 gifts becomes the whole tier-2 reward. Paid means a line that
     is not a gift line: the £0 gift variant of the same product must not trip it. */
  function giftTier2AmberPaidInCart(cart) {
    const handle = CFG.gift2Handle;
    if (!handle || !cart || !cart.items) return false;
    return cart.items.some(function(item) {
      return String(item.handle || '') === String(handle)
        && !hasGiftProperty(item)
        && Number(item.final_line_price || 0) > 0;
    });
  }

  /* The tier-1 gift variants that can actually be given right now, in chip order. */
  function tier1GiftVids() {
    const out = [];
    for (var i = 0; i < TIER1_SLOTS; i++) {
      var gv = CFG.giftVariants ? CFG.giftVariants[i] : null;
      if (gv != null && giftIsAvailable(i)) out.push(Number(gv));
    }
    return out;
  }

  /* What the basket has earned, expressed as the gift lines that belong in it.

     `slots` is how many free lines are allowed; `vids` is which variants, when
     the choice is settled. Slots WITHOUT vids means "one gift line, variant not
     yet decided" — the state a page is in before any chip is chosen. That
     distinction is the whole reason a gift the customer picked in an earlier
     session is left alone instead of being removed and re-added on load.

     The tiers replace each other. Above tier 2 the answer is either the one
     tier-2 gift or the two tier-1 gifts — never all three. */
  function giftEntitlement(regularMajor, cart) {
    if (!(regularMajor >= Number(CFG.thresholdGift))) return { slots: 0, vids: [] };
    if (tier2Configured() && regularMajor >= Number(CFG.thresholdGift2)) {
      const wantsBoth = state.selectedGift2 === 'both' || giftTier2AmberPaidInCart(cart);
      if (!wantsBoth && giftIsAvailable(GIFT2_INDEX)) {
        return { slots: 1, vids: [Number(CFG.giftVariants[GIFT2_INDEX])] };
      }
      const pair = tier1GiftVids().slice(0, 2);
      /* Only a real pair is a second tier. With one tier-1 gift left in stock
         there is nothing extra to give, so fall through to the tier-1 answer. */
      if (pair.length > 1) return { slots: pair.length, vids: pair };
    }
    const chosen = (state.selectedGift >= 0 && state.selectedGift < TIER1_SLOTS && CFG.giftVariants)
      ? CFG.giftVariants[state.selectedGift] : null;
    return { slots: 1, vids: chosen != null ? [Number(chosen)] : [] };
  }

  /* Keep It Coming has a fixed kit, separate from spend rewards. The real
     Shopify allocation establishes eligibility; a private property never does.
     These one-time checkout lines MUST NOT carry selling_plan: Appstle already
     owns the kit on each renewal contract. Keep in sync with the Function. */
  const SUBSCRIPTION_REWARDS = {
    plans: ['693320253770', '693320286538', '693320319306'],
    products: ['amber-hojari', 'hojari-blend', 'sultans-jade-frankincense', 'sacred-myrrh', 'dragon-blood'],
    variants: ['61400658018634', '63382272704842']
  };

  /* The kit is always kept in stock (Sho, 2026-09-27), so a kit that did not
     land is a network blip, and the next tap adds it again. Say exactly that,
     instead of the general checkout error that left subscribers with no way
     forward. The Shopify gift Function still requires the kit, because
     express checkouts never pass through this drawer. */
  function kitNotReadyText() {
    return localeText(
      'Your subscription gifts are still being added. Tap again to continue.',
      'Jeszcze dodajemy prezenty do subskrypcji. Dotknij ponownie, aby przejść dalej.',
      'Ještě přidáváme dárky k předplatnému. Klepni znovu a pokračuj.',
      'Deine Abo-Geschenke werden noch hinzugefügt. Tippe erneut, um fortzufahren.',
      'Je abonnementscadeaus worden nog toegevoegd. Tik opnieuw om verder te gaan.');
  }

  /* a cart that may owe the Keep It Coming kit */
  function mayOweKit(cart) {
    return !!(cart && cart.items && cart.items.some(function(i) {
      return !hasGiftProperty(i) && SUBSCRIPTION_REWARDS.plans.indexOf(String(sellingPlanId(i))) >= 0;
    }));
  }

  function isSubscriptionReward(item) {
    return !!item && SUBSCRIPTION_REWARDS.variants.indexOf(String(item.variant_id || item.id)) >= 0;
  }

  function subscriptionGiftCorrection(cart) {
    const items = cart && cart.items || [];
    const eligible = items.some(function(item) {
      const allocation = item.selling_plan_allocation;
      return Number(item.final_line_price) > 0 && Number(item.quantity) > 0
        && SUBSCRIPTION_REWARDS.products.indexOf(item.handle) >= 0
        && allocation && allocation.selling_plan
        && SUBSCRIPTION_REWARDS.plans.indexOf(String(allocation.selling_plan.id)) >= 0;
    });
    const seen = new Set();
    const updates = {};
    items.filter(isSubscriptionReward).forEach(function(item) {
      const id = String(item.variant_id || item.id);
      // Never silently keep a charged gift or a gift made recurring by another
      // producer. Replace it with our exact one-time line after removing it.
      const keep = eligible && !seen.has(id) && !item.selling_plan_allocation
        && Number(item.final_line_price) === 0;
      const quantity = keep ? 1 : 0;
      if (keep) seen.add(id);
      if (Number(item.quantity) !== quantity) updates[item.key] = quantity;
    });
    const add = eligible ? SUBSCRIPTION_REWARDS.variants.filter(function(id) {
      return !seen.has(id);
    }).map(function(id) { return { id: Number(id), quantity: 1 }; }) : [];
    return Object.keys(updates).length || add.length
      ? { reason: 'subscription', updates: updates, add: add } : null;
  }




  /* Is any price-reducing discount currently applied? When not, an add's new
     subtotal is purely additive and already known, so it can be shown the
     instant the preview paints instead of a skeleton. This only reports
     CURRENTLY-applied discounts; a threshold-triggered automatic discount
     reports nothing until the add crosses it, which is safe because such a
     discount can only LOWER the total — the optimistic figure is an upper
     bound the authoritative fetch corrects downward, never an undercharge. */
  function hasActiveDiscount(cart) {
    if (!cart) return false;
    if (Number(cart.total_discount) > 0) return true;
    if ((cart.cart_level_discount_applications || []).length > 0) return true;
    if ((cart.discount_codes || []).some(function(d) { return d && d.code && d.applicable !== false; })) return true;
    return (cart.items || []).some(function(item) {
      return (item.line_level_discount_allocations || []).some(function(a) { return Number(a.amount) > 0; })
        || Number(item.total_discount) > 0
        || Number(item.original_line_price) > Number(item.final_line_price);
    });
  }

  function normalizeDiscountAllocations(item) {
    if (Array.isArray(item.line_level_discount_allocations)) return item.line_level_discount_allocations;
    if (!Array.isArray(item.discounts)) return [];
    return item.discounts.map(function(discount) {
      return {
        amount: Number(discount.amount || discount.total_allocated_amount || 0),
        /* type carries through where the legacy shape has it: the copy chip is
           gated on discount_code, and a missing type simply means no chip */
        discount_application: discount.discount_application || { title: discount.title || '', type: discount.type }
      };
    });
  }

  function normalizeCartItem(item) {
    if (!item) return null;
    const quantity = Number(item.quantity || 0);
    const price = Number(item.price != null ? item.price : (item.final_price != null ? item.final_price : 0));
    const finalLinePrice = Number(item.final_line_price != null ? item.final_line_price : (item.line_price != null ? item.line_price : price * quantity));
    const originalLinePrice = Number(item.original_line_price != null ? item.original_line_price : (item.line_price != null ? item.line_price : finalLinePrice));
    return Object.assign({}, item, {
      quantity: quantity,
      price: price,
      final_price: Number(item.final_price != null ? item.final_price : price),
      original_price: Number(item.original_price != null ? item.original_price : price),
      line_price: Number(item.line_price != null ? item.line_price : finalLinePrice),
      final_line_price: finalLinePrice,
      original_line_price: originalLinePrice,
      total_discount: Number(item.total_discount || 0),
      properties: item.properties && typeof item.properties === 'object' ? item.properties : {},
      line_level_discount_allocations: normalizeDiscountAllocations(item)
    });
  }

  function normalizeCart(cart) {
    if (!cart || typeof cart !== 'object') return null;
    const items = Array.isArray(cart.items) ? cart.items.map(normalizeCartItem).filter(Boolean) : [];
    return Object.assign({}, cart, {
      items: items,
      item_count: cart.item_count != null ? Number(cart.item_count) : items.reduce((sum, item) => sum + item.quantity, 0),
      total_price: cart.total_price != null ? Number(cart.total_price) : items.reduce((sum, item) => sum + Number(item.final_line_price || item.line_price || 0), 0),
      cart_level_discount_applications: Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []
    });
  }

  function emptyCart() {
    return normalizeCart({
      items: [],
      item_count: 0,
      total_price: 0,
      cart_level_discount_applications: []
    });
  }

  function getAddedItems(addData) {
    if (!addData) return [];
    if (Array.isArray(addData.items)) return addData.items.map(normalizeCartItem).filter(Boolean);
    const normalized = normalizeCartItem(addData);
    return normalized ? [normalized] : [];
  }


  function sellingPlanName(el, sellingPlan) {
    if (!sellingPlan) return '';
    try {
      const form = el && el.closest ? el.closest('form') : null;
      const selected = form && form.querySelector('[name="selling_plan"]');
      if (selected && selected.options && selected.selectedIndex >= 0) {
        const label = selected.options[selected.selectedIndex].textContent.trim();
        if (label) return label;
      }
      const r45 = document.querySelector('input[name="r45-selling-plan"][value="' + String(sellingPlan).replace(/[^0-9]/g, '') + '"]');
      if (r45) {
        const label = r45.getAttribute('data-plan-name');
        if (label) return label;
      }
      const scope = el && el.closest ? (el.closest('.shopify-section') || document) : document;
      const config = scope.querySelector && scope.querySelector('[data-product-hero-config]');
      if (config) {
        const parsed = JSON.parse(config.textContent);
        const plan = parsed.sellingPlans && parsed.sellingPlans[String(sellingPlan)];
        if (plan && plan.name) return plan.name;
      }
    } catch (e) {}
    return '';
  }

  /* The plan's interval as the r45 radio states it (data-plan-months, from
     snippets/selling-plan-months.liquid), so a preview line labels itself the
     same way in every language. 0 when unknown. */
  function sellingPlanMonths(sellingPlan) {
    if (!sellingPlan) return 0;
    try {
      const r45 = document.querySelector('input[name="r45-selling-plan"][value="' + String(sellingPlan).replace(/[^0-9]/g, '') + '"]');
      return r45 ? (Number(r45.getAttribute('data-plan-months')) || 0) : 0;
    } catch (e) { return 0; }
  }

  function buildPreviewItem(el, variantId, qty, properties, sellingPlan) {
    qty = qty || 1;
    /* read product data from Liquid-rendered data attributes (always correct) */
    var source = (el && el.dataset && el.dataset.productTitle) ? el : (el && el.closest ? el.closest('[data-product-title]') : null);
    var title = source ? source.dataset.productTitle : '';
    var image = source ? (source.dataset.productImage || '') : '';
    var unitPrice = source ? parseInt(source.dataset.productPrice || '0', 10) : 0;
    var url = source ? (source.dataset.productUrl || '') : '';
    var type = source ? (source.dataset.productType || '') : '';
    var variantTitle = source ? (source.dataset.variantTitle || null) : null;

    /* Fallback for custom PDPs (e.g. Jade Oil) whose add form lacks data-product-*:
       recover title/image/price from the page so the add is still INSTANT (optimistic),
       instead of waiting ~700ms for the network round-trip. */
    if (!title) {
      try {
        var _m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
        if (_m && _m.variants) {
          var _v = _m.variants.filter(function(x){ return Number(x.id) === Number(variantId); })[0];
          if (_v) {
            title = _v.name || '';
            if (_v.public_title && title.indexOf(' - ' + _v.public_title) > -1) { variantTitle = _v.public_title; title = title.replace(' - ' + _v.public_title, ''); }
            if (_v.price != null) unitPrice = _v.price;
          }
        }
      } catch (e) {}
      if (!title) { var _h1 = document.querySelector('h1'); title = _h1 ? _h1.textContent.trim() : ''; }
      if (!image) { var _og = document.querySelector('meta[property="og:image"]'); if (_og) image = _og.getAttribute('content') || ''; }
      if (!url) url = window.location.pathname;
    }

    if (!title) return null;

    var handleMatch = url && url.match(/\/products\/([^?#/]+)/);
    var planName = sellingPlanName(el, sellingPlan);
    return normalizeCartItem({
      id: Number(variantId),
      variant_id: Number(variantId),
      key: 'preview:' + (++_previewId),
      quantity: qty,
      properties: properties || {},
      product_title: title,
      variant_title: variantTitle,
      title: variantTitle ? title + ' - ' + variantTitle : title,
      handle: handleMatch ? handleMatch[1] : '',
      url: url || window.location.pathname,
      product_type: type,
      featured_image: image ? { url: image, alt: title } : null,
      image: image,
      price: unitPrice,
      final_price: unitPrice,
      original_price: unitPrice,
      line_price: unitPrice * qty,
      final_line_price: unitPrice * qty,
      original_line_price: unitPrice * qty,
      line_level_discount_allocations: [],
      selling_plan: sellingPlan || null,
      selling_plan_allocation: sellingPlan ? {
        selling_plan: { id: Number(sellingPlan) || sellingPlan, name: planName, months: sellingPlanMonths(sellingPlan) }
      } : null,
      __preview: true
    });
  }



  /* ═══════════════════════════════════════
     FETCH HELPERS
  ═══════════════════════════════════════ */
  /** Wrapper that tracks network health around every cart API call */
  async function trackedFetch(url, opts) {
    try {
      const res = await fetch(url, opts);
      trackNetworkHealth(true);
      return res;
    } catch (err) {
      trackNetworkHealth(false);
      throw err;
    }
  }

  /* One product request lane for catalog fallback, cart availability and live
     revalidation. at === 0 means the request is still in flight, so even a
     force-fresh 422 heal joins it instead of opening a duplicate connection.
     Settled product truth is reusable for one minute: long enough to cover an
     open/add/reopen burst, short enough for stock to be checked again while a
     shopper keeps the page open. */
  const PRODUCT_REQUEST_TTL_MS = 60000;
  const PRODUCT_READ_ABORT_MS = 4000;
  const _productRequestCache = new Map(); /* handle → { promise, at } */

  function fetchProductByHandle(handle, options) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return Promise.resolve(null);

    const cached = _productRequestCache.get(normalizedHandle);
    const forceFresh = !!(options && options.forceFresh);
    if (cached && (
      cached.at === 0 ||
      (!forceFresh && Date.now() - cached.at < PRODUCT_REQUEST_TTL_MS)
    )) {
      return cached.promise;
    }

    const entry = { promise: null, at: 0 };
    /* bounded, body included: a heal or a gift check waits on this inside
       the cart queue, and a stalled product read must not hold the queue */
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(function() { controller.abort(); }, PRODUCT_READ_ABORT_MS) : null;
    const request = trackedFetch(
      (CFG.productBase || '/products/') + encodeURIComponent(normalizedHandle) + '.js',
      controller ? { credentials: 'same-origin', signal: controller.signal } : { credentials: 'same-origin' }
    ).then(function(res) {
      if (!res.ok) {
        const error = new Error(String(res.status));
        error.status = res.status;
        throw error;
      }
      return res.json();
    });
    if (timer) request.then(function() { clearTimeout(timer); }, function() { clearTimeout(timer); });
    entry.promise = request;
    _productRequestCache.set(normalizedHandle, entry);

    request.then(function() {
      if (_productRequestCache.get(normalizedHandle) === entry) entry.at = Date.now();
    }, function(error) {
      if (_productRequestCache.get(normalizedHandle) !== entry) return;
      /* A 404 is stable product truth for the TTL. Network/server/parse
         failures are not truth and stay immediately retryable. */
      if (Number(error && error.status) === 404) entry.at = Date.now();
      else _productRequestCache.delete(normalizedHandle);
    });
    return request;
  }

  /* The budget for ONE read. Still four seconds -- the number does not move,
     and the 3.5s scenario in cart-abort-reason-proof.mjs is what holds it
     there. */
  const CART_READ_ABORT_MS = 4000;

  async function fetchCartAttempt() {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const startedAt = Date.now();
    let timedOut = false;
    let res;
    const timeout = controller ? setTimeout(function() {
      timedOut = true;
      controller.abort();
    }, CART_READ_ABORT_MS) : null;
    try {
      const opts = { credentials: 'same-origin' };
      if (controller) opts.signal = controller.signal;
      res = await readBody(await trackedFetch(CFG.cartUrl, opts));
    } catch (error) {
      throw stampCartRequestFailure(error, startedAt, timedOut);
    } finally {
      if (timeout) clearTimeout(timeout);
    }
    /* The budget covers headers AND body (a body that stalls is a spent
       budget, stamped above). The parse is outside the stamping catch: a
       refusal it raises -- Shopify's own 503, a truncated body -- reaches the
       caller carrying the server's status and no cartAbortReason, which is
       the shape fetchCart's transientReadStatus reads. */
    return parseJsonResponse(res);
  }

  /* A read the server REFUSED is the same question as a spent budget, and got
     the opposite answer. PostHog, PL homepage, 2035 sessions to 2026-09-15: a
     background refresh took HTTP 503 from /cart.js, the row was filed
     non-fatal and swallowed by design -- correct, nobody was waiting -- and
     then nothing re-read. The drawer kept painting the cart it last knew until
     some unrelated trigger happened to read again. Shopify answers 503 when it
     sheds load: nothing was read, nothing was written, and the next second is
     usually fine. That is a read worth asking twice, on exactly the terms the
     spent budget already gets.

     Which statuses: 502, 503 and 504 -- an edge or an overloaded upstream
     saying "not now". NOT 500: that is the application answering, and asking
     the same question again repeats it. NOT any 4xx: a refusal about the
     request itself -- a 422 sellout, a 404 -- which a second GET cannot
     change. Both stay a single failure, as today.

     The pause before the second ask is what a 5xx needs and a spent budget
     does not: a refused read comes back instantly, so an immediate re-ask
     lands inside the same shed window. Small, and jittered for the reason
     rateLimitDelayMs jitters -- two tabs, or a drawer and a PDP form, must not
     re-ask in lockstep into the window that just refused them. */
  const TRANSIENT_READ_STATUSES = [502, 503, 504];
  const TRANSIENT_READ_RETRY_MS = 250;
  const TRANSIENT_READ_JITTER_MS = 150;
  function transientReadStatus(error) {
    if (!error || typeof error !== 'object') return 0;
    const status = Number(error.status) || 0;
    return TRANSIENT_READ_STATUSES.indexOf(status) === -1 ? 0 : status;
  }

  /* A read the drawer's own timer ended, or one Shopify refused with 502, 503
     or 504, is asked once more: /cart.js is a read, a second one cannot
     double-write anything, and Shopify is occasionally slow, not reliably so
     (PL homepage, 2035 sessions to 2026-09-15: a background 503 was filed and
     then nothing re-read). Never on a document that is leaving. */
  async function fetchCart() {
    try {
      return await fetchCartAttempt();
    } catch (error) {
      const timedOut = !!(error && typeof error === 'object' && error.cartAbortReason === 'timeout');
      const refusedStatus = transientReadStatus(error);
      if ((!timedOut && !refusedStatus) || _pageLeaving) throw error;
      if (refusedStatus) {
        await sleep(TRANSIENT_READ_RETRY_MS + Math.floor(Math.random() * TRANSIENT_READ_JITTER_MS));
        if (_pageLeaving) throw error;
      }
      let recovered;
      try { recovered = await fetchCartAttempt(); }
      catch (retryError) {
        /* the shopper waited through both budgets: the row says so */
        if (timedOut && retryError && typeof retryError === 'object' && retryError.cartDurationMs != null && error.cartDurationMs != null) {
          retryError.cartDurationMs += error.cartDurationMs;
        }
        throw retryError;
      }
      reportCartError('cart_read', error, {
        abort_reason: timedOut ? 'timeout_recovered' : 'status_recovered',
        duration_ms: error.cartDurationMs,
        fatal: false
      });
      return recovered;
    }
  }

  function sleep(ms) { return new Promise(function(resolve) { setTimeout(resolve, ms); }); }

  /* ═══════════════════════════════════════
     THE SYNC CORE (2026-09-26 rebuild)

     Sho: "adding, removing, adding gift, all that stuff happens super fast and
     then finally gets validated at checkout."

     The drawer shows the customer's cart as the truth and syncs it behind
     them. Four pieces, each answering a recorded failure:

     1. ONE QUEUE for every cart request — add, change, update, a code, the
        attribution attributes, /localization and the reads — one on the wire
        at a time in this document, each write attempt under a cross-tab Web
        Lock. Two concurrent /cart/add.js POSTs lost a unit (observed live,
        2026-07-23); a slower /cart/change.js carrying a pre-remove snapshot
        put a removed line back and billed it (observed live, 2026-07-26). A
        FIFO queue makes both impossible by construction, and a read that
        joins it can never be answered from before a write it follows.
     2. ONE LIST OF INTENTS — a line's quantity (a removal is quantity 0; a
        fold is a removal held for its five-second undo), an add with its
        preview — applied to every server cart before it is painted. A settle
        can therefore never flick a number back, and every tap paints in its
        own frame.
     3. GIFTS ARE A FUNCTION OF THE CART ON SCREEN (both tiers, the
        subscription kit): decided at every tap, written when the queue has
        nothing else to send, validated before the wire.
     4. CHECKOUT IS THE ONLY WAITER: it drains the queue, reads the cart once,
        checks the gift rules and the kit on that truth, and leaves only with
        the cart the customer was looking at.

     No money mask on line prices, ever (0aecbea84 added one with no incident
     behind it; a third of the next 234 commits repaired what it broke). Under
     a live discount only the TOTAL may be masked, and only while the queue
     still owes Shopify a change and the discount's own arithmetic has not
     reproduced Shopify's last answer.
  ═══════════════════════════════════════ */
  /* The read budget above; the write budget below. Exposed on
     window.SacrasoulCart so the proofs read them instead of the source. */
  const CART_MUTATION_ABORT_MS = 8000;
  const RATE_LIMIT_RETRY_WINDOW_MS = 2500;
  const RATE_LIMIT_MAX_RETRIES = 2;
  const RATE_LIMIT_BASE_DELAY_MS = 400;
  /* quiet background retries of a write that did not take: same backoff the
     old recovery ladder used, reset by success, by coming back online, by the
     tab becoming visible and by the customer's next tap */
  const RETRY_LADDER_MS = [900, 2000, 4500, 8000];
  const QTY_BATCH_MS = 350;
  const SOFT_REMOVE_MS = 5000;
  const CHECKOUT_BOUND_MS = 9500;
  const RETRY = { retry: true };

  let _server = null;       /* the last cart Shopify answered with */
  let _seq = 0;             /* completed writes that may have changed the cart */
  let _intents = [];
  let _intentId = 0;
  let _fold = null;         /* the one row in its undo window */
  let _qtyTimer = null;
  const _jobs = [];
  let _running = null;
  let _pauseTimer = null;
  let _parked = false;
  let _ladderStep = 0;
  let _needsRead = false;
  /* This tab decides the gift. A mirror read (another tab's write, a tab
     return, a bfcache restore) hands the decision to whoever acted: the two
     tabs of one shopper handed the free line back and forth eight times in
     forty seconds when both decided (PostHog, 2026-09-11). */
  let _giftsActing = true;
  let _giftTried = '';
  let _giftSyncWaiters = [];
  let _settleWaiters = [];
  let _settled = true;
  let _lastReadAt = 0;
  let _cartEdits = 0;
  let _paintSeq = 0;
  let _checkoutMode = false;
  let _checkoutFailed = false;
  let _checkoutPaidFailed = false;  /* a paid quantity/removal write failed during this checkout */
  let _currencyHealTried = false;
  let _lastSavedCache = '';
  /* does the discount arithmetic reproduce Shopify's last answer? */
  let _discountRuleHolds = true;

  function noteCustomerEdit() {
    _cartEdits += 1;
    _giftsActing = true;
    _giftTried = '';
  }

  function cartEndpointName(url) {
    const path = String(url || '').split('?')[0];
    const last = path.substring(path.lastIndexOf('/') + 1);
    return last.replace(/\.js$/, '') || 'cart';
  }


  /* Retry-After is authoritative when Shopify sends it (cart answers in whole
     seconds; the HTTP-date form is not honoured and falls through to backoff).
     Jitter keeps two tabs, or a drawer and a PDP form, from re-sending in
     lockstep into the same window that just refused them. */
  /* The server's own declared wait, in ms, or NaN when it sent none worth
     honouring. The HTTP-date form is just as authoritative as seconds:
     proxies rewrite the seconds Shopify sends into absolute dates, and a
     date is usable exactly as its distance from now. A past, zero or
     unparseable value is NaN — refused as unusable, same as no header. */
  function declaredRetryAfterMs(res) {
    let after = NaN;
    try {
      const header = res && res.headers && res.headers.get ? res.headers.get('Retry-After') : null;
      if (header != null && String(header).trim() !== '') {
        const declared = String(header).trim();
        after = Number(declared) * 1000;
        if (!isFinite(after)) {
          const at = Date.parse(declared);
          if (isFinite(at)) after = at - Date.now();
        }
      }
    } catch (e) { after = NaN; }
    return (isFinite(after) && after > 0) ? after : NaN;
  }

  function rateLimitDelayMs(res, retryIndex) {
    /* declaredRetryAfterMs is the floor's ONE owner: it answers NaN for
       zero, past and unparseable values, so whatever it declares finite is
       honoured verbatim here — a far wait then refuses at the window gate
       below instead of a blind 400ms re-poke INTO a window the server just
       declared. Splitting the floor across both functions would leave each
       half looking optional. */
    const after = declaredRetryAfterMs(res);
    if (isFinite(after)) return after;
    return RATE_LIMIT_BASE_DELAY_MS * Math.pow(2, retryIndex) + Math.floor(Math.random() * 150);
  }

  let _rateLimitReported = false;
  function noteRateLimited(url) {
    if (_rateLimitReported) return;
    _rateLimitReported = true;
    /* an absorbed throttle is not a customer-facing failure: its own stage, non-fatal */
    reportCartError('cart_rate_limited', new Error('Cart request rate limited'), {
      substage: cartEndpointName(url), http_status: 429, fatal: false
    });
  }

  /* The wait for the lock is bounded by what is left of the write's budget
     (a tab that hangs holding it can never stall this one): a wait that
     runs out is "not sent", and the job is asked again later. */
  function crossTabCartLock(task, waitMs) {
    try {
      if (typeof navigator !== 'undefined' && navigator.locks &&
          typeof navigator.locks.request === 'function') {
        const notGranted = function() { return Object.assign(new Error('cart write lock not granted in time (not sent)'), { name: 'AbortError', ssNotSent: true, ssLockTimeout: true }); };
        if (!(waitMs > 0)) return Promise.reject(notGranted());
        const options = { mode: 'exclusive' };
        /* AbortSignal.timeout is missing where Web Locks already exist
           (iOS 15.4-15.x): a controller and a timer bound the same wait */
        let timer = null;
        if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') options.signal = AbortSignal.timeout(waitMs);
        else if (typeof AbortController !== 'undefined') {
          const controller = new AbortController();
          timer = setTimeout(function() { controller.abort(); }, waitMs);
          options.signal = controller.signal;
        } else {
          /* no way to bound the wait: skip the cross-tab lock, as a browser
             without Web Locks does; the in-document queue still orders writes */
          return task();
        }
        let entered = false;
        let pending;
        try {
          pending = navigator.locks.request('sacrasoul-cart-write', options, function(lock) { entered = true; if (timer) clearTimeout(timer); return task(lock); });
        } catch (syncError) {
          if (timer) clearTimeout(timer);
          throw syncError;
        }
        return pending.catch(function(error) {
          if (timer) clearTimeout(timer);
          if (entered) throw error;
          throw notGranted();
        });
      }
    } catch (e) { /* the in-document queue still applies */ }
    return task();
  }

  function jsonPost(body) {
    return { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
  }

  /* One write, one attempt at a time, under the cross-tab lock, with its own
     abort opened when it leaves (never while it waits its turn). A 429 on an
     ABSOLUTE write — change and update set a value, so a re-send lands the
     same state — is re-sent after the wait Shopify declared, twice at most,
     inside 2.5 s, with the lock released for the wait so a background tab's
     throttle never holds a foreground tab (79a5c8443). add.js increments and
     is never re-sent here; its caller reads the cart and lets the diff decide. */
  async function sendWrite(url, init, opts) {
    opts = opts || {};
    const first = Date.now();
    /* ONE abort budget for the whole write, re-sends included, opened when it
       first leaves: a throttled write can never hold the queue longer than a
       single write may (cart-rate-limit-proof E) */
    const deadlineAt = opts.deadlineAt || first + CART_MUTATION_ABORT_MS;
    let retries = 0;
    for (;;) {
      let startedAt = 0;
      let timedOut = false;
      let res;
      try {
        /* one absolute deadline: no budget left means not sent, before the lock */
        if (!(deadlineAt - Date.now() > 0)) throw Object.assign(new Error('cart write budget spent before send (not sent)'), { name: 'AbortError', ssNotSent: true });
        res = await crossTabCartLock(async function() {
          /* a producer's precondition (the attribution consent guard), asked
             at the wire boundary on every attempt */
          if (typeof opts.guard === 'function') {
            const allowed = opts.guard();
            if (allowed && typeof allowed.then === 'function') throw new Error('cart mutation guard must be synchronous');
            if (allowed !== true) throw new Error('cart mutation guard refused');
          }
          const request = Object.assign({}, init);
          /* A gift-line write names its line from a cart read HERE, inside the
             lock every tab of this engine writes under, on every attempt (a 429
             re-send included): nothing can re-key the line between that read
             and this send (gift_correction 400, sacrasoul.com/products/
             dragon-blood). A hook that throws sends nothing. */
          if (typeof opts.address === 'function') request.body = JSON.stringify(await opts.address());
          const budget = deadlineAt - Date.now();
          if (!(budget > 0)) throw Object.assign(new Error('cart write budget spent before send (not sent)'), { name: 'AbortError', ssNotSent: true });
          const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
          if (controller) request.signal = controller.signal;
          startedAt = Date.now();
          const timer = controller ? setTimeout(function() { timedOut = true; controller.abort(); }, budget) : null;
          /* headers and body, both inside the budget */
          try { return await readBody(await trackedFetch(url, request)); }
          finally { if (timer) clearTimeout(timer); }
        }, deadlineAt - Date.now());
      } catch (error) {
        throw stampCartRequestFailure(error, startedAt || Date.now(), timedOut);
      }
      if (!res || Number(res.status) !== 429) return res;
      const declared = declaredRetryAfterMs(res);
      try { res.__ssDeclaredThrottleMs = isFinite(declared) ? declared : 0; } catch (e) { /* frozen response */ }
      if (!opts.absolute || retries >= RATE_LIMIT_MAX_RETRIES) return res;
      const wait = rateLimitDelayMs(res, retries);
      if (Date.now() + wait - first > RATE_LIMIT_RETRY_WINDOW_MS) return res;
      /* an absolute value re-sent after a pause can overwrite a newer one
         another tab wrote during it; only a write addressed from a fresh read
         may be re-sent with a sibling tab alive */
      if (!opts.address && siblingMayWrite()) return res;
      noteRateLimited(url);
      await sleep(wait);
      if (!opts.address && siblingMayWrite()) return res;
      retries += 1;
    }
  }

  /* ── Sibling tabs ──
     A Shopify cart is shared by every tab of this browser. Tabs are
     detectable: say hello on a BroadcastChannel at load and answer every
     hello. Sticky for the session (a sibling that closed may have written
     before it went), fail closed for the first 250 ms (absence is not proven
     until a hello has had time to cross), and closed on pagehide — an open
     channel receiving the next page's hello evicted this document from
     Chromium's back/forward cache (0.88 s against 0.41 s, 0928178b9). Read by
     exactly two rules: an absolute write is re-sent after a 429 only with no
     sibling alive, and an add is never re-sent on a read a sibling could have
     produced. */
  const SIBLING_DISCOVERY_GRACE_MS = 250;
  let _siblingSeen = false;
  let _tabChannel = null;
  let _siblingProbeUntil = 0;
  function connectSiblingChannel() {
    if (_tabChannel || typeof BroadcastChannel === 'undefined') return;
    let channel;
    try { channel = new BroadcastChannel('sacrasoul-cart-siblings'); } catch (e) { return; }
    _tabChannel = channel;
    _siblingProbeUntil = Date.now() + SIBLING_DISCOVERY_GRACE_MS;
    channel.onmessage = function(ev) {
      if (_tabChannel !== channel) return;
      const kind = ev && ev.data && ev.data.type;
      if (kind === 'hello') {
        _siblingSeen = true;
        try { channel.postMessage({ type: 'here' }); } catch (e) { /* gone */ }
      } else if (kind === 'here') {
        _siblingSeen = true;
      }
    };
    try { channel.postMessage({ type: 'hello' }); } catch (e) { disconnectSiblingChannel(); }
  }
  function disconnectSiblingChannel() {
    const channel = _tabChannel;
    _tabChannel = null;
    if (!channel) return;
    try { channel.onmessage = null; } catch (e) {}
    try { channel.close(); } catch (e) {}
  }
  /* another tab's cart change seen here (its cache write reaches this tab) */
  let _siblingWrites = 0;
  function siblingMayWrite() {
    return _siblingSeen || !_tabChannel || Date.now() < _siblingProbeUntil;
  }
  connectSiblingChannel();
  listen(window, 'pagehide', disconnectSiblingChannel, true);
  listen(window, 'pageshow', function(ev) { if (ev && ev.persisted) connectSiblingChannel(); }, true);

  /* A failure worth another try later: the wire, a timeout, a throttle, the
     server shedding load. A refusal about the request itself is not. */
  function isTransientFailure(error) {
    if (!error) return false;
    const status = Number(error.status) || 0;
    return !status || status === 429 || status >= 500;
  }

  /* ── The queue ── */
  function enqueue(job) {
    if (_destroyed) return job;
    _jobs.push(job);
    /* the customer's own tap never waits out a background backoff */
    if (job.tap) wakeQueue();
    pump();
    return job;
  }

  function wakeQueue() {
    if (_pauseTimer) { clearTimeout(_pauseTimer); _pauseTimer = null; }
    _parked = false;
    _ladderStep = 0;
  }

  function pump() {
    if (_destroyed) return;
    if (_running || _pauseTimer || _parked) { syncSettled(); return; }
    const job = nextJob();
    if (!job) { syncSettled(); return; }
    _running = job;
    syncSettled();
    Promise.resolve().then(function() { return job.run(job); }).then(function(outcome) {
      _running = null;
      if (outcome === RETRY && !job.cancelled) {
        job.retry = true;
        _jobs.unshift(job);
        if (_ladderStep >= RETRY_LADDER_MS.length) {
          /* parked until the customer, the network or the tab comes back */
          _parked = true;
        } else {
          const delay = Math.max(RETRY_LADDER_MS[_ladderStep], Number(job.retryAfter) || 0);
          _ladderStep += 1;
          _pauseTimer = setTimeout(function() { _pauseTimer = null; pump(); }, delay);
        }
      } else if (outcome !== RETRY) {
        _ladderStep = 0;
      }
      pump();
    }, function(error) {
      /* a job never throws by design; if one does, the queue must not stall */
      _running = null;
      reportCartError('queue', error, { fatal: false });
      pump();
    });
  }

  function nextJob() {
    /* add.js answers with the added items only: the cart after an add is read
       before anything that addresses lines by key, and once at the end of a
       burst of adds */
    if (_needsRead && !(_jobs[0] && _jobs[0].kind === 'add')) {
      _needsRead = false;
      /* a read already queued (an idle check, an open) is that read: one
         /cart.js, not two back to back */
      if (_jobs[0] && _jobs[0].kind === 'read') {
        const queued = _jobs.shift();
        queued.mirror = false;
        queued.afterAdd = true;
        return queued;
      }
      return { kind: 'read', run: runAfterAddRead };
    }
    if (_jobs.length) return _jobs.shift();
    /* a quantity batch still in its debounce owns the next write */
    if (_qtyTimer) return null;
    const plan = giftPlan();
    if (plan) return { kind: 'gift', plan: plan, run: runGiftSync };
    return null;
  }

  /* gift work the queue still owes, including work a throttle's cooldown is
     holding back: a deferred gift is not a settled cart */
  function giftOwed() {
    return !!(giftPlan() || (Date.now() < _giftThrottledUntil && giftPlan(false, true)));
  }

  function isSettled() {
    return !_running && !_jobs.length && !_pauseTimer && !_parked && !_needsRead && !_qtyTimer
      && !_intents.length && !state.checkoutPending && !giftOwed();
  }

  function syncSettled() {
    const settled = isSettled();
    if (drawer && drawer.getAttribute('data-settled') !== String(settled)) drawer.setAttribute('data-settled', String(settled));
    if (settled !== _settled) {
      _settled = settled;
      updateSyncUi();
    }
    /* checkout waits for the queue to have nothing left to send; a fold is
       committed before it waits, so an undo window never holds it */
    if (!_running && !_jobs.length && !_pauseTimer && !_parked && !_needsRead && !_qtyTimer && !giftOwed()) {
      const waiters = _settleWaiters;
      _settleWaiters = [];
      waiters.forEach(function(resolve) { resolve(true); });
    }
    if (!_running && !giftOwed()) {
      const giftWaiters = _giftSyncWaiters;
      _giftSyncWaiters = [];
      giftWaiters.forEach(function(resolve) { resolve(state.cart); });
    }
  }

  function drained() {
    return new Promise(function(resolve) {
      _settleWaiters.push(resolve);
      syncSettled();
    });
  }

  /* ── The basket, once an add is true ──
     After a customer add (every surface lands in addLanded) the engine
     announces Shopify's own cart, once every add still in flight has been
     read back, as one provider-neutral DOM event. It names no vendor and does
     no work of its own; the listener (assets/ss-klaviyo.js, the abandoned-
     basket flow's Added to Cart) waits for an idle frame. A burst of taps
     is one announcement, carrying the cart they ended in. */
  const BASKET_EVENT = 'sacrasoul:basket-confirmed';
  let _basketOwed = false;
  function noteBasketAdd() {
    _basketOwed = true;
    announceBasket();
  }
  function announceBasket() {
    if (!_basketOwed || !_server) return;
    if (_intents.some(function(i) { return i.kind === 'add'; })) return;
    _basketOwed = false;
    try {
      document.dispatchEvent(new CustomEvent(BASKET_EVENT, { detail: { cart: _server } }));
    } catch (e) { /* a listener must never affect the cart */ }
  }

  /* ── Server carts ──
     Every cart Shopify answers with is applied through here: the whole cart,
     then every intent the server has not been told about yet on top of it.
     A read joins the same queue as the writes, so it can never be answered
     from before a write it follows; intents whose write has landed leave the
     list in the same step that paints the cart that includes them. */
  function applyServer(raw, seq, opts) {
    const cart = normalizeCart(raw);
    if (!cart || !Array.isArray(cart.items)) return false;
    _server = cart;
    _lastReadAt = Date.now();
    const landed = [];
    _intents = _intents.filter(function(it) {
      const done = it.doneSeq != null && it.doneSeq <= seq;
      if (done && it.awaitingRead) landed.push(it);
      return !done;
    });
    const mirror = !!(opts && opts.mirror);
    if (mirror && !_intents.length) _giftsActing = false;
    else if (!mirror && opts && opts.read) _giftsActing = true;
    syncGiftChoiceFromServer(cart);
    _discountRuleHolds = discountRuleHolds(cart);
    clearLandedUpsellError(cart);
    if (!mirror) {
      const snapshot = JSON.stringify(cart);
      if (snapshot !== _lastSavedCache) { _lastSavedCache = snapshot; saveCartCache(cart); }
    }
    maybeHealCurrency(cart);
    paint();
    landed.forEach(function(it) { it.resolve(state.cart); });
    announceBasket();
    return true;
  }

  /* Will our arithmetic give Shopify's answer at ANY quantity? Only when
     every percentage lands on a whole grosz per unit: then Shopify's rounding,
     per unit or per line, has nothing to round (SACRA10 on sacrasoul.pl, all
     whole-zloty prices, matched on every basket measured 2026-09-26). A €24.95
     line under 10% does not, and neither does a fixed-amount code: there the
     total alone waits for the queue. Line prices never wait. */
  function discountRuleHolds(cart) {
    if (!hasActiveDiscount(cart)) return true;
    const whole = function(unit, app) {
      if (!app || app.value_type !== 'percentage') return false;
      const pct = Number(app.value);
      return isFinite(pct) && (Number(unit) * pct) % 100 === 0;
    };
    const cartApps = cart.cart_level_discount_applications || [];
    const exact = cart.items.every(function(item) {
      if (hasGiftProperty(item)) return true;
      return (item.line_level_discount_allocations || []).every(function(alloc) {
        return !(Number(alloc && alloc.amount) > 0) || whole(item.original_price != null ? item.original_price : item.price, alloc.discount_application);
      }) && cartApps.every(function(app) { return whole(item.final_price != null ? item.final_price : item.price, app); });
    });
    if (!exact) return false;
    try {
      const probe = recalcCartTotals(cloneCart(cart));
      return Number(probe.total_price) === Number(cart.total_price);
    } catch (e) { return false; }
  }

  /* The gift chips follow the server's gift lines until the customer taps one
     (from then on the tap outranks a line still in the basket). */
  function syncGiftChoiceFromServer(cart) {
    let freeTier1 = 0;
    cart.items.forEach(function(item) {
      if (!hasGiftProperty(item) || !CFG.giftVariants) return;
      const idx = CFG.giftVariants.findIndex(function(gv) { return gv != null && Number(gv) === Number(item.variant_id); });
      if (idx === GIFT2_INDEX) {
        if (!state.gift2Explicit) state.selectedGift2 = 'amber';
      } else if (idx >= 0) {
        freeTier1 += 1;
        if (!state.giftExplicit) state.selectedGift = idx;
      }
    });
    if (tier2Configured()) {
      if (giftTier2AmberPaidInCart(cart)) state.selectedGift2 = 'both';
      else if (freeTier1 > 1 && !state.gift2Explicit) state.selectedGift2 = 'both';
    }
  }

  /* A cart priced in a different market than the page (observed live
     2026-07-26: a localization=GB page over a PLN cart) is re-localized once
     per session, through the queue, never during checkout. formatMoney already
     speaks the cart's own currency meanwhile. */
  function maybeHealCurrency(cart) {
    if (_currencyHealTried || state.checkoutPending || !cart.currency || !CFG.currency
        || cart.currency === CFG.currency || !CFG.country) return;
    _currencyHealTried = true;
    enqueue({ kind: 'localization', run: async function() {
      try {
        const body = new URLSearchParams();
        body.set('_method', 'put');
        body.set('country_code', CFG.country);
        body.set('return_to', window.location.pathname);
        await sendWrite('/localization', { method: 'POST', credentials: 'same-origin', body: body }, {});
        _seq += 1;
        applyServer(await fetchCart(), _seq, { read: true });
      } catch (e) { /* truthful formatting carries it */ }
    } });
  }

  /* ── The cart on screen ── */
  function buildView() {
    const cart = _server ? cloneCart(_server) : emptyCart();
    let touched = false;
    let unknown = 0;
    let guessed = false;
    _intents.forEach(function(it) {
      if (it.kind === 'add') {
        if (!it.preview) { unknown += it.qty; return; }
        if (mergePreview(cart, it)) guessed = true;
        touched = true;
        return;
      }
      const line = findLine(cart, it.identity);
      if (!line) return;
      touched = true;
      if (it.fold) { line.__folded = true; return; }
      if (it.qty <= 0) { cart.items.splice(cart.items.indexOf(line), 1); return; }
      applyLineQuantity(line, it.qty, Number(line.quantity) || 1);
    });
    if (overlayGifts(cart)) touched = true;
    cart.items.forEach(function(item) {
      item.__unavailable = !hasGiftProperty(item) && _availabilityCache.get(Number(item.variant_id)) === false;
    });
    if (touched) recalcCartTotals(cart);
    cart.__pendingUnknown = unknown;
    /* the one figure that may wait: see discountRuleHolds */
    cart.__approx = !!(touched && (guessed || (hasActiveDiscount(cart) && !_discountRuleHolds)));
    return cart;
  }

  /* An add's preview joins the line it will join on the server (same
     variant, properties and plan), or stands as its own row. Answers true
     when the row's money is a guess: a new line under a live code, whose
     entitlement only Shopify knows. */
  function mergePreview(cart, it) {
    const existing = findLine(cart, lineIdentity(it.preview));
    if (existing && !existing.__folded) {
      const before = Number(existing.quantity) || 0;
      applyLineQuantity(existing, before + it.qty, before || 1);
      return false;
    }
    const line = cloneCart(it.preview);
    line.quantity = it.qty;
    const original = Number(line.original_price) > 0 ? Number(line.original_price) : Number(line.price || 0);
    let unit = Number(line.final_price != null ? line.final_price : line.price || 0);
    /* SACRA10 is a per-line percentage (live, 2026-09-26: allocation "each",
       every paid line at the same value, the line's final price exactly its
       unit x quantity). A new line most likely takes it too: drawn at that
       price, while the total alone waits for Shopify's word. A set never
       does (SETS AND CODES), so it is drawn at its own price. */
    const code = unit > 0 && !isSetLine(line) ? lineCodePercent(cart) : null;
    if (code) {
      unit = Math.round(original * (100 - code.pct) / 100);
      line.final_price = unit;
      line.line_level_discount_allocations = [{ amount: (original - unit) * it.qty, discount_application: code.application }];
    }
    line.line_price = line.final_line_price = unit * it.qty;
    line.original_line_price = original * it.qty;
    line.__preview = true;
    cart.items.unshift(line);
    /* a new line under a live code: whether it is entitled is Shopify's to
       say, so the total waits for it (an unpriced preview shows no figure on
       its row and leaves the total as the priced lines make it) */
    return unit > 0 && hasActiveDiscount(cart);
  }

  /* The one percentage every paid line of the cart carries, when a code is
     applied that way; null otherwise. */
  function lineCodePercent(cart) {
    let found = null;
    const paid = (cart.items || []).filter(function(i) { return !hasGiftProperty(i) && !i.__folded && !i.__preview && Number(i.quantity) > 0; });
    if (!paid.length) return null;
    for (let n = 0; n < paid.length; n += 1) {
      const allocs = paid[n].line_level_discount_allocations || [];
      const app = allocs.length === 1 && allocs[0].discount_application;
      const pct = app && app.value_type === 'percentage' && app.allocation_method === 'each' ? Number(app.value) : NaN;
      if (!(pct > 0 && pct < 100)) return null;
      if (found && found.pct !== pct) return null;
      found = found || { pct: pct, application: app };
    }
    return found;
  }

  /* One line's quantity, and every price that follows from it. Split out of
     changeQty so the banked paint, the settle overlay and the ordinary tap
     all move a line the same way. previousQty is what the current discount
     allocations were computed for. */
  function applyLineQuantity(item, newQty, previousQty) {
    item.quantity = newQty;
    const discountedUnitPrice = Number(item.final_price != null ? item.final_price : item.price || 0);
    const originalUnitPrice = Number(item.original_price != null ? item.original_price : item.price || 0);
    item.final_line_price = discountedUnitPrice * newQty;
    item.line_price = item.final_line_price;
    item.original_line_price = originalUnitPrice * newQty;
    if (Array.isArray(item.line_level_discount_allocations) && item.line_level_discount_allocations.length) {
      const perUnitDiscounts = Array.isArray(item.__discountPerUnit)
        ? item.__discountPerUnit
        : item.line_level_discount_allocations.map(function(alloc) {
            return Number(alloc.amount || 0) / Math.max(previousQty, 1);
          });
      item.__discountPerUnit = perUnitDiscounts;
      item.line_level_discount_allocations = item.line_level_discount_allocations.map(function(alloc, index) {
        return Object.assign({}, alloc, {
          amount: Math.round((perUnitDiscounts[index] || 0) * newQty)
        });
      });
    }
  }

  function giftProgramOn() {
    return !!(giftPick && CFG.giftVariants && CFG.giftVariants.some(function(gv) { return gv != null; }));
  }

  function giftIndexOf(vid) {
    return CFG.giftVariants ? CFG.giftVariants.findIndex(function(gv) { return gv != null && Number(gv) === Number(vid); }) : -1;
  }

  /* What the lines the customer is paying for have earned, as gift variants. */
  function paidLineCents(cart) {
    return (cart.items || []).reduce(function(sum, item) {
      return item.__folded || hasGiftProperty(item) ? sum : sum + Number(item.final_line_price || 0);
    }, 0);
  }
  /* A gift the customer already holds stays theirs even when new stock of it
     ran out: they own it, and it anchors the chooser. */
  function giftOwned(vid) {
    return !!(_server && _server.items.some(function(i) { return hasGiftProperty(i) && Number(i.variant_id) === Number(vid); }));
  }
  function giftGivable(vid) {
    return giftOwned(vid) || giftIsAvailable(giftIndexOf(vid));
  }
  function desiredGiftVids(cart) {
    if (!giftProgramOn()) return null;
    const entitlement = giftEntitlement(paidLineCents(cart) / 100, cart);
    if (!entitlement.slots) return [];
    let vids = entitlement.vids.map(Number);
    if (vids.length === 1 && giftIndexOf(vids[0]) < TIER1_SLOTS && !giftGivable(vids[0])) vids = [];
    if (!vids.length) {
      /* one gift, chip not chosen yet (or the chosen one gone): the first that
         can be given, which is also the chip the chooser opens on */
      const idx = firstAvailableTier1Gift();
      vids = idx >= 0 ? [Number(CFG.giftVariants[idx])] : [];
    }
    return vids.filter(giftGivable);
  }

  /* While this tab decides, the gift lines on screen are the ones the cart on
     screen has earned — one each, quantity one — whatever the server holds
     until the queue gets there. */
  function overlayGifts(cart) {
    if (!_giftsActing) return false;
    const desired = desiredGiftVids(cart);
    if (!desired) return false;
    let changed = false;
    const kept = [];
    cart.items.slice().forEach(function(item) {
      if (!hasGiftProperty(item) || isSubscriptionReward(item)) return;
      const vid = Number(item.variant_id);
      if (desired.indexOf(vid) >= 0 && kept.indexOf(vid) < 0) {
        kept.push(vid);
        if (Number(item.quantity) !== 1) { applyLineQuantity(item, 1, Number(item.quantity) || 1); changed = true; }
        return;
      }
      cart.items.splice(cart.items.indexOf(item), 1);
      changed = true;
    });
    desired.forEach(function(vid) {
      if (kept.indexOf(vid) >= 0) return;
      cart.items.push(giftPreviewLine(vid));
      changed = true;
    });
    return changed;
  }

  function giftPreviewLine(vid) {
    const idx = giftIndexOf(vid);
    const data = CFG.giftProducts ? CFG.giftProducts[idx] : null;
    const title = data ? data.title : '';
    return normalizeCartItem({
      id: vid, variant_id: vid,
      key: 'preview:gift:' + vid,
      quantity: 1,
      properties: { _gift: 'true' },
      product_title: title, variant_title: null, title: title,
      handle: data ? data.handle : '', url: data ? data.url : '', product_type: '',
      featured_image: data && data.img ? { url: data.img, alt: title } : null,
      image: data ? data.img : '',
      price: 0, final_price: 0, original_price: 0,
      line_price: 0, final_line_price: 0, original_line_price: 0,
      line_level_discount_allocations: [],
      __preview: true
    });
  }

  function paint() {
    const prev = state.cart;
    const view = buildView();
    state.cart = view;
    _paintSeq += 1;
    try { applyUIState(view, prev); }
    catch (paintError) { console.error('[SacrasoulCart] paint failed', paintError); reportCartError('paint', paintError, { fatal: false }); }
    syncSettled();
  }

  /* ── Reads ── */
  function readCart(opts) {
    const mirror = !!(opts && opts.mirror);
    /* a read that has not left yet answers this one too (a same-tick burst
       is one request); one already on the wire does not — it may have left
       before whatever this caller just changed */
    let job = _running && _running.kind === 'read' && _running.promise && !_running.departed ? _running
      : _jobs.find(function(j) { return j.kind === 'read' && j.promise; });
    if (job) {
      if (!mirror) job.mirror = false;
      /* someone is waiting now: the flight answers them, once, whatever it
         gets, and a read resting on the backoff goes now */
      if (opts && opts.visible) { job.visible = true; wakeQueue(); pump(); }
      return job.promise;
    }
    /* a read the shopper asked for never waits out a background backoff */
    job = { kind: 'read', mirror: mirror, visible: !!(opts && opts.visible), tap: !!(opts && opts.visible), run: runRead };
    job.promise = new Promise(function(resolve, reject) { job.resolve = resolve; job.reject = reject; });
    job.promise.catch(function() {});
    enqueue(job);
    return job.promise;
  }

  let _rereadOnShow = false;
  listen(window, 'pageshow', function(ev) {
    /* a restore finds the removal pagehide owed (theme.liquid also calls
       reconcileRestore; flushing twice queues nothing twice) */
    if (ev && ev.persisted && _intents.some(function(it) { return it.kind === 'qty' && !it.fold && !it.queued; })) { flushQty(); wakeQueue(); pump(); }
    if (!_rereadOnShow) return;
    _rereadOnShow = false;
    readCart({ mirror: true }).catch(function() {});
  });

  async function runRead(job) {
    job.departed = true;
    try {
      const cart = await fetchCart();
      applyServer(cart, _seq, { mirror: job.mirror, read: true });
      if (job.resolve) job.resolve(state.cart);
    } catch (error) {
      console.error('[SacrasoulCart] refresh failed', error); reportCartError('refresh', error, job.visible ? { substage: 'visible' } : { substage: 'background', fatal: false });
      /* a read that died with a leaving document is asked again if the
         document comes back (iOS pagehide on an app switch, a bfcache Back) */
      if (_pageLeaving) _rereadOnShow = true;
      if (job.visible || _checkoutMode) {
        if (job.visible && !isNavigationCancellation(error)) setStatus(CFG.strings.cartError, 'error', 'error');
        if (job.resolve) job.resolve(null);
        /* the read an add still owes is asked again on the ladder, as a read
           nobody waits on (its callers have had their answer) */
        return job.afterAdd && !_checkoutMode ? owedAgain(job) : null;
      }
      if (job.afterAdd) { if (job.resolve) job.resolve(null); return owedAgain(job); }
      /* nobody is watching: whoever asked hears "no cart", the drawer keeps
         the cart it has, and the next open, tab return or write reads again
         (a failed background read is the 503 and its one retry, never a
         ladder of its own: cart-abort-reason-proof Y/Z) */
      if (job.resolve) job.resolve(null);
      return null;
    }
  }

  function owedAgain(job) {
    job.promise = job.resolve = job.reject = null;
    job.visible = false;
    return RETRY;
  }

  async function runAfterAddRead() {
    try {
      applyServer(await fetchCart(), _seq, { read: true });
    } catch (error) {
      reportCartError('refresh', error, { substage: 'background', fatal: false });
      if (_checkoutMode) return null;
      /* re-run on the ladder like any write that did not take */
      return RETRY;
    }
  }

  /* Inside a running job the queue is already ours: read now, apply, return. */
  async function readNow() {
    const cart = await fetchCart();
    applyServer(cart, _seq, { read: true });
    return _server;
  }

  /* ── Adds ── */
  function addIntent(req) {
    /* a line folded in its undo window and added again: the removal goes
       first, so the add lands on an empty slot */
    if (_fold && req.preview && identityKey(_fold.identity) === identityKey(lineIdentity(req.preview))) commitFold();
    flushQty();
    const it = Object.assign({ kind: 'add', id: ++_intentId }, req);
    it.originalVid = it.vid;
    it.promise = new Promise(function(resolve, reject) { it.resolve = resolve; it.reject = reject; });
    it.promise.catch(function() {});
    noteCustomerEdit();
    _intents.push(it);
    it.job = enqueue({ kind: 'add', tap: true, intent: it, run: function() { return runAdd(it); } });
    return it;
  }

  function dropIntent(it) {
    const at = _intents.indexOf(it);
    if (at >= 0) _intents.splice(at, 1);
  }

  function lineQty(cart, identity) {
    const line = findLine(cart, identity);
    return line ? Number(line.quantity) || 0 : 0;
  }

  function addIdentity(it) {
    return it.preview ? lineIdentity(it.preview) : {
      variant: Number(it.vid), gift: false,
      props: linePropsFingerprint({ properties: it.props || {}, selling_plan: it.plan || null })
    };
  }

  function isUncertainAddFailure(error) {
    if (!error) return false;
    if (Number(error.status) > 0) return false;
    const name = String(error.name || '');
    if (name === 'AbortError' || name === 'TimeoutError') return true;
    if (name !== 'TypeError') return false;
    return /^(failed to fetch|load failed|network\s?error|network request failed|the network connection was lost|cancelled|the request timed out|the internet connection appears to be offline)/i
      .test(String(error.message || '').trim());
  }

  async function runAdd(it) {
    if (it.cancelled) return null;
    /* what the server holds of this line before this add: its last answer,
       plus earlier adds of the same line that landed and have not been read
       back yet (a double tap) — so a refusal is never credited with a unit
       another add put there. Taken again after a heal re-points the add. */
    let identity, baseline;
    const measure = function() {
      identity = addIdentity(it);
      baseline = lineQty(_server, identity) + _intents.reduce(function(sum, other) {
        return sum + (other !== it && other.kind === 'add' && other.doneSeq != null
          && identityKey(addIdentity(other)) === identityKey(identity) ? Number(other.qty) || 0 : 0);
      }, 0);
      /* addRefused's own read asks the same question */
      it.landedIn = function(cart) { return !!(cart && Array.isArray(cart.items)) && lineQty(normalizeCart(cart), identity) >= baseline + it.qty; };
      /* growth proves THIS add only when no other tab can have added the same
         line since the baseline */
      const writesAt = _siblingWrites;
      it.growthIsOurs = function() { return !siblingMayWrite() && _siblingWrites === writesAt; };
    };
    measure();
    const first = Date.now();
    let throttled = 0;
    let healChecked;
    for (;;) {
      const item = { id: Number(it.vid), quantity: it.qty };
      if (it.props && Object.keys(it.props).length) item.properties = it.props;
      if (it.plan) item.selling_plan = parseInt(it.plan, 10) || it.plan;
      let res;
      it.sent = true;
      try {
        res = await sendWrite(CFG.addUrl, jsonPost({ items: [item] }), { deadlineAt: throttled ? first + CART_MUTATION_ABORT_MS : 0 });
      } catch (error) {
        if (error && error.ssLockTimeout) { it.sent = false; return RETRY; }
        if (error && error.ssNotSent) return addRefused(it, Object.assign(new Error('Too many requests'), { status: 429, ssNotSent: true }), healChecked);
        return addUncertain(it, error);
      }
      const status = Number(res.status);
      if (status === 429 || status >= 500) {
        /* Shopify said "not now" (a throttle, an edge shedding load). An add
           increments, so it is never blind-replayed: wait what Shopify asked,
           read the cart, and let the line itself say whether this add is
           still owed. Twice at most; then it is refused like any other. */
        if (status === 429) noteRateLimited(CFG.addUrl);
        let truth = null;
        const wait = rateLimitDelayMs(res, throttled);
        if (throttled < RATE_LIMIT_MAX_RETRIES && Date.now() + wait - first <= RATE_LIMIT_RETRY_WINDOW_MS) {
          await sleep(wait);
          try { truth = await readNow(); } catch (readError) { truth = null; }
          if (truth && lineQty(truth, identity) >= baseline + it.qty) {
            /* the line grew, but a sibling tab may have grown it: never
               replayed, never claimed — uncertain, and the read decides */
            if (!it.growthIsOurs()) return addUncertain(it, Object.assign(new Error('add refused; the line grew but another tab may have added it'), { status: 0, ssRefusedGrowth: status }));
            reportCartError(it.source === 'upsell' ? 'upsell_add' : 'add', new Error('add committed behind a refusal'), { substage: 'landed_despite_refusal', fatal: false, http_status: status });
            return addLanded(it, null);
          }
        }
        throttled += 1;
        /* the read spent real time: a re-send that no longer fits the window,
           or one a sibling tab's write could have made look absent, is not sent */
        if (throttled <= RATE_LIMIT_MAX_RETRIES && truth && Date.now() - first <= RATE_LIMIT_RETRY_WINDOW_MS && !siblingMayWrite()) continue;
        let refused = null;
        try { await parseJsonResponse(res); } catch (error) { refused = error; }
        return addRefused(it, refused || Object.assign(new Error('Cart request failed'), { status: status }), healChecked);
      }
      let data = null;
      let refusal = null;
      try { data = await parseJsonResponse(res); } catch (error) { refusal = error; }
      /* a 200 whose body could not be read still landed: the read decides */
      if (!refusal) return addLanded(it, data || { items: [] });
      if (isUncertainAddFailure(refusal)) return addUncertain(it, refusal);
      /* Stale page (f98883521: a paid visitor was told the Alchemist was sold
         out while /products/alchemist.js said available). Re-read the product
         once; when it is unambiguously the same single-variant offer under a
         new variant id, point the preview at it and ask once more. */
      /* No handle on the preview (a caller with no product on the page: the browse
         quiz on /collections/all, 3 Oct) -- but when the shopper already HOLDS this
         variant, the cart line carries its handle, and that is the one case the
         re-check must settle: their last unit (cap) or the line truly gone. */
      const heldLine = _server.items.filter(function(i) { return Number(i.variant_id) === Number(it.vid) && !i.__preview && i.quantity > 0; })[0];
      const handle = (it.preview && it.preview.handle) || (heldLine && heldLine.handle);
      if (isUnprocessableAdd(refusal) && handle && healChecked === undefined) {
        try { healChecked = await freshProductState(handle, { forceFresh: true }); } catch (e) { healChecked = null; }
        const healVid = healChecked && healChecked.vid;
        if (healVid && healChecked.sole && healVid !== Number(it.vid) && !_server.items.some(function(i) { return Number(i.variant_id) === healVid; })) {
          emitBehavior('cart_add_heal_attempted', {
            product_handle: handle, old_variant_id: Number(it.vid), new_variant_id: healVid,
            source: it.source === 'upsell' ? 'upsell' : (/\/products\//.test(window.location.pathname) ? 'product_atc' : 'quick_add')
          });
          repointAdd(it, healVid, handle);
          measure();
          continue;
        }
      }
      return addRefused(it, refusal, healChecked);
    }
  }

  /* The held preview and every row drawn from it move to the live variant
     and its live price, so the settle cannot draw a phantom old line beside
     the healed one (d7afedb62). */
  function repointAdd(it, vid, handle) {
    it.vid = vid;
    it.healed = true;
    if (it.preview) {
      it.preview.variant_id = vid;
      it.preview.id = vid;
      const fresh = _upsellProducts[handle];
      if (fresh && fresh.price !== it.preview.final_price) {
        it.preview.price = it.preview.final_price = it.preview.original_price = fresh.price;
      }
    }
    paint();
  }

  function addLanded(it, data) {
    if (data) {
      /* add.js answers with the added items only: the add's promise resolves
         with the cart once the read that follows it has landed */
      it.doneSeq = ++_seq;
      it.awaitingRead = true;
      _needsRead = true;
    } else {
      /* the read that proved it landed is already on screen */
      it.doneSeq = _seq;
      dropIntent(it);
      paint();
    }
    const added = data ? getAddedItems(data) : [];
    const name = added.length ? added[0].product_title : (it.preview ? it.preview.product_title : '');
    if (it.healed) {
      emitBehavior('cart_add_heal_succeeded', { product_handle: it.preview && it.preview.handle, old_variant_id: Number(it.originalVid), new_variant_id: Number(it.vid) });
    }
    /* one canonical success per customer add, on Shopify's confirmation */
    const lookup = { items: added.concat(state.cart ? state.cart.items : []) };
    if (it.source === 'undo') {
      emitBehavior('cart_item_restored', cartItemBehaviorProperties(lookup, it.vid, it.qty, 'undo', it.props, it.plan));
      announce(localeText('Item restored to cart', 'Produkt przywrócony', 'Produkt vrácen do košíku', 'Artikel wieder im Warenkorb', 'Artikel terug in je winkelwagen'));
    } else {
      emitBehavior('cart_item_added', cartItemBehaviorProperties(lookup, it.vid, it.qty, it.source, it.props, it.plan));
      if (it.nudge) emitBehavior('cart_nudge_added', nudgeBehaviorProperties(it.nudge));
      announce(addedToCartText(name || localeText('Item', 'Produkt', 'Položka', 'Artikel', 'Artikel')));
    }
    /* with add.js's answer the cart is not read yet: applyServer announces it */
    noteBasketAdd();
    if (!data) it.resolve(state.cart);
    return null;
  }

  /* An add whose POST left and never answered (an abort, iOS resuming a
     suspended page, a dropped wire). PostHog, 2026-07-28: a shopper whose item
     WAS in the cart read "something went wrong", tapped again, and bought two.
     So it is never re-sent: the caller hears "uncertain" at once, the cart is
     read once, and the drawer shows what Shopify holds, with a notice. */
  async function addUncertain(it, error) {
    it.doneSeq = ++_seq;
    /* A pairing row answers where it was tapped. The cart is read before
       anything is said: the line landed — it stays, confirmed; it did not —
       the rail offers it again with its own retry line (a re-tap is then
       safe: the read shows nothing to double), before the row leaves. */
    if (it.source === 'upsell') {
      let fresh = null;
      try { fresh = normalizeCart(await fetchCart()); } catch (readError) { _needsRead = true; }
      const landed = !!(fresh && fresh.items.some(function(i) { return Number(i.variant_id) === Number(it.vid); }));
      /* the row is filed after the read: an add whose answer our own timer
         cut off but which Shopify committed reached the shopper (PostHog, PL
         jade-oil, 2026-10-07: fatal at 8002 ms, cart_item_added 1.2 s later) */
      if (landed) reportCartError('upsell_add', error, { substage: 'landed_after_uncertain', fatal: false });
      else { console.error('[SacrasoulCart] upsell add failed', error); reportCartError('upsell_add', error); }
      if (!landed && it.pData && !isNavigationCancellation(error)) {
        const failedOrderItem = _upsellOrder.find(function(item) { return item.handle === it.pData.handle; });
        _upsellError = {
          vid: Number(it.vid), handle: it.pData.handle, product: it.pData,
          item: failedOrderItem || { handle: it.pData.handle, isSet: !!BUNDLE_META[it.pData.handle] },
          text: catalogText('upsellAddError', it.pData.title), soldOut: false
        };
        clearRailAck();
        renderUpsellPage();
      }
      if (fresh) applyServer(fresh, _seq, { read: true });
      else { dropIntent(it); paint(); }
      if (landed) { emitBehavior('cart_item_added', cartItemBehaviorProperties(state.cart, it.vid, 1, 'upsell')); noteBasketAdd(); it.resolve(state.cart); }
      else {
        if (state.cart) updateUpsellStates(state.cart, { force: true, immediate: true });
        it.reject(error);
      }
      return null;
    }
    console.error('[SacrasoulCart] addAndOpen uncertain', error); reportCartError('add-uncertain', error);
    const uncertain = new Error(String(error && error.message || 'Cart add outcome uncertain'));
    uncertain.name = String(error && error.name || 'Error');
    uncertain.ssCartUncertain = true;
    uncertain.cause = error;
    emitBehavior('cart_add_uncertain', cartItemBehaviorProperties(state.cart, it.vid, it.qty, it.source, it.props, it.plan));
    it.reject(uncertain);
    const message = CFG.strings.cartUncertain || CFG.strings.cartError;
    try {
      await readNow();
    } catch (readError) {
      reportCartError('uncertain_add_refresh', readError, { fatal: false });
      _needsRead = true;
    }
    if (!isNavigationCancellation(error)) {
      setStatus(message, 'notice', 'error');
      announce(message);
    }
    return null;
  }

  /* Shopify refused the add. The row the customer saw goes back, visibly, with
     the true sentence: the classifier decides between a stock cap, a sellout
     and an unexplained fault from state and one fresh read, never from the
     localized 422 text alone (e3471a987, 86d1cc171). */
  async function addRefused(it, error, healChecked) {
    let fresh = null;
    try { fresh = await fetchCart(); } catch (readError) { fresh = null; }
    /* a throttle or a shed request can hide a committed add: when this read
       shows the line grew by this add, it landed — no failure, no second unit */
    const status = Number(error && error.status) || 0;
    if (!(error && error.ssNotSent) && (status === 429 || status >= 500) && it.landedIn && it.landedIn(fresh)) {
      if (it.growthIsOurs && !it.growthIsOurs()) {
        if (fresh) applyServer(fresh, _seq, { read: true });
        return addUncertain(it, Object.assign(new Error('add refused; the line grew but another tab may have added it'), { status: 0, ssRefusedGrowth: status }));
      }
      reportCartError(it.source === 'upsell' ? 'upsell_add' : 'add', new Error('add committed behind a refusal'), { substage: 'landed_despite_refusal', fatal: false, http_status: status });
      applyServer(fresh, _seq, { read: true });
      return addLanded(it, null);
    }
    const vid = Number(it.vid);
    const healAvailability = healChecked === undefined ? undefined : _availabilityCache.get(vid);
    const rail = it.source === 'upsell';
    const verdict = rail
      ? railInventoryVerdict(error, healChecked, vid, healAvailability, fresh)
      : addInventoryVerdict(error, healChecked, vid, healAvailability, fresh && Array.isArray(fresh.items) ? normalizeCart(fresh) : _server);
    error.ssVerdictSettled = true;
    if (verdict) {
      error.ssInventory = verdict;
      if (verdict === 'sold_out') {
        error.ssSoldOut = true;
        _availabilityCache.set(vid, false);
        if (rail && it.pData) markUpsellUnavailable(it.pData.handle);
      }
    } else {
      console.error('[SacrasoulCart] ' + (rail ? 'upsell' : 'addAndOpen') + ' add failed', error); reportCartError(rail ? 'upsell_add' : 'add', error, { product_handle: (it.preview && it.preview.handle) || undefined });
    }
    const capMessage = localeText(
      'All available stock is already in your cart',
      'Cała dostępna ilość jest już w koszyku',
      'Veškeré dostupné kusy už máš v košíku',
      'Alle verfügbaren Stücke sind bereits in deinem Warenkorb',
      'Alle beschikbare voorraad zit al in je winkelwagen'
    );
    if (rail) {
      /* the rail says it first, then the row leaves: there is never a frame
         where the line simply disappears (d7afedb62) */
      const pData = it.pData;
      if (pData) {
        const failedOrderItem = _upsellOrder.find(function(item) { return item.handle === pData.handle; });
        _upsellError = {
          vid: vid,
          handle: pData.handle,
          product: pData,
          item: failedOrderItem || { handle: pData.handle, isSet: !!BUNDLE_META[pData.handle] },
          text: verdict === 'sold_out' ? CFG.strings.soldOut : (verdict === 'cap' ? capMessage : catalogText('upsellAddError', pData.title)),
          soldOut: verdict === 'sold_out'
        };
        clearRailAck();
        renderUpsellPage();
        if (verdict === 'sold_out') announce(CFG.strings.soldOut);
      } else if (!isNavigationCancellation(error)) {
        setStatus(CFG.strings.cartError, 'error', 'error');
      }
      if (verdict) {
        const props = cartItemBehaviorProperties(state.cart, vid, 1, 'upsell');
        props.reason = String(verdict);
        emitBehavior('cart_add_unavailable', props);
      }
    } else {
      let failMsg = CFG.strings.cartError;
      if (isUnprocessableAdd(error)) {
        if (verdict === 'cap') failMsg = capMessage;
        else if (verdict === 'sold_out') {
          failMsg = healChecked
            ? localeText('That option just sold out', 'Ta opcja właśnie się wyprzedała', 'Tato varianta se právě vyprodala', 'Diese Variante ist gerade ausverkauft', 'Deze variant is net uitverkocht')
            : localeText('Just sold out', 'Właśnie się wyprzedało', 'Právě vyprodáno', 'Gerade ausverkauft', 'Net uitverkocht');
        } else if (CFG.locale === 'en' && error.message && !isSoldOutMessage(error.message)) {
          /* Shopify's own words, verbatim — except the sellout sentence the
             classifier declined to believe */
          failMsg = error.message;
        }
      }
      if (!isNavigationCancellation(error)) {
        setStatus(failMsg, 'error', 'error');
        announce(failMsg);
      }
      const props = cartItemBehaviorProperties(state.cart, vid, it.qty, it.source, it.props, it.plan);
      if (verdict) props.reason = String(verdict);
      emitBehavior(verdict ? 'cart_add_unavailable' : 'cart_add_failed', props);
    }
    dropIntent(it);
    if (fresh && Array.isArray(fresh.items)) applyServer(fresh, _seq, { read: true });
    else paint();
    if (rail && state.cart) updateUpsellStates(state.cart, { force: true, immediate: true });
    it.reject(error);
    return null;
  }

  /* ── Quantities, removals and the fold ── */
  function setLineQty(line, qty, dir) {
    noteCustomerEdit();
    const identity = lineIdentity(line);
    let it = _intents.find(function(i) { return i.kind === 'qty' && !i.fold && !i.queued && identityKey(i.identity) === identityKey(identity); });
    if (it) it.qty = qty;
    else {
      it = { kind: 'qty', id: ++_intentId, identity: identity, qty: qty, name: line.product_title };
      _intents.push(it);
    }
    it.name = line.product_title;
    _rollHint = dir ? { ident: identityKey(identity), dir: dir } : null;
    paint();
    _rollHint = null;
    if (qty <= 0) flushQty();
    else {
      clearTimeout(_qtyTimer);
      _qtyTimer = setTimeout(flushQty, QTY_BATCH_MS);
      syncSettled();
    }
  }

  /* Taps on the stepper gather for 350 ms into one /cart/update.js with
     absolute quantities; a removal leaves at once on /cart/change.js. The
     jobs join the queue in the order the customer made them. */
  function flushQty() {
    clearTimeout(_qtyTimer);
    _qtyTimer = null;
    const batch = _intents.filter(function(i) { return i.kind === 'qty' && !i.fold && !i.queued; });
    if (!batch.length) { syncSettled(); return; }
    batch.forEach(function(i) { i.queued = true; });
    const removals = batch.filter(function(i) { return i.qty <= 0; });
    const updates = batch.filter(function(i) { return i.qty > 0; });
    removals.forEach(function(it) {
      enqueue({ kind: 'remove', tap: true, intents: [it], run: function() { return runLineWrite([it], true); } });
    });
    if (updates.length) enqueue({ kind: 'qty', tap: true, intents: updates, run: function() { return runLineWrite(updates, false); } });
  }

  /* One write for these intents, their lines resolved by identity against the
     latest server cart at the head of the queue — never a key banked at the
     tap, which Shopify may have renamed since (400 "brak prawidłowego id lub
     parametru linii", recorded). One read and one retry on a 400. */
  async function runLineWrite(list, single) {
    const live = list.filter(function(it) { return _intents.indexOf(it) >= 0; });
    let carried = [];
    /* the body, from a cart: every intent's line by identity; a line that left
       the server by another road has nothing to write, and says so */
    const build = function(cart) {
      const body = {};
      carried = [];
      live.forEach(function(it) {
        if (_intents.indexOf(it) < 0) return;
        const line = findLine(cart, it.identity);
        if (!line) {
          reportCartError('qty_batch', new Error('qty line absent from cart'), { substage: 'stale_line', fatal: false });
          dropIntent(it);
          return;
        }
        body[line.key] = it.qty;
        carried.push(it);
      });
      const keys = Object.keys(body);
      if (!keys.length) throw Object.assign(new Error('no line left to write (not sent)'), { ssSettled: true });
      return single ? { id: keys[0], quantity: body[keys[0]] } : { updates: body };
    };
    /* The write a checkout tap is waiting on decides what the shopper is billed
       (checkout_handoff 400, 2026-08-08), so it is built from a cart read
       inside the write lock: a key the server retired unseen never leaves.
       Every other write is built from the latest server answer, and a 400
       costs one read and one retry. */
    const addressed = _checkoutMode ? async function() {
      const truth = await fetchCart();
      applyServer(truth, _seq, { read: true });
      return build(_server);
    } : null;
    let retried = false;
    for (;;) {
      let cart;
      let firstKey = '';
      try {
        const payload = addressed ? {} : build(_server);
        firstKey = payload.id || Object.keys(payload.updates || {})[0] || '';
        const res = await sendWrite(single ? CFG.changeUrl : CFG.updateUrl, jsonPost(payload), { absolute: true, address: addressed });
        cart = await parseJsonResponse(res);
      } catch (error) {
        if (error && error.ssSettled) { paint(); return null; }
        if (!retried && Number(error.status) === 400) {
          retried = true;
          try { await readNow(); } catch (readError) { return lineWriteFailed(carried, error, single); }
          reportCartError('qty_batch', error, { substage: 'stale_batch_retried', fatal: false, line_key: firstKey });
          continue;
        }
        return lineWriteFailed(carried, error, single);
      }
      const seq = ++_seq;
      carried.forEach(function(it) { it.doneSeq = seq; });
      applyServer(cart, seq);
      if (!single) {
        announce(carried.map(function(it) {
          return String(CFG.strings.quantityAnnouncement || '{name}, quantity {quantity}')
            .replace('{name}', it.name || '').replace('{quantity}', it.qty);
        }).join('; '));
      }
      return null;
    }
  }

  /* A write that did not take. The wire, a timeout, a throttle: the change
     is still owed and still on screen, and it is tried again quietly.
     Shopify refusing it, or checkout waiting on it: it is dropped, the
     server's cart is shown, and — only when the customer is there to act on
     it — said. */
  async function lineWriteFailed(list, error, single) {
    console.error('[SacrasoulCart] ' + (single ? 'remove' : 'qty batch') + ' failed', error); reportCartError(single ? 'remove' : 'qty_batch', error, _checkoutMode ? undefined : { fatal: false });
    /* a throttled quantity with another tab alive is not re-asserted later
       either: that tab may have written a newer value meanwhile. The edit is
       handed back — the server's cart shown, and said. */
    const siblingRefusal = Number(error && error.status) === 429 && siblingMayWrite();
    if (isTransientFailure(error) && !_checkoutMode && !siblingRefusal) {
      list.forEach(function(it) { it.queued = true; });
      return RETRY;
    }
    list.forEach(dropIntent);
    if (_checkoutMode) { _checkoutFailed = true; _checkoutPaidFailed = true; }
    try { await readNow(); } catch (readError) { paint(); }
    if (!_checkoutMode && !isNavigationCancellation(error)) setStatus(CFG.strings.cartError, 'error', 'error');
    return null;
  }

  function findByKey(cart, key) {
    return cart && cart.items ? cart.items.find(function(i) { return i.key === key; }) || null : null;
  }

  function changeQty(key, delta) {
    const line = findByKey(state.cart, key);
    if (!line || line.__folded) return;
    const next = (Number(line.quantity) || 0) + delta;
    if (next < 1) { softRemoveItem(key); return; }
    /* cap at 99 — matches the type-to-edit input.max so +/− and typing agree */
    if (next > 99) { haptic(4); return; }
    const stockLimit = stockLimitFor(line);
    if (stockLimit != null && next > stockLimit) {
      haptic(4);
      announce(localeText('Only ' + stockLimit + ' left', 'Zostało tylko ' + stockLimit + ' szt.', 'Skladem jen ' + stockLimit + ' ks', 'Nur noch ' + stockLimit + ' da', 'Nog maar ' + stockLimit + ' over'));
      return;
    }
    if (delta < 0) noteUnitFreed(line);
    setLineQty(line, next, delta > 0 ? 1 : -1);
  }

  /* The collection grid remembers a cap; exactly one thing undoes it, a unit
     freed here. assets/browse.js listens and hands the card back. */
  function noteUnitFreed(line) {
    if (!line || !line.variant_id) return;
    emitBehavior('cart_unit_freed', { variant_id: Number(line.variant_id) });
  }

  /* ═══════════════════════════════════════
     SOFT REMOVE — the way back lives in the row

     A remove tap folds the row into "removed — undo" for five seconds. The
     money, the count and the gift leave at the fold (the customer sees what
     they are left with) and come back with Undo; the write waits for the
     window to close — the timer, checkout, a link out of the drawer, closing
     the drawer, the page going away, or another fold — so an undo costs
     nothing on the wire, and neither does the gift it would have taken.
  ═══════════════════════════════════════ */
  /* keyOrIdentity: a key read at the tap, or a line identity captured when a
     gesture or an edit began (Shopify may have renamed the line since) */
  function softRemoveItem(keyOrIdentity, options) {
    const line = keyOrIdentity && typeof keyOrIdentity === 'object'
      ? findLine(state.cart, keyOrIdentity) : findByKey(state.cart, keyOrIdentity);
    if (!line || line.__folded) return Promise.resolve(state.cart);
    const key = line.key;
    const row = itemsWrap && itemsWrap.querySelector('.item[data-key="' + cssKey(key) + '"]');
    const swipe = row && row.closest('.item-swipe');
    /* Instant, no undo, where undo would be a lie: a sold-out line (the drawer
       is asking them to remove it), a preview whose add has not landed, a gift. */
    const eligible = !!swipe && !line.__preview && !line.__unavailable && !isGiftItem(line);
    noteUnitFreed(line);
    if (!eligible) return removeNow(line);
    commitFold();
    flushQty();
    noteCustomerEdit();
    const title = line.product_title || localeText('Item', 'Produkt', 'Položka', 'Artikel', 'Artikel');
    const it = { kind: 'qty', id: ++_intentId, identity: lineIdentity(line), qty: 0, fold: true, title: title, timer: null };
    _intents.push(it);
    _fold = it;
    it.timer = setTimeout(commitFold, SOFT_REMOVE_MS);
    const fromHeight = _reducedMotion ? 0 : swipe.getBoundingClientRect().height;
    paint();
    tweenSlotHeight(swipe, fromHeight);
    announce(removedFromCartText(title) + ', ' + localeText(
      'undo available',
      'możesz cofnąć',
      'lze vrátit zpět',
      'kann rückgängig gemacht werden'
    , 'ongedaan te maken'));
    /* Focus follows a keyboard removal only. Yanking focus into the strip after
       a thumb tap would move the page under the thumb. */
    if (options && options.keyboard) {
      const undoControl = swipe.querySelector('.item-undo__btn');
      if (undoControl) requestAnimationFrame(function() { try { undoControl.focus(); } catch (e) {} });
    }
    return Promise.resolve(state.cart);
  }

  /** Put the row back. Nothing was written, so nothing is undone on the wire. */
  function undoSoftRemove() {
    const it = _fold;
    if (!it) return;
    _fold = null;
    clearTimeout(it.timer);
    dropIntent(it);
    noteCustomerEdit();
    const found = findLine(state.cart, it.identity);
    const row = found && itemsWrap ? itemsWrap.querySelector('.item[data-key="' + cssKey(found.key) + '"]') : null;
    const swipe = row && row.closest('.item-swipe');
    const fromHeight = (!_reducedMotion && swipe && swipe.isConnected) ? swipe.getBoundingClientRect().height : 0;
    paint();
    tweenSlotHeight(swipe, fromHeight);
    haptic(8);
    announce(it.title + ' ' + localeText(
      'is back in your cart',
      'z powrotem w koszyku',
      'je zpět v košíku',
      'ist zurück im Warenkorb'
    , 'staat weer in je winkelwagen'));
    const back = row && row.isConnected && row.querySelector('.item__remove');
    if (back) requestAnimationFrame(function() { try { back.focus(); } catch (e) {} });
  }

  /** The window closes: the removal becomes an ordinary one and leaves now. */
  function commitFold() {
    const it = _fold;
    if (!it) return;
    _fold = null;
    clearTimeout(it.timer);
    it.fold = false;
    paint();
    flushQty();
  }

  /* A row that cannot offer an undo in place: a preview whose add has not
     left is simply withdrawn; anything else leaves now, and a paid line gets
     the bottom undo toast. */
  function removeNow(line) {
    const identity = lineIdentity(line);
    if (line.__preview) {
      const add = _intents.find(function(i) { return i.kind === 'add' && i.preview && !i.sent && identityKey(lineIdentity(i.preview)) === identityKey(identity); });
      if (add) {
        add.cancelled = true;
        dropIntent(add);
        const j = _jobs.indexOf(add.job);
        if (j >= 0) _jobs.splice(j, 1);
        add.resolve(state.cart);
        paint();
        return Promise.resolve(state.cart);
      }
    }
    if (isGiftItem(line)) return Promise.resolve(state.cart);
    const index = state.cart.items.indexOf(line);
    state.undoData = {
      id: line.variant_id,
      quantity: line.quantity,
      properties: line.properties || {},
      sellingPlan: sellingPlanId(line),
      preview: cloneCart(line),
      index: index
    };
    const title = line.product_title;
    setLineQty(line, 0, 0);
    announce(removedFromCartText(title));
    showUndoToast(title);
    return Promise.resolve(state.cart);
  }

  function undoRemove() {
    commitFold();
    clearUndoTimer();
    const undoData = state.undoData;
    if (!undoData) return;
    state.undoData = null;
    clearStatus('error');
    const preview = normalizeCartItem(cloneCart(undoData.preview));
    preview.key = 'preview:' + (++_previewId);
    const it = addIntent({ vid: Number(undoData.id), qty: Number(undoData.quantity) || 1, props: undoData.properties, plan: undoData.sellingPlan, preview: preview, source: 'undo' });
    _justAdded.add(identityKey(lineIdentity(preview)));
    paint();
    it.promise.then(function() {
      requestAnimationFrame(function() {
        const row = itemsWrap && (itemsWrap.querySelector('.is-just-added') || itemsWrap.querySelector('.item'));
        if (row) { const btn = row.querySelector('.qty__btn'); if (btn) btn.focus(); }
      });
    }, function(e) {
      reportCartError('undo', e, { fatal: false });
    });
    return it.promise;
  }

  /* ═══════════════════════════════════════
     GIFTS ON THE WIRE

     The gift the cart on screen has earned is painted at the tap (overlayGifts).
     Its write waits until the queue has nothing else to send, and is decided
     THEN, against the server cart as it stands: a gift lost and earned back
     inside one save never leaves the browser (cart-optimistic-proof
     gift_minus_plus), a fold's gift is written only when the fold is, and a
     removal queued behind another write can still be taken back. One gift
     line per variant, quantity one, the kit pair for a subscription — the
     entitlement giftEntitlement names, applied as a difference.
  ═══════════════════════════════════════ */
  function giftSignature(cart) {
    return (cart.items || []).filter(function(i) { return hasGiftProperty(i) || isSubscriptionReward(i); })
      .map(function(i) { return Number(i.variant_id) + 'x' + Number(i.quantity); }).sort().join(',');
  }

  /* The server cart with the folds in their undo window taken out, or null
     when nothing is folded. */
  function foldedServer() {
    const folds = _intents.filter(function(it) { return it.kind === 'qty' && it.fold; });
    if (!folds.length) return null;
    const cart = cloneCart(_server);
    folds.forEach(function(it) { const line = findLine(cart, it.identity); if (line) line.__folded = true; });
    return cart;
  }

  function giftPlan(ignoreTried, ignoreThrottle) {
    if (!_server || !_giftsActing) return null;
    if (!ignoreThrottle && !_checkoutMode && Date.now() < _giftThrottledUntil) return null;
    /* A fold in its undo window is not written, and it can be taken back: a
       gift is kept while either the server's lines or the lines on screen
       earn it, and added only when both do (deadlock-proof D: a fold beside
       an upsell never buys a gift the screen does not show). */
    const desired = desiredGiftVids(_server);
    const folded = foldedServer();
    const shown = folded ? desiredGiftVids(folded) : desired;
    const changes = [];
    const adds = [];
    if (desired) {
      const kept = [];
      _server.items.forEach(function(item) {
        if (!hasGiftProperty(item) || isSubscriptionReward(item)) return;
        const vid = Number(item.variant_id);
        if ((desired.indexOf(vid) >= 0 || shown.indexOf(vid) >= 0) && kept.indexOf(vid) < 0) {
          kept.push(vid);
          if (Number(item.quantity) !== 1) changes.push({ identity: lineIdentity(item), qty: 1 });
        } else {
          changes.push({ identity: lineIdentity(item), qty: 0 });
        }
      });
      desired.forEach(function(vid) { if (kept.indexOf(vid) < 0 && shown.indexOf(vid) >= 0) adds.push(vid); });
    }
    const kit = subscriptionGiftCorrection(_server);
    /* the chip the customer chose stopped being given before its write left:
       said once, never posted (cart-gift-correction-key-proof K) */
    const deadChoice = desired && state.giftExplicit && state.selectedGift >= 0 && state.selectedGift < TIER1_SLOTS
      && !giftGivable(Number(CFG.giftVariants[state.selectedGift])) ? state.selectedGift : -1;
    if (!changes.length && !adds.length && !kit && deadChoice < 0) return null;
    const sig = giftSignature(_server) + '>' + (desired || []).join(',') + (kit ? '+kit' : '');
    if (!ignoreTried && sig === _giftTried) return null;
    return { changes: changes, adds: adds, kit: kit, deadChoice: deadChoice, sig: sig };
  }

  /* Said once, to assistive tech, when the gift a basket no longer earns is
     given back: how far the basket is from keeping it. */
  function announceGiftLoss(cart) {
    const paid = paidLineCents(cart);
    const short = Math.max(Number(CFG.thresholdGift) - paid / 100, 0);
    if (!paid || !(short > 0)) return;
    const amount = formatThresholdAmount(short);
    announce(localeText('Add ' + amount + ' to keep your gift', 'Dodaj ' + amount + ', aby zachować prezent', 'Přidej ' + amount + ', ať ti dárek zůstane', 'Leg ' + amount + ' dazu, damit dein Geschenk bleibt', 'Voeg ' + amount + ' toe om je cadeau te houden'));
  }

  async function runGiftSync(job) {
    _giftTried = job.plan.sig;
    giftRetiredThisStep = false;
    let lossSaid = false;
    try {
      /* a retry after a failure the server may have half-answered decides
         from a fresh read, never from the cart it failed against */
      if (job.retry) await readNow();
      /* one write at a time, each decided against the server cart as it
         stands and the cart on screen as it stands NOW: a removal the
         customer has since taken back is simply not sent */
      for (let step = 0; step < 8; step += 1) {
        const plan = giftPlan(true);
        if (!plan) return null;
        if (plan.deadChoice >= 0) {
          await giftRetired(Number(CFG.giftVariants[plan.deadChoice]), plan.deadChoice, null);
          continue;
        }
        if (plan.changes.length || (plan.kit && Object.keys(plan.kit.updates).length)) {
          const kitStep = !plan.changes.length;
          const many = plan.changes.length > 1;
          if (!kitStep && !lossSaid) {
            const owed = desiredGiftVids(_server);
            if (owed && !owed.length) { lossSaid = true; announceGiftLoss(_server); }
          }
          let res;
          try {
            res = await sendWrite(kitStep || many ? CFG.updateUrl : CFG.changeUrl, jsonPost({}), { absolute: true, address: addressGiftStep(kitStep, many) });
          } catch (error) {
            if (error && error.ssSettled) continue;
            throw error;
          }
          applyServer(await parseJsonResponse(res), ++_seq);
          /* the kit is checked again against a fresh read: another writer can
             retire a kit line the moment this batch lets go of the lock, and a
             subscriber's first box must not ship short (the old four-pass rule) */
          if (kitStep) await readNow();
          continue;
        }
        if (plan.kit && plan.kit.add.length) {
          /* Keep It Coming: one kit pair, one-time lines, never a selling plan */
          const res = await sendWrite(CFG.addUrl, jsonPost({ items: plan.kit.add }), {});
          await parseJsonResponse(res);
          _seq += 1;
          await readNow();
          continue;
        }
        if (plan.adds.length) {
          const before = giftSignature(_server);
          /* this step's own answer: a retirement earlier in the run (a dead
             chip) must not license a second post of a gift that left no line */
          giftRetiredThisStep = false;
          await addGiftLine(plan.adds[0], job);
          /* an add that answered and left no line (an app stripping free
             lines, a proxy) is not asked again for the same cart */
          if (giftSignature(_server) === before && !giftRetiredThisStep) {
            /* tried for this cart, whatever cleared the mark earlier in the run
               (a dead chip's retirement) */
            _giftTried = plan.sig;
            return null;
          }
          giftRetiredThisStep = false;
          continue;
        }
        return null;
      }
      return null;
    } catch (error) {
      console.error('[SacrasoulCart] gift sync failed', error); if (!error || !error.ssReported) reportCartError(Number(error && error.status) === 429 || !(error && error.status) ? 'gift_add' : 'gift_correction', error, { fatal: false });
      if (_checkoutMode) { _checkoutFailed = true; return null; }
      if (error && error.ssEpisode) { _giftTried = ''; return null; }
      if (isTransientFailure(error)) { _giftTried = ''; return RETRY; }
      try { await readNow(); } catch (readError) { /* the next read heals */ }
      return null;
    }
  }
  /* The body of one gift-line write, from a cart read inside the write lock.
     Three honest answers, and only one of them sends: the line is held —
     under its current name, which goes on the wire; the cart was read and
     no longer needs this write — said, nothing sent; the cart could not be
     read, or holds the line under a name that cannot go on the wire — an
     unanswered question, nothing sent, asked again later. Treating either of
     the last as "already gone" would ship a free resin. */
  function addressGiftStep(kitStep, many) {
    return async function() {
      let truth;
      try { truth = normalizeCart(await fetchCart()); }
      catch (readError) {
        reportCartError('gift_correction', new Error('gift correction could not read server truth (not sent)'), { substage: 'truth_unreadable', fatal: false });
        throw Object.assign(new Error('gift correction could not read server truth (not sent)'), { status: 0, ssReported: true });
      }
      if (!truth || !Array.isArray(truth.items)) throw Object.assign(new Error('gift correction read no cart (not sent)'), { status: 0 });
      applyServer(truth, _seq, { read: true });
      const plan = giftPlan(true);
      if (kitStep) {
        const updates = plan && plan.kit && !plan.changes.length ? plan.kit.updates : null;
        if (!updates || !Object.keys(updates).length) throw Object.assign(new Error('kit already settled (not sent)'), { ssSettled: true });
        return { updates: updates };
      }
      const changes = plan ? (many ? plan.changes : plan.changes.slice(0, 1)) : [];
      if (!changes.length) {
        reportCartError('gift_correction', new Error('gift line absent from server cart'), { substage: 'already_absent', fatal: false });
        throw Object.assign(new Error('gift correction already settled (not sent)'), { ssSettled: true });
      }
      const updates = {};
      changes.forEach(function(change) {
        const line = findLine(truth, change.identity);
        if (!line || typeof line.key !== 'string' || !line.key || line.key.indexOf('preview:') === 0) {
          reportCartError('gift_correction', new Error('gift line unaddressable in server cart'), { substage: 'truth_unreadable', fatal: false });
          throw Object.assign(new Error('gift line unaddressable in server cart (not sent)'), { status: 0, ssReported: true });
        }
        updates[line.key] = change.qty;
      });
      /* several gift lines leave in one absolute write (a tier-2 pair given
         back is one request, not two) */
      if (many) return { updates: updates };
      return { id: Object.keys(updates)[0], quantity: changes[0].qty };
    };
  }

  let giftRetiredThisStep = false;
  const GIFT_THROTTLE_COOLDOWN_MS = 15000;
  let _giftThrottledUntil = 0;
  let _giftThrottleTimer = null;

  /* One £0 line on the wire, and only after a fresh product read has vouched
     for it: a rotted gift variant bought a shopper a customer-visible 422
     "Cannot find variant" on sacrasoul.pl/products/alchemist (2026-09-13). */
  async function addGiftLine(vid, job) {
    const idx = giftIndexOf(vid);
    const data = CFG.giftProducts ? CFG.giftProducts[idx] : null;
    if (!_server || _server.items.some(function(i) { return hasGiftProperty(i) && Number(i.variant_id) === Number(vid); })) return;
    if (data && data.handle && _availabilityCache.get(Number(vid)) === undefined) {
      await Promise.race([freshProductState(data.handle), sleep(CART_READ_ABORT_MS)]);
    }
    if (!giftIsAvailable(idx)) return giftRetired(vid, idx, null);
    if (data && data.handle && _availabilityCache.get(Number(vid)) === undefined) {
      /* could not ask is not a verdict: nothing retired, nothing posted, tried again later */
      const unsettled = new Error('gift variant unverified — fresh read did not settle');
      reportCartError('gift_add', unsettled, { substage: 'refused_unvalidated', fatal: false });
      unsettled.ssReported = true;
      throw unsettled;
    }
    /* the last word before the wire is the cart as it stands now */
    const now = desiredGiftVids(_server) || [];
    if (now.indexOf(Number(vid)) < 0) return;
    /* A throttled gift add (PostHog, jade-oil, 2026-08-16: the promised gift
       never arrived) is asked again inside Shopify's declared wait, twice at
       most within 2.5 s — never blind: the cart is read first, and a gift
       that committed behind its 429 is a success. A throttle that outlives
       that is said once, and the AUTOMATIC gift waits out the whole declared
       window (15 s at least); the customer's own chip tap does not. */
    const first = Date.now();
    let res;
    for (let retries = 0; ; retries += 1) {
      res = await sendWrite(CFG.addUrl, jsonPost({ items: [{ id: Number(vid), quantity: 1, properties: { _gift: 'true' } }] }), { deadlineAt: first + CART_MUTATION_ABORT_MS });
      if (Number(res.status) !== 429) break;
      const wait = rateLimitDelayMs(res, retries);
      if (retries < RATE_LIMIT_MAX_RETRIES && Date.now() + wait - first <= RATE_LIMIT_RETRY_WINDOW_MS) {
        noteRateLimited(CFG.addUrl);
        await sleep(wait);
        const truth = await readNow();
        if (truth.items.some(function(i) { return hasGiftProperty(i) && Number(i.variant_id) === Number(vid); })) {
          reportCartError('gift_add', new Error('gift committed behind a 429'), { substage: 'landed_despite_refusal', fatal: false, http_status: 429 });
          giftAddedNote(vid);
          return;
        }
        /* the read spent real time, and a sibling tab's write can make a
           committed gift read as absent: no late or unproven re-send */
        if (Date.now() - first <= RATE_LIMIT_RETRY_WINDOW_MS && !siblingMayWrite()) continue;
      }
      const throttled = Object.assign(new Error('Too many requests'), { status: 429 });
      reportCartError('gift_add', throttled);
      _giftThrottledUntil = Date.now() + Math.max(GIFT_THROTTLE_COOLDOWN_MS, Number(res.__ssDeclaredThrottleMs) || 0);
      clearTimeout(_giftThrottleTimer);
      _giftThrottleTimer = setTimeout(pump, _giftThrottledUntil - Date.now() + 10);
      throttled.ssReported = true;
      throttled.ssEpisode = true;
      throw throttled;
    }
    try { await parseJsonResponse(res); }
    catch (error) {
      if (isUnprocessableAdd(error) || Number(error.status) === 404) return giftRetired(vid, idx, error);
      throw error;
    }
    _seq += 1;
    await readNow();
    giftAddedNote(vid);
  }

  function giftAddedNote(vid) {
    emitBehavior('cart_gift_added', cartItemBehaviorProperties(state.cart, vid, 1, state.giftExplicit ? 'selected' : 'automatic', { _gift: 'true' }));
    announce(CFG.strings.giftAdded);
  }

  /* A gift the shop no longer gives: its tile goes, the next gift takes its
     place on screen, and the customer — who saw it — is told. */
  async function giftRetired(vid, idx, error) {
    giftRetiredThisStep = true;
    reportCartError('gift_add', error || new Error('gift variant retired before the POST'), error ? undefined : { substage: 'refused_before_post', fatal: false });
    _availabilityCache.set(Number(vid), false);
    if (CFG.giftProducts && CFG.giftProducts[idx]) CFG.giftProducts[idx].available = false;
    if (state.selectedGift === idx) { state.selectedGift = -1; state.giftExplicit = false; }
    syncGiftAvailabilityUi();
    const next = desiredGiftVids(_server) || [];
    const owned = _server.items.some(function(i) { return hasGiftProperty(i); });
    /* DE and NL translated 2026-09-23 */
    const msg = owned
      ? localeText('That gift just sold out', 'Ten prezent się wyprzedał', 'Dárek se vyprodal', 'Dieses Geschenk ist gerade ausverkauft', 'Dit cadeau is net uitverkocht')
      : next.length
      ? localeText('That gift just sold out — choosing another', 'Ten prezent się wyprzedał — wybieramy inny', 'Dárek se vyprodal — vybíráme jiný', 'Dieses Geschenk ist gerade ausverkauft — wir wählen ein anderes', 'Dit cadeau is net uitverkocht — we kiezen een ander')
      : localeText('The free gift just sold out', 'Prezent gratis właśnie się wyprzedał', 'Dárek zdarma se právě vyprodal', 'Das kostenlose Geschenk ist gerade ausverkauft', 'Het gratis cadeau is net uitverkocht');
    setStatus(msg, 'error', 'error');
    announce(msg);
    _giftTried = '';
    paint();
  }

  function selectGift(index) {
    noteCustomerEdit();
    _giftThrottledUntil = 0;
    state.giftExplicit = true;
    state.selectedGift = index;
    syncGiftSelectionUi(index);
    paint();
    wakeQueue();
    pump();
    return giftSettled();
  }

  /* Equivalent selections share one answer: the promise of the gift write
     that follows them. */
  let _giftSettledPromise = null;
  function giftSettled() {
    if (_giftSettledPromise) return _giftSettledPromise;
    const p = new Promise(function(resolve) { _giftSyncWaiters.push(resolve); });
    _giftSettledPromise = p;
    p.then(function() { if (_giftSettledPromise === p) _giftSettledPromise = null; });
    syncSettled();
    return p;
  }

  /* One tier-2 gift, or the two tier-1 gifts together: its own question, its
     own state, the cart re-drawn to whatever the answer earns. */
  function selectGift2(choice) {
    const wanted = choice === 'both' ? 'both' : 'amber';
    /* Amber is withheld while a paid Amber is in the basket */
    if (wanted === 'amber' && giftTier2AmberPaidInCart(state.cart)) return;
    if (state.selectedGift2 === wanted) return;
    noteCustomerEdit();
    state.selectedGift2 = wanted;
    state.gift2Explicit = true;
    syncGiftSelectionUi();
    paint();
    wakeQueue();
    pump();
  }

  /* ═══════════════════════════════════════
     CHECKOUT — the one step that waits

     The tap is answered in its own frame ("Przygotowujemy…"), then the queue
     drains, the cart is read once, the kit and the gift rules are checked on
     that truth, and the server's cart is compared with the cart that was on
     screen at the tap. Equal: go. Different: show the server's cart, say so,
     stay — the next tap compares afresh, and a second identical difference
     goes through (2026-07-29: a checkout that holds forever is the worse
     failure; Shopify's checkout computes the real order anyway). Bounded at
     9.5 s, then go.
  ═══════════════════════════════════════ */
  let _checkoutRun = 0;
  let _heldPrint = null;

  /* A cart as a comparable thing: paid lines by variant, properties and
     selling plan with their quantity; gift lines by variant with their
     quantity. The SERVER side is built from the server's cart alone — no
     client bookkeeping may hide a line it still holds (a failed removal once
     let checkout bill the line it had hidden). */
  function cartAsShown(cart) {
    const paid = {};
    const gifts = {};
    ((cart && cart.items) || []).forEach(function(item) {
      if (item.__folded || isSubscriptionReward(item)) return;
      const qty = Number(item.quantity || 0);
      if (!qty) return;
      if (hasGiftProperty(item)) {
        gifts[Number(item.variant_id)] = (gifts[Number(item.variant_id)] || 0) + qty;
        return;
      }
      const sig = Number(item.variant_id) + '|' + linePropsFingerprint(item);
      paid[sig] = (paid[sig] || 0) + qty;
    });
    return { paid: paid, gifts: gifts };
  }
  function shownPrint(shown) {
    const flat = function(o) { return Object.keys(o).sort().map(function(k) { return [k, o[k]]; }); };
    return JSON.stringify([flat(shown.paid), flat(shown.gifts), !!shown.unsettled]);
  }

  function ackCheckoutTap() {
    if (!checkoutBtn) return;
    checkoutBtn.classList.remove('is-tap-ack');
    void checkoutBtn.offsetWidth;
    checkoutBtn.classList.add('is-tap-ack');
  }

  async function proceedToCheckout() {
    if (state.checkoutPending) {
      /* every tap toward checkout is answered, even "already going"
         ($dead_click on "Przejdź do płatności", 2026-07-25) */
      ackCheckoutTap();
      announce(localeText('Taking you to checkout', 'Przechodzimy do kasy', 'Přech\xE1z\xEDme k pokladně', 'Weiter zur Kasse', 'Door naar afrekenen'));
      return;
    }
    if (!state.cart || !visibleItemCount(state.cart)) return;
    ackCheckoutTap();
    clearStatus('error');
    const shown = cartAsShown(state.cart);
    const attempt = ++_checkoutRun;
    const deadline = Date.now() + CHECKOUT_BOUND_MS;
    state.checkoutPending = true;
    _checkoutMode = true;
    _checkoutFailed = false;
    _checkoutPaidFailed = false;
    updateSyncUi();
    commitFold();
    flushQty();
    wakeQueue();
    pump();
    const within = function(promise) {
      return Promise.race([promise, sleep(Math.max(0, deadline - Date.now())).then(function() { return TIMED_OUT; })]);
    };
    let _handoffPhase = 'wait_idle';
    let truth = null;
    let unsettled = false;
    try {
      if ((await within(drained())) === TIMED_OUT) throw TIMED_OUT;
      _handoffPhase = 'fresh_cart';
      truth = await within(queuedRead());
      if (truth === TIMED_OUT) throw TIMED_OUT;
      /* the gift rules and the kit, on server truth: whatever they still owe
         is written now, once, and the cart read again */
      _giftsActing = true;
      _giftTried = '';
      if (giftPlan()) {
        _handoffPhase = 'gift_cleanup';
        if ((await within(drained())) === TIMED_OUT) throw TIMED_OUT;
        truth = await within(queuedRead());
        if (truth === TIMED_OUT) throw TIMED_OUT;
      }
      /* the Keep It Coming kit: a kit that cannot be verified never leaves
         (Sho-authorised gate, 053befa7d) */
      if (subscriptionGiftCorrection(truth)) {
        checkoutStays(attempt, kitNotReadyText(), 'subscription_gifts', new Error('Subscription gifts unverified'), truth);
        return;
      }
      /* anything the gift rules still owe after that one pass is a
         difference the customer is shown, never a silent pass */
      if (giftPlan(true) || _checkoutFailed) unsettled = true;
    } catch (error) {
      if (attempt !== _checkoutRun) return;
      if (error === TIMED_OUT) {
        /* bounded: Shopify's checkout recomputes the order, so go — except a
           Keep It Coming cart, whose kit is never handed over unverified */
        if (mayOweKit(state.cart)) {
          checkoutStays(attempt, kitNotReadyText(), 'subscription_gifts', new Error('Subscription gifts unverified (bound reached)'), null);
          return;
        }
        /* never bill a line the shopper changed or removed before Shopify
           has confirmed it: stay, say so, the next tap tries again. Only
           gift uncertainty may go (Shopify's gift Function is the last gate) */
        if (paidUnconfirmed()) {
          checkoutStays(attempt, CFG.strings.checkoutError, 'paid_unconfirmed', new Error('a paid change is not confirmed (bound reached)'), null);
          return;
        }
        reportCartError('checkout_handoff', new Error('checkout validation bound reached'), { substage: _handoffPhase, fatal: false });
        return leaveForCheckout(attempt);
      }
      reportCartError('checkout_handoff', error, { substage: _handoffPhase, fatal: false });
      unsettled = true;
    }
    if (attempt !== _checkoutRun) return;
    if (!truth) {
      /* the cart could not be read: Shopify's checkout recomputes the order,
         so go — except a Keep It Coming cart (053befa7d: a kit that cannot be
         verified never leaves) */
      if (mayOweKit(state.cart)) {
        checkoutStays(attempt, kitNotReadyText(), 'subscription_gifts', new Error('Subscription gifts unverified (cart unreadable)'), null);
        return;
      }
      /* a paid write failed and the cart cannot be read: what checkout
         would bill is unknown, so the shopper stays */
      if (paidUnconfirmed()) {
        checkoutStays(attempt, CFG.strings.checkoutError, 'paid_unconfirmed', new Error('a paid change is not confirmed (cart unreadable)'), null);
        return;
      }
      return leaveForCheckout(attempt);
    }
    if (!Number(truth.item_count)) {
      checkoutStays(attempt, null, null, null, truth);
      return;
    }
    const serverShown = cartAsShown(truth);
    if (unsettled) serverShown.unsettled = true;
    const serverPrint = shownPrint(serverShown) + '@' + _cartEdits;
    const heldPrint = _heldPrint;
    _heldPrint = null;
    if (!unsettled && shownPrint(serverShown) === shownPrint(shown)) return leaveForCheckout(attempt);
    if (serverPrint === heldPrint) {
      /* the same difference twice, nothing touched in between: go */
      reportCartError('checkout_handoff', new Error('server cart still differs from the screen (let through)'), { substage: 'navigate', fatal: false });
      return leaveForCheckout(attempt);
    }
    _heldPrint = serverPrint;
    const paidOnly = function(s) { return JSON.stringify(Object.keys(s.paid).sort().map(function(k) { return [k, s.paid[k]]; })); };
    const giftOnly = !unsettled && paidOnly(serverShown) === paidOnly(shown);
    const msg = (giftOnly || (unsettled && paidOnly(serverShown) === paidOnly(shown)))
      ? localeText(
        'Your free gift changed to match your basket. Check it, then tap again.',
        'Prezent zmienił się zgodnie z koszykiem. Sprawdź go i dotknij ponownie.',
        'Dárek se změnil podle košíku. Zkontroluj ho a klepni znovu.',
        'Dein Geschenk wurde an deinen Warenkorb angepasst. Prüfe ihn und tippe erneut.',
        'Je cadeau is aangepast aan je winkelwagen. Controleer hem en tik opnieuw.')
      : localeText(
        'Your basket changed while we saved it. Check it, then tap again.',
        'Koszyk zmienił się podczas zapisywania. Sprawdź go i dotknij ponownie.',
        'Košík se při ukládání změnil. Zkontroluj ho a klepni znovu.',
        'Dein Warenkorb hat sich beim Speichern geändert. Prüfe ihn und tippe erneut.',
        'Je winkelwagen is veranderd tijdens het opslaan. Controleer hem en tik opnieuw.');
    checkoutStays(attempt, msg, 'qty_unconfirmed_hold', new Error(giftOnly
      ? 'server gift lines differ from the cart on screen (held once)'
      : 'server cart differs from the cart on screen (held once)'), truth);
  }
  const TIMED_OUT = { timedOut: true };

  /* a paid quantity or removal the server has not confirmed, or one that
     failed during this checkout */
  function paidUnconfirmed() {
    /* an intent Shopify answered (doneSeq) is confirmed even while it waits
       for a readable cart to retire it */
    return _checkoutPaidFailed || _intents.some(function(it) { return it.kind === 'qty' && it.doneSeq == null && !it.cancelled; });
  }

  function queuedRead() {
    return new Promise(function(resolve, reject) {
      enqueue({ kind: 'read', tap: true, run: async function() {
        try { resolve(await readNow()); } catch (error) { reject(error); }
        return null;
      } });
    });
  }

  /* The door stays shut this once: the server's own cart on screen (every
     intent has landed or failed, and this tab no longer paints a gift the
     server does not hold), the reason said, the next tap compares afresh. */
  function checkoutStays(attempt, msg, substage, error, truth) {
    if (attempt !== _checkoutRun) return;
    state.checkoutPending = false;
    _checkoutMode = false;
    if (truth) {
      _intents = _intents.filter(function(it) { return it === _fold; });
      _giftsActing = false;
      applyServer(truth, _seq, { mirror: true });
    }
    updateSyncUi();
    if (msg) {
      setStatus(msg, substage === 'subscription_gifts' ? 'error' : 'notice', 'error');
      announce(msg);
    }
    if (error) reportCartError('checkout_handoff', error, { substage: substage, fatal: substage === 'subscription_gifts' });
    pump();
  }

  function leaveForCheckout(attempt) {
    if (attempt !== _checkoutRun) return;
    emitBehavior('checkout_started', cartBehaviorProperties('standard'));
    try {
      window.location.href = getCheckoutUrl();
    } catch (error) {
      /* a navigation the browser refused: the shopper is still here, and says so */
      state.checkoutPending = false;
      _checkoutMode = false;
      setStatus(CFG.strings.checkoutError, 'error', 'error');
      updateSyncUi();
      reportCartError('checkout_handoff', error, { substage: 'navigate' });
    }
  }

  /* bfcache restore (layout/theme.liquid's earliest pageshow): the page comes
     back exactly as it left, and only the cart can be stale. Whatever was
     still owed is sent, and the cart is read once, silently: a restore never
     decides a gift. */
  function reconcileRestore() {
    if (state.checkoutPending) {
      _checkoutRun += 1;
      state.checkoutPending = false;
      _checkoutMode = false;
      updateSyncUi();
    }
    /* pagehide turned the fold into an owed removal (and sent it keepalive):
       it is owed until the queue confirms it */
    flushQty();
    wakeQueue();
    readCart({ mirror: true }).catch(function() {});
    pump();
  }

  /* Attribution writes its private __ss_* cart attributes through the same
     queue, never its own POST: two concurrent cart POSTs lost a unit, live.
     The consent guard is asked at the wire on every attempt. */
  function writeCartUpdate(payload, kind, guard) {
    return new Promise(function(resolve, reject) {
      enqueue({ kind: kind || 'update', tap: kind === 'discount', run: async function() {
        try {
          const res = await sendWrite(CFG.updateUrl, jsonPost(payload), { absolute: true, guard: guard });
          let cart = await parseJsonResponse(res);
          const seq = ++_seq;
          /* a code's answer must carry the code; when the update response
             does not, one read does */
          if (payload && Object.prototype.hasOwnProperty.call(payload, 'discount')
              && !(cart && Array.isArray(cart.cart_level_discount_applications) && Array.isArray(cart.discount_codes))) {
            cart = await fetchCart();
          }
          applyServer(cart, seq, kind === 'discount' ? { read: true } : undefined);
          resolve(_server);
        } catch (error) {
          reject(error);
        }
        return null;
      } });
    });
  }


  function syncUndoToastOffset() {
    if (!drawer) return;
    const footerHeight = footerEl && footerEl.style.display !== 'none' ? footerEl.offsetHeight : 0;
    /* the CTA sits inside the footer, so footerHeight already counts it */
    const baseOffset = Math.max(22, footerHeight + 18);
    drawer.style.setProperty('--undo-bottom-offset', baseOffset + 'px');
  }

  /* Does the list continue past the fold, and where are we in it. Three
     classes, written only when they change, so this never touches the DOM on
     a frame where nothing moved. */
  function syncScrollAffordance() {
    if (!body) return;
    const overflow = body.scrollHeight - body.clientHeight;
    const hasMore = overflow > 8;
    const atTop = body.scrollTop <= 4;
    const atEnd = body.scrollTop >= overflow - 4;
    if (body.classList.contains('has-more') !== hasMore) body.classList.toggle('has-more', hasMore);
    if (body.classList.contains('at-top') !== atTop) body.classList.toggle('at-top', atTop);
    if (body.classList.contains('at-end') !== atEnd) body.classList.toggle('at-end', atEnd);
  }
  let _scrollAffFrame = null;
  function scheduleScrollAffordance() {
    if (_scrollAffFrame) return;
    _scrollAffFrame = requestAnimationFrame(function() { _scrollAffFrame = null; syncScrollAffordance(); });
  }

  function scheduleUndoToastOffset() {
    if (_offsetSyncFrame) return;
    _offsetSyncFrame = requestAnimationFrame(function() {
      _offsetSyncFrame = null;
      syncUndoToastOffset();
    });
  }

  let _errorClearTimer = null;
  /* A recoverable message the shopper never had time to read is not a message.
     applyUIState clears the error line the instant any cart update paints
     cleanly -- the right rule for an error a later success has made stale, and
     the wrong one for the failure still on screen. A remove that genuinely
     fails re-reads the cart and can auto-add a gift straight afterwards; that
     repaint wiped the line inside the same tick. Measured against the shipped
     engine on 2026-08-25, the failed-remove banner lived under 50ms, which is
     why "a genuine remove failure tells the shopper nothing" read as missing UI
     when the UI was there and being erased.
     So the courtesy clear waits until the message has had a readable moment.
     setStatus's own 4s timer still ends it; this only stops something else
     ending it first, and bounds how long a stale error can outlive its cure. */
  const STATUS_ERROR_MIN_VISIBLE_MS = 2500;
  let _statusErrorAt = 0;

  function setStatus(text, tone, source) {
    if (source === 'error') _statusErrorAt = drawer.classList.contains('is-open') ? Date.now() : 0;
    state.status = { text: text || '', tone: tone || null, source: source || null };
    if (!ctaStatus) return;
    ctaStatus.textContent = state.status.text;
    ctaStatus.className = 'cta__status' + (state.status.tone ? ' cta__status--' + state.status.tone : '');
    ctaStatus.hidden = !state.status.text;
    /* auto-clear errors after 4s so they don't stick */
    if (_errorClearTimer) { clearTimeout(_errorClearTimer); _errorClearTimer = null; }
    if ((source === 'error' && _statusErrorAt) || source === 'discount') {
      /* A notice asks the customer to do something — look at the cart before
         adding again. Four seconds is enough to read a failure and forget it;
         it is not enough to read an instruction and act on it. */
      _errorClearTimer = setTimeout(function() {
        _errorClearTimer = null;
        clearStatus(source);
      }, tone === 'notice' ? 9000 : 4000);
    }
  }

  function clearStatus(source) {
    if (source && state.status.source !== source) return;
    setStatus('', null, null);
  }

  /* The drawer never looks busy while it syncs. What still speaks, and only
     where it is true:
       * the checkout button says "Przygotowujemy…" / "Securing" from the tap
         that asked for checkout until the page leaves (a label that stayed
         put for a 5 s save read as a dead button);
       * the page going to checkout is inert, and dims the code and pairings;
       * the express wallets stay locked while anything is unsaved: they read
         the server cart at their tap and cannot wait for us;
       * under a live discount whose arithmetic we have not reproduced, the
         TOTAL alone waits for the queue (a calm skeleton, one piece). */
  function updateSyncUi() {
    const empty = !state.cart || !visibleItemCount(state.cart);
    const settled = isSettled();
    /* the boot shell's pending look ends with the engine's first paint */
    drawer.classList.remove('is-totals-pending', 'is-totals-unavailable');
    drawer.classList.toggle('is-interaction-locked', state.checkoutPending);
    drawer.classList.toggle('is-checkout-blocked', empty || !settled || state.checkoutPending);
    const approx = !!(state.cart && (state.cart.__approx || (state.cart.__pendingUnknown && !settled)));
    drawer.classList.toggle('is-total-pending', approx && !settled);
    if (footerEl) {
      if (approx && !settled) footerEl.setAttribute('aria-busy', 'true');
      else footerEl.removeAttribute('aria-busy');
    }
    if (body) body.toggleAttribute('inert', state.checkoutPending);
    if (discountWrap) discountWrap.toggleAttribute('inert', state.checkoutPending);
    if (undoBtn) undoBtn.disabled = state.checkoutPending;
    if (checkoutBtn) {
      /* NEVER native-disabled before the handoff (owner ruling: the browser
         swallows clicks on disabled controls before any listener can hear the
         intent). Empty speaks through aria-disabled. */
      checkoutBtn.disabled = state.checkoutPending;
      checkoutBtn.setAttribute('aria-disabled', empty && !state.checkoutPending ? 'true' : 'false');
      checkoutBtn.classList.toggle('is-disabled', empty && !state.checkoutPending);
      checkoutBtn.classList.toggle('is-loading', state.checkoutPending);
    }
    if (ctaLabel) {
      const label = state.checkoutPending
        ? (CFG.strings.preparingCheckout || localeText('Securing', 'Przygotowujemy…', 'Připravujeme', 'Wird vorbereitet', 'Voorbereiden…'))
        : CFG.strings.secureCheckout;
      if (ctaLabel.textContent !== label) ctaLabel.textContent = label;
    }
    if (state.checkoutPending || state.status.source === 'checkout') clearStatus('checkout');
  }

  function renderLoadingState() {
    if (!itemsWrap) return;
    /* skeleton parity: one placeholder per known line, so the settle never
       jumps the list height (a lone skeleton against a 3-line cart popped) */
    var knownLines = 1;
    if (state.cart && state.cart.items && state.cart.items.length) knownLines = state.cart.items.length;
    else {
      var cached = loadCartCache && loadCartCache();
      if (cached && cached.items && cached.items.length) knownLines = cached.items.length;
    }
    knownLines = Math.max(1, Math.min(knownLines, 4));
    var one = '<div class="skeleton-item"><div class="skeleton-block skeleton-img"></div><div class="skeleton-info"><div class="skeleton-block skeleton-title"></div><div class="skeleton-block skeleton-variant"></div><div class="skeleton-block skeleton-price"></div></div></div>';
    itemsWrap.innerHTML = new Array(knownLines + 1).join(one);
    drawer.classList.remove('cart--empty');
    if (emptyEl) {
      emptyEl.classList.remove('is-visible');
      emptyEl.setAttribute('aria-hidden', 'true');
    }
    /* the footer stays, its total a calm skeleton until the add is read —
       hiding it made the whole CTA block pop in later */
    if (footerEl) footerEl.style.display = '';
    if (divider) divider.style.display = '';
  }

  /* ═══════════════════════════════════════
     DRAWER OPEN / CLOSE
  ═══════════════════════════════════════ */
  let _drawerTrigger = null;
  /* boot captured the true opener before the shell stole focus */
  if (window.__ssOpener && window.__ssOpener !== document.body) _drawerTrigger = window.__ssOpener;
  let _drawerOpenedAt = 0;
  let _drawerFocusFrame = null;

  function openDrawer(options) {
    const opts = options || {};
    const wasOpen = drawer.classList.contains('is-open');
    const bootReportedOpen = window.__ssCartBootOpenReported === true;
    if (bootReportedOpen) window.__ssCartBootOpenReported = false;
    /* remember what opened the drawer so we can return focus on close */
    /* the boot shell may have moved focus into the drawer before the engine
       arrived; an element inside the drawer can never be the thing to return to */
    var activeNow = document.activeElement;
    if (activeNow && drawer.contains(activeNow)) {
      activeNow = (window.__ssOpener && !drawer.contains(window.__ssOpener)) ? window.__ssOpener : null;
    }
    /* An add or refresh may reopen an already visible drawer. Keep both the
       original return target and the control the shopper is using. */
    if (!wasOpen || !_drawerTrigger) _drawerTrigger = opts.trigger || activeNow || null;
    _drawerOpenedAt = Date.now();
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    /* Contents gate bound (snippets/cart-drawer.liquid): the sheet's own rule
       releases the gate; a hung sheet is released at 4s. Boot owns the one
       implementation and shares it the way it shares SSBehavior.emit. */
    if (window.__ssSkinBound) window.__ssSkinBound(drawer);
    /* Measure the classic scrollbar BEFORE any lock so body.sacrasoul-drawer-open
       can pad it back (css --ss-sb); boot.js does the same on the first open. */
    var sb = window.innerWidth - document.documentElement.clientWidth;
    if (sb > 0) document.documentElement.style.setProperty('--ss-sb', sb + 'px');
    document.body.classList.add('sacrasoul-drawer-open');
    document.documentElement.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    /* A failure that arrived off-screen still deserves its readable window. */
    if (state.status.source === 'error' && !_statusErrorAt) {
      setStatus(state.status.text, state.status.tone, state.status.source);
    }
    /* each open is its own look at the nudge */
    if (!wasOpen) _nudgeShownKey = '';
    if (!wasOpen && !bootReportedOpen) {
      emitBehavior('cart_opened', cartBehaviorProperties(
        opts.fromAdd ? 'after_add' : (opts.trigger ? 'cart_control' : 'programmatic')
      ));
    }
    /* The cart on screen, drawn now. A deep-link open with nothing in the
       served snapshot shows the last cart this browser saw while the read
       below asks Shopify — never a skeleton that nothing will ever replace. */
    if (!_intents.length && _server && !_server.item_count) {
      const cached = loadCartCache();
      if (cached && cached.item_count > 0) _server = cached;
    }
    paint();
    /* opening the drawer is the moment of truth: one read, unless one has
       just landed (the boot read, an add's own read) */
    scheduleOpenRead();
    /* the arrival line reads only the Warsaw clock and the country — never
       the cart's contents. Refreshing it on open (and at init) is enough;
       recomputing the whole shipping-days table on every mutation settle was
       pure waste (owner ruling 2026-07-26 kept: a pre-order line still speaks
       on its own row). */
    updateDispatch();
    scheduleUndoToastOffset();
    scheduleUpsellLoad();
    /* re-check upsell + gift products against the live store so a stale
       page can never recommend something that already sold out */
    revalidateCatalog().catch(function() {});
    /* a render skipped while the drawer was closed left the signature
       invalidated — re-render the carousel for the current cart */
    /* read before the call below commits a new signature */
    const railUnchanged = !!(state.cart && _upsellSignature && getUpsellSignature(state.cart) === _upsellSignature);
    if (state.cart && _catalogReady) updateUpsellStates(state.cart);
    /* A reopen on an unchanged cart repaints nothing, so the nudge already on
       the rail is this open's showing. A changed cart repaints and reports
       itself from noteNudgePainted. */
    if (!wasOpen && railUnchanged && _nudgeLive && upsellTrack && upsellTrack.querySelector('.urow--nudge')) {
      _nudgeShownKey = _nudgeLive.key;
      emitBehavior('cart_nudge_shown', nudgeBehaviorProperties(_nudgeLive));
    }
    /* check item availability so any out-of-stock items surface before checkout */
    if (state.cart && state.cart.item_count > 0) {
      checkAvailability(state.cart).catch(function() {});
    }
    /* preconnect to checkout domain so TLS handshake is ready */
    if (state.cart && state.cart.item_count > 0 && !document.querySelector('link[rel="preconnect"][href*="checkout"]')) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = 'https://checkout.shopify.com';
      document.head.appendChild(link);
    }
    maybeShowCartSaved(opts);
    /* Focus the dialog, not the close button. Chrome treats a programmatic
       .focus() as keyboard-originated, so focusing the X painted Dawn's full
       focus-visible treatment — a 2px rust outline plus a double box-shadow —
       around the close button every single time the cart opened, including on
       a phone where no one has a keyboard. Focusing the dialog itself is the
       standard pattern: the label is announced, Tab still lands on the first
       control inside, and nothing is drawn. */
    if (!wasOpen || bootReportedOpen) {
      if (_drawerFocusFrame !== null) cancelAnimationFrame(_drawerFocusFrame);
      _drawerFocusFrame = requestAnimationFrame(function() {
        _drawerFocusFrame = null;
        if (drawer.classList.contains('is-open')) drawer.focus({ preventScroll: true });
      });
    }
  }

  function maybeShowCartSaved(opts) {
    if (opts && (opts.forceLoading || opts.fromAdd)) return;
    if (!state.cart || !state.cart.item_count) return;
    try {
      if (sessionStorage.getItem('sacrasoul_cart_seen')) return;
      sessionStorage.setItem('sacrasoul_cart_seen', '1');
    } catch (e) { return; }

    const msg = document.createElement('div');
    msg.className = 'cart-saved-msg';
    msg.setAttribute('role', 'status');
    /* DE and NL translated 2026-09-23 */
    msg.textContent = localeText('Your cart is still here', 'Twój koszyk wciąż tu jest', 'Tvůj košík tu stále je', 'Dein Warenkorb ist noch da', 'Je winkelwagen is er nog');
    if (itemsWrap && itemsWrap.parentNode) {
      itemsWrap.parentNode.insertBefore(msg, itemsWrap);
    }
    setTimeout(function() {
      msg.classList.add('is-fading');
      setTimeout(function() { if (msg.parentNode) msg.parentNode.removeChild(msg); }, DUR_REVEAL);
    }, 2500);
  }

  let _openReadTimer = null;
  function scheduleOpenRead() {
    clearTimeout(_openReadTimer);
    _openReadTimer = setTimeout(function() {
      _openReadTimer = null;
      if (state.checkoutPending || !drawer.classList.contains('is-open')) return;
      if (Date.now() - _lastReadAt < 1500) return;
      /* an add on its way reads the cart when it lands: that read is this one */
      if (_needsRead || _intents.some(function(it) { return it.kind === 'add'; })) return;
      readCart({}).catch(function() {});
    }, 400);
  }

  function closeDrawer() {
    if (!drawer.classList.contains('is-open')) return;
    if (_drawerFocusFrame !== null) cancelAnimationFrame(_drawerFocusFrame);
    if (state.status.source === 'error') {
      /* Seen for its minimum window: it has done its job and must not replay
         over a cart the shopper has since fixed. Never seen (arrived while the
         drawer was closed): keep it for the next open. */
      var seen = _statusErrorAt && Date.now() - _statusErrorAt >= STATUS_ERROR_MIN_VISIBLE_MS;
      _statusErrorAt = 0;
      clearTimeout(_errorClearTimer);
      _errorClearTimer = null;
      if (seen) clearStatus('error');
    }
    /* the undo window closes with the drawer — an offer nobody can see is not
       an offer, and the cart must be truthful the moment it leaves the screen */
    commitFold();
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.classList.remove('sacrasoul-drawer-open');
    document.documentElement.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    /* return focus to the element that opened the drawer */
    const trigger = _drawerTrigger;
    _drawerTrigger = null;
    if (trigger && typeof trigger.focus === 'function') {
      _drawerFocusFrame = requestAnimationFrame(function() {
        _drawerFocusFrame = null;
        if (!drawer.classList.contains('is-open') && trigger.isConnected) trigger.focus({ preventScroll: true });
      });
    }
  }

  /* ═══════════════════════════════════════
     JOURNEY PROGRESS BAR
  ═══════════════════════════════════════ */
  var _lastJourneyPct = -1;
  var _journeyMsgTurnTimer = null;
  var _lastJourneyShipping = null;
  var _lastJourneyGift = null;
  var _lastJourneyGiftInCart = null;
  var _lastJourneyGift2 = null;

  /* ── Threshold ticks ──
     The rail is a map, not only a meter: each threshold owns a notch at the
     position the fill has to reach, and the notch is derived from the SAME
     journeyGoal the fill divides by — one number, so they cannot drift.

     Liquid paints the ticks server-side. JS only rebuilds them when the
     thresholds themselves change under it (a country switch rewrites
     CFG.thresholdShipping from thresholdMap after first paint); otherwise it
     just carries the earned state across. Nothing here touches the fill. */
  function journeyTickName(kind) {
    if (kind === 'shipping') return localeText('Free shipping', 'Darmowa dostawa', 'Doprava zdarma', 'Gratisversand', 'Gratis verzending');
    return localeText('Your gift', 'Prezent', 'Dárek', 'Geschenk', 'Je cadeau');
  }

  function journeyTickPlan() {
    const shippingEligible = Number(CFG.thresholdShipping) > 0;
    const gift = Number(CFG.thresholdGift) || 0;
    const shipping = shippingEligible ? Number(CFG.thresholdShipping) : 0;
    /* The second gift is the last stop on the rail when it is configured, so the
       whole journey is drawn to it — otherwise the bar would read full while a
       reward the customer can still earn sits past its end. */
    const gift2 = tier2Configured() ? Number(CFG.thresholdGift2) : 0;
    const goal = Math.max(gift, shipping, gift2);
    if (!(goal > 0)) return [];
    const pos = function(v) { return Math.max(0, Math.min((v / goal) * 100, 100)); };
    const ticks = [];
    if (shippingEligible && shipping === gift) {
      ticks.push({ pos: pos(gift), name: journeyTickName('shipping') + ' · ' + journeyTickName('gift'), kind: 'both' });
    } else {
      ticks.push({ pos: pos(gift), name: journeyTickName('gift'), kind: 'gift' });
      if (shippingEligible) ticks.push({ pos: pos(shipping), name: journeyTickName('shipping'), kind: 'shipping' });
    }
    if (gift2 > 0) ticks.push({ pos: pos(gift2), name: journeyTickName('gift'), kind: 'gift2' });
    ticks.sort(function(a, b) { return a.pos - b.pos; });
    return ticks;
  }

  /* Each milestone says what it is (2026-09-25): a drawn parcel for delivery,
     a drawn gift for each gift. Byte-identical to the Liquid first paint. */
  /* function declarations: hoisted, so an early repaint can never read them unset */
  function journeyTickShip() { return '<svg class="journey__tick-ico" viewBox="0 0 24 24"><path d="M3.8 7.2h9.9v8.9H3.8z"/><path d="M13.7 10.2h3.7l2.8 3v2.9h-6.5"/><circle cx="7.3" cy="16.9" r="1.7"/><circle cx="16.9" cy="16.9" r="1.7"/></svg>'; }
  function journeyTickGift() { return '<svg class="journey__tick-ico" viewBox="0 0 24 24"><path d="M5 11h14v8.5H5z"/><path d="M4 7.8h16V11H4z"/><path d="M12 7.8v11.7"/><path d="M12 7.8C10.9 5.2 7.6 4.6 7.6 6.6c0 1.3 2.2 1.2 4.4 1.2Zm0 0c1.1-2.6 4.4-3.2 4.4-1.2 0 1.3-2.2 1.2-4.4 1.2Z"/></svg>'; }

  function syncJourneyTicks(shippingDone, giftDone, gift2Done) {
    if (!journeyTicks) return;
    const plan = journeyTickPlan();
    const sig = String(CFG.thresholdShipping) + '|' + String(CFG.thresholdGift) + '|' + String(CFG.thresholdGift2 || 0);
    if (journeyTicks.getAttribute('data-sig') !== sig || journeyTicks.children.length !== plan.length) {
      journeyTicks.setAttribute('data-sig', sig);
      journeyTicks.innerHTML = plan.map(function(t) {
        const edge = t.pos >= 99 ? ' journey__tick--end' : (t.pos <= 6 ? ' journey__tick--start' : '');
        return '<span class="journey__tick' + edge + '" data-kind="' + t.kind + '" style="left: ' + t.pos.toFixed(2) + '%;">'
          + '<span class="journey__tick-mark">' + (t.kind === 'shipping' ? journeyTickShip() : t.kind === 'both' ? journeyTickShip() + journeyTickGift() : journeyTickGift()) + '</span>'
          + '</span>';
      }).join('');
    }
    $$('.journey__tick', journeyTicks).forEach(function(el) {
      const kind = el.getAttribute('data-kind');
      let met;
      if (kind === 'shipping') met = !!shippingDone;
      else if (kind === 'gift') met = !!giftDone;
      else if (kind === 'both') met = !!(shippingDone && giftDone);
      else if (kind === 'gift2') met = !!gift2Done;
      else return; /* server-rendered tick with no kind yet — leave its state alone */
      el.classList.toggle('is-met', met);
    });
  }

  function updateJourney(cart) {
    /* calculate total excluding gift items */
    let totalCents = regularTotalCents(cart);
    const total = totalCents / 100;
    const shippingEligible = Number(CFG.thresholdShipping) > 0;
    const remainingShipping = shippingEligible ? Math.max(CFG.thresholdShipping - total, 0) : 0;
    const remainingGift = Math.max(CFG.thresholdGift - total, 0);
    const shippingDone = shippingEligible && total >= CFG.thresholdShipping;
    const giftDone = total >= CFG.thresholdGift;
    /* Tier 2. Every term below collapses to today's behaviour when it is off. */
    const tier2On = tier2Configured();
    const remainingGift2 = tier2On ? Math.max(Number(CFG.thresholdGift2) - total, 0) : 0;
    const gift2Done = tier2On && total >= Number(CFG.thresholdGift2);
    /* The gift is auto-included the moment it is earned. The chooser, the
       journey line and the chips therefore speak in ONE final voice from the
       first frame — "your gift, included", default chip chosen — instead of
       inviting a pick and then correcting themselves a second later when the
       £0 line lands. The line itself is invisible and free, so its arrival
       changes nothing on screen. */
    const giftAssured = giftDone && (state.giftInCart || anyGiftAvailable());

    /* Fill toward the last available reward. In most markets shipping unlocks
       first. In Switzerland, Cyprus and Malta the gift unlocks first and free
       shipping comes later, so the rail must continue to the higher threshold. */
    const allRewardsDone = giftDone && (!shippingEligible || shippingDone) && (!tier2On || gift2Done);
    const journeyGoal = Math.max(Number(CFG.thresholdGift) || 0, shippingEligible ? Number(CFG.thresholdShipping) || 0 : 0, tier2On ? Number(CFG.thresholdGift2) || 0 : 0);
    let fillPct;
    if (allRewardsDone) {
      fillPct = 100;
    } else {
      fillPct = journeyGoal > 0 ? Math.max(0, Math.min((total / journeyGoal) * 100, 100)) : 100;
    }

    /* skip DOM work if nothing changed */
    const pctRounded = Math.round(fillPct * 100);
    if (pctRounded === _lastJourneyPct && shippingDone === _lastJourneyShipping && giftDone === _lastJourneyGift && giftAssured === _lastJourneyGiftInCart && gift2Done === _lastJourneyGift2) return;

    /* A NARRATIVE beat (reward earned or lost) is different from an amount tick.
       Amounts snap — decisive, like money. The story line gets one calm breath:
       fade out, turn the sentence, fade back. First paint (memo at -1) never fades. */
    const journeyStateChanged = _lastJourneyPct !== -1 && (
      shippingDone !== _lastJourneyShipping ||
      giftDone !== _lastJourneyGift ||
      gift2Done !== _lastJourneyGift2 ||
      giftAssured !== _lastJourneyGiftInCart
    );

    _lastJourneyPct = pctRounded;
    _lastJourneyShipping = shippingDone;
    _lastJourneyGift = giftDone;
    _lastJourneyGiftInCart = giftAssured;
    _lastJourneyGift2 = gift2Done;

    if (journeyFill) journeyFill.style.transform = 'scaleX(' + (fillPct / 100).toFixed(4) + ')';
    /* all available rewards earned: nothing left to chase */
    if (journeyEl) journeyEl.classList.toggle('journey--complete', allRewardsDone);
    /* the map keeps its ticks in every state, earned included */
    syncJourneyTicks(shippingDone, giftDone, gift2Done);

    /* message copy + state class so CSS can colour the bold amount:
       beaver toward free delivery, deep sage toward the gift. A quiet check
       marks each reward already earned — no colour flash, no celebration. */
    const giftBeforeShipping = shippingEligible && Number(CFG.thresholdGift) < Number(CFG.thresholdShipping);
    let nextAmount = remainingShipping;
    if (giftDone && !shippingDone) nextAmount = remainingShipping;
    else if (!giftDone && (!shippingEligible || giftBeforeShipping)) nextAmount = remainingGift;
    else if (shippingDone) nextAmount = remainingGift;
    const amt = formatThresholdAmount(nextAmount);
    const CK = '<span class="journey__ck" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7"/></svg></span> ';
    const amt2 = formatThresholdAmount(remainingGift2);
    const applyJourneyMsg = function() {
      journeyMsg.classList.remove('journey__msg--toward-shipping', 'journey__msg--toward-gift', 'journey__msg--gift-ready');
      /* Shipping and the first gift are earned and a second gift is still ahead.
         SAID ONCE (2026-09-25). With the first gift assured, the chooser's hint
         right under the chips already says it with the value ("Jeszcze 50 zł, a
         dodamy Bursztynowy Hojari, wartość 119 zł"), so the header confirms what
         is earned instead of repeating the ask. The header keeps the ask only
         when no chooser is open to carry it. */
      if (tier2On && !gift2Done && giftDone && (!shippingEligible || shippingDone) && giftAssured) {
        journeyMsg.classList.add('journey__msg--gift-ready');
        journeyMsg.innerHTML = CK + CFG.strings.giftIncluded;
      } else if (tier2On && !gift2Done && giftDone && (!shippingEligible || shippingDone)) {
        journeyMsg.classList.add('journey__msg--toward-gift');
        journeyMsg.innerHTML = '<strong>' + amt2 + '</strong> ' + CFG.strings.toGift2;
      } else if (allRewardsDone && giftAssured) {
        journeyMsg.classList.add('journey__msg--gift-ready');
        journeyMsg.innerHTML = CK + CFG.strings.giftIncluded;
      } else if (allRewardsDone) {
        journeyMsg.classList.add('journey__msg--gift-ready');
        journeyMsg.innerHTML = CK + CFG.strings.giftAwaits;
      } else if (giftDone && giftAssured) {
        journeyMsg.classList.add('journey__msg--toward-shipping');
        journeyMsg.innerHTML = CK + '<span class="journey__muted">' + CFG.strings.giftIncludedShort + '</span> <strong>' + amt + '</strong> ' + CFG.strings.toFreeShipping;
      } else if (giftDone) {
        journeyMsg.classList.add('journey__msg--gift-ready');
        journeyMsg.innerHTML = CK + CFG.strings.giftAwaits;
      } else if (!shippingEligible || giftBeforeShipping) {
        journeyMsg.classList.add('journey__msg--toward-gift');
        journeyMsg.innerHTML = '<strong>' + amt + '</strong> ' + CFG.strings.toGift;
      } else if (shippingDone) {
        journeyMsg.classList.add('journey__msg--toward-gift');
        journeyMsg.innerHTML = CK + '<span class="journey__muted">' + CFG.strings.shippingOnUs + '</span> <strong>' + amt + '</strong> ' + CFG.strings.toGift;
      } else {
        journeyMsg.classList.add('journey__msg--toward-shipping');
        journeyMsg.innerHTML = '<strong>' + amt + '</strong> ' + CFG.strings.toFreeShipping;
      }
    };
    /* any newer update supersedes a breath already in flight */
    clearTimeout(_journeyMsgTurnTimer);
    journeyMsg.classList.remove('is-turning');
    var wantsFade = journeyStateChanged && !_reducedMotion;
    if (wantsFade) {
      journeyMsg.classList.add('is-turning');
      _journeyMsgTurnTimer = setTimeout(function() {
        applyJourneyMsg();
        journeyMsg.classList.remove('is-turning');
      }, DUR_FEEDBACK);
    } else {
      applyJourneyMsg();
    }

    /* update shipping note */
    if (shippingNote) {
      shippingNote.textContent = (shippingEligible && shippingDone) ? CFG.strings.freeShippingNote : CFG.strings.shippingCalcNote;
    }

    /* gift chooser visibility */
    if (giftPick) {
      if (giftLabel) {
        giftLabel.textContent = giftAssured ? CFG.strings.yourGift : CFG.strings.chooseGift;
      }
      /* Default the chip before the line exists, so the chooser opens already
         decided — the same default the gift sync will add (desiredGiftVids). */
      if (giftAssured && state.selectedGift < 0 && CFG.giftVariants) {
        state.selectedGift = firstAvailableTier1Gift();
        if (state.selectedGift >= 0) syncGiftSelectionUi(state.selectedGift);
      }
      /* The chooser shows whichever tier the basket has earned, so a tier change
         has to re-run the chip sweep — the tiers replace each other in the same grid. */
      if (tier2On) syncGiftAvailabilityUi();
      else syncGiftTier2Hint();
      if (giftDone && (state.giftInCart || anyGiftAvailable() || (gift2Done && giftIsAvailable(GIFT2_INDEX)))) {
        giftPick.classList.add('is-open');
      } else {
        giftPick.classList.remove('is-open');
      }
    }
    /* The gift you are getting and the choice of which gift are the same
       subject. When both are on screen they are drawn as one block rather than
       two tinted boxes with two radii, two insets and a gap between them. */
    drawer.classList.toggle(
      'has-gift-line',
      !!(giftPick && giftPick.classList.contains('is-open'))
    );
  }

  /* ═══════════════════════════════════════
     A DISPLAYED CODE IS SOMETHING TO LIFT

     Every place the drawer prints a discount code, the customer's next move is
     often to use it somewhere else — a second order, a message to a friend. So
     the chip that prints it is a real button that copies it. The code stays
     visible as text either way; the copy is an extra, never a replacement, and
     the remove pill beside it is never touched.
  ═══════════════════════════════════════ */

  /** A discount title is only a code if it reads like one: one uppercase token,
      no spaces. "Summer Sale" is a label and gets no copy affordance. */
  function discountCodeOf(title) {
    const t = String(title == null ? '' : title).trim();
    if (t.length < 3 || t.length > 32) return '';
    if (t !== t.toUpperCase()) return '';
    if (!/^[A-Z0-9][A-Z0-9._-]*$/.test(t)) return '';
    return t;
  }

  function copiedLabel() {
    return localeText('Copied', 'Skopiowano', 'Zkopírováno', 'Kopiert', 'Gekopieerd');
  }

  function copyCodeAriaLabel(code) {
    return localeText(
      'Copy code ' + code,
      'Skopiuj kod ' + code,
      'Zkopírovat kód ' + code,
      'Code ' + code + ' kopieren',
      'Code ' + code + ' kopiëren'
    );
  }

  const COPY_GLYPH_HTML =
    '<svg class="code-copy__glyph" viewBox="0 0 14 14" aria-hidden="true">'
    + '<rect x="4.4" y="1.4" width="8.2" height="8.2" rx="1.6"/>'
    + '<path d="M9.6 12.6H2.9a1.5 1.5 0 01-1.5-1.5V4.4"/>'
    + '<polyline class="code-copy__tick" points="3 7.4 5.6 10 11 4.6"/>'
    + '</svg>';

  /** Wrap a code chip's inner content in a copy button, or leave it as-is. */
  function copyChipHtml(code, inner, className) {
    if (!code) return '<span class="' + className + '">' + inner + '</span>';
    return '<button type="button" class="' + className + ' code-copy"'
      + ' data-copy-code="' + esc(code) + '"'
      + ' data-copied-label="' + esc(copiedLabel()) + '"'
      + ' aria-label="' + esc(copyCodeAriaLabel(code)) + '">'
      + inner + COPY_GLYPH_HTML + '</button>';
  }

  /** execCommand fallback for the browsers (and insecure contexts) with no
      async clipboard. Off-screen, readonly, gone in the same tick. */
  function legacyCopyText(text) {
    try {
      const field = document.createElement('textarea');
      field.value = text;
      field.setAttribute('readonly', '');
      field.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
      document.body.appendChild(field);
      field.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(field);
      return ok;
    } catch (e) { return false; }
  }

  function copyDiscountCode(btn) {
    const code = btn.getAttribute('data-copy-code');
    if (!code) return;
    const confirm = function() {
      btn.classList.add('is-copied');
      announce(code + ' ' + copiedLabel().toLowerCase());
      if (btn._copyTimer) clearTimeout(btn._copyTimer);
      btn._copyTimer = setTimeout(function() {
        btn.classList.remove('is-copied');
        btn._copyTimer = null;
      }, 1500);
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(confirm, function() {
          if (legacyCopyText(code)) confirm();
        });
        return;
      }
    } catch (e) {}
    if (legacyCopyText(code)) confirm();
  }

  function renderDiscountBadges(item) {
    let html = '';
    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
      item.line_level_discount_allocations.forEach(function(alloc) {
        if (alloc && alloc.amount > 0 && alloc.discount_application && alloc.discount_application.title) {
          const title = alloc.discount_application.title;
          const inner = '<svg viewBox="0 0 12 12"><path d="M3.5 1.5h2.12l4.88 4.88-3.12 3.12L2.5 4.62V2.5a1 1 0 011-1z"/><circle cx="5" cy="4" r=".5"/></svg>'
            + esc(title) + ' (-' + formatMoney(alloc.amount) + ')';
          /* Only a real code copies. An AUTOMATIC line discount can carry an
             all-caps title that looks exactly like a code, and handing the
             customer a copy button for something no one can type is a lie.
             Same test the cart-level chip already applies. */
          const isCode = alloc.discount_application.type === 'discount_code';
          html += copyChipHtml(isCode ? discountCodeOf(title) : '', inner, 'item__discount');
        }
      });
    }
    return html;
  }

  function renderLineProperties(properties) {
    return Object.entries(properties || {}).reduce(function(html, entry) {
      const name = String(entry[0] || '');
      const value = entry[1] == null ? '' : String(entry[1]).trim();
      if (!name || name.charAt(0) === '_' || !value) return html;
      return html + '<span class="item__meta item__meta--property">'
        + esc(name) + ': ' + esc(value) + '</span>';
    }, '');
  }

  function renderItemPriceArea(item, isGift) {
    /* a preview whose price the page could not tell us draws no figure at
       all rather than a zero: its price arrives with Shopify's answer */
    if (!isGift && item.__preview && !(Number(item.final_line_price) > 0)) return '';
    if (isGift) {
      return '<span class="item__price item__price--gift">' + localeText('Free', 'Gratis', 'Zdarma', 'Gratis', 'Gratis') + '</span>';
    }

    const finalPrice = item.final_line_price;
    let html = '';
    if (item.original_line_price && item.original_line_price > finalPrice) {
      html += '<span class="item__price--was">' + formatMoney(item.original_line_price) + '</span>';
    }
    html += '<span class="item__price">' + formatMoney(finalPrice) + '</span>';
    html += renderDiscountBadges(item);
    html += '<button class="item__remove" data-action="remove" data-key="' + esc(item.key) + '" aria-label="' + esc(CFG.strings.remove + ' ' + item.product_title) + '">' + CFG.strings.remove + '</button>';
    return html;
  }


  /**
   * @param {Element} el the .qty__val span
   * @param {number} next the truthful quantity
   * @param {number} dir 1 rising, -1 descending, 0 no animation
   */
  function setQtyDisplay(el, next, dir) {
    if (!el) return;
    /* Every roll takes a token. A second tap landing mid-animation invalidates
       the first one's cleanup, so the older timer cannot strip the newer
       animation or leave the newer ghost stranded. */
    el._rollToken = (el._rollToken || 0) + 1;
    const token = el._rollToken;
    /* clear any ghost still in flight before reading or writing the value */
    const stale = el.querySelector('.qty__ghost');
    if (stale) stale.remove();
    const previous = el.textContent;
    /* The digit lives in an inner slot, never in .qty__val directly. The slot
       is the clipping box (the travel is ±55% of the line, which would
       otherwise paint outside the 22px column), and .qty__val keeps its own
       44px ::before/::after tap targets un-clipped. */
    let slot = el.querySelector('.qty__slot');
    if (!slot) {
      el.textContent = '';
      slot = document.createElement('span');
      slot.className = 'qty__slot';
      slot.innerHTML = '<span class="qty__num"></span>';
      el.appendChild(slot);
    }
    let num = slot.querySelector('.qty__num');
    if (!num) {
      slot.textContent = '';
      num = document.createElement('span');
      num.className = 'qty__num';
      slot.appendChild(num);
    }
    num.textContent = next;
    if (!dir || _reducedMotion || String(previous).trim() === String(next)) {
      slot.classList.remove('is-rolling-up', 'is-rolling-down');
      return;
    }
    /* The slot holds still and clips; the number travels in from ±55% while the
       aria-hidden ghost of the old value leaves the other way, and the two read
       as one continuous pass. aria-hidden keeps the reading to a single number. */
    const ghost = document.createElement('span');
    ghost.className = 'qty__ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.textContent = previous;
    slot.appendChild(ghost);
    slot.classList.remove('is-rolling-up', 'is-rolling-down');
    void slot.offsetWidth;
    slot.classList.add(dir > 0 ? 'is-rolling-up' : 'is-rolling-down');
    setTimeout(function() {
      /* a newer roll owns the slot now — leave its ghost and its classes alone */
      if (el._rollToken !== token) return;
      if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
      if (slot.isConnected) slot.classList.remove('is-rolling-up', 'is-rolling-down');
    }, 200);
  }

  /* ═══════════════════════════════════════
     RENDER CART ITEMS — keyed, in place

     A settle patches what changed and nothing else. Rows are found by the
     line's identity (variant, properties, plan, gift), so a preview row that
     becomes a server row, a key Shopify renamed, or a quantity that moved
     keeps its node, its decoded photograph and its focus. Wholesale rebuilds
     re-decoded every thumbnail on every settle ("it flashes for the other
     products as well", Sho, 2026-09-13) and put rows back on screen that the
     customer had just removed.
  ═══════════════════════════════════════ */
  const _justAdded = new Set();

  function cssKey(key) {
    return String(key == null ? '' : key).replace(/["\\]/g, '\\$&');
  }

  function unitRow(unit) {
    return unit && unit.classList && unit.classList.contains('item') ? unit : (unit ? unit.querySelector('.item') : null);
  }

  function buildUnit(item) {
    const tpl = document.createElement('template');
    tpl.innerHTML = cartItemRowHtml(item, false);
    return tpl.content.firstElementChild;
  }

  function undoPanel(item) {
    const panel = document.createElement('div');
    panel.className = 'item-undo';
    panel.style.setProperty('--ss-undo-ms', SOFT_REMOVE_MS + 'ms');
    const title = item.product_title || localeText('Item', 'Produkt', 'Položka', 'Artikel', 'Artikel');
    panel.innerHTML =
      '<span class="item-undo__txt">' + esc(removedFromCartText(title)) + '</span>'
      + '<button type="button" class="item-undo__btn">' + esc(undoLabel()) + '</button>'
      + '<span class="item-undo__bar" aria-hidden="true"><i></i></span>';
    panel.querySelector('.item-undo__btn').addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      undoSoftRemove();
    });
    return panel;
  }

  /* Bring one unit in line with its item: the parts whose markup differs are
     replaced; the photograph is kept whenever its source is the same; the
     quantity rolls in the direction a tap sent it. */
  function patchUnit(unit, item, fresh) {
    const row = unitRow(unit);
    const next = unitRow(fresh);
    if (!row || !next) return unit;
    /* a row that changed kind (paid ↔ gift) is a different row */
    if (unit.classList.contains('item-swipe') !== fresh.classList.contains('item-swipe')) {
      unit.replaceWith(fresh);
      return fresh;
    }
    ['data-key', 'data-variant-id', 'data-handle'].forEach(function(a) {
      if (row.getAttribute(a) !== next.getAttribute(a)) row.setAttribute(a, next.getAttribute(a));
    });
    ['item--gift', 'item--unavailable', 'is-unavailable'].forEach(function(c) {
      row.classList.toggle(c, next.classList.contains(c));
    });
    const img = row.querySelector('.item__img img');
    const nextImg = next.querySelector('.item__img img');
    if (!img || !nextImg || img.getAttribute('src') !== nextImg.getAttribute('src')) {
      const box = row.querySelector('.item__img');
      const nextBox = next.querySelector('.item__img');
      if (box && nextBox) box.replaceWith(nextBox);
    } else if (img.getAttribute('alt') !== nextImg.getAttribute('alt')) {
      img.setAttribute('alt', nextImg.getAttribute('alt'));
    }
    const info = row.querySelector('.item__top');
    const nextInfo = next.querySelector('.item__top');
    if (info && nextInfo && info.innerHTML !== nextInfo.innerHTML) info.innerHTML = nextInfo.innerHTML;
    const price = row.querySelector('.item__price-area');
    const nextPrice = next.querySelector('.item__price-area');
    if (price && nextPrice && price.innerHTML !== nextPrice.innerHTML) price.innerHTML = nextPrice.innerHTML;
    const qty = row.querySelector('.qty');
    const nextQty = next.querySelector('.qty');
    if (qty && nextQty) {
      const val = qty.querySelector('.qty__val');
      const shown = val ? parseInt(val.getAttribute('aria-valuenow'), 10) : NaN;
      if (val && !val.dataset.editing && shown !== Number(item.quantity)) {
        const dir = _rollHint && _rollHint.ident === identityKey(lineIdentity(item)) ? _rollHint.dir : 0;
        setQtyDisplay(val, item.quantity, dir);
        val.setAttribute('aria-valuenow', String(item.quantity));
      }
      const nextVal = nextQty.querySelector('.qty__val');
      if (val && nextVal) val.setAttribute('aria-valuemax', nextVal.getAttribute('aria-valuemax'));
      ['decrease', 'increase'].forEach(function(action) {
        const b = qty.querySelector('[data-action="' + action + '"]');
        const nb = nextQty.querySelector('[data-action="' + action + '"]');
        if (!b || !nb) return;
        b.className = nb.className;
        b.setAttribute('data-key', nb.getAttribute('data-key'));
        b.setAttribute('aria-label', nb.getAttribute('aria-label'));
        b.disabled = nb.disabled;
        if (nb.hasAttribute('aria-disabled')) b.setAttribute('aria-disabled', nb.getAttribute('aria-disabled'));
        else b.removeAttribute('aria-disabled');
      });
    } else if (!!qty !== !!nextQty) {
      const controls = row.querySelector('.item__controls');
      const nextControls = next.querySelector('.item__controls');
      if (controls && nextControls) controls.replaceWith(nextControls);
    }
    return unit;
  }

  /* The fold is drawn from state: a folded line keeps its row, collapsed to
     its undo strip; a row whose fold was undone opens again. */
  function syncFold(unit, item) {
    if (!unit.classList.contains('item-swipe')) return;
    const folded = !!item.__folded;
    if (folded && !unit.classList.contains('is-soft-removed')) {
      unit.classList.add('is-soft-removed');
      unit.appendChild(undoPanel(item));
    } else if (!folded && unit.classList.contains('is-soft-removed')) {
      unit.classList.remove('is-soft-removed');
      const panel = unit.querySelector('.item-undo');
      if (panel) panel.remove();
      unit.style.height = '';
      unit.style.transition = '';
    }
  }

  function renderCartItems(cart) {
    if (!itemsWrap) return;
    cart = cart || state.cart;
    if (!cart) return;
    _codeOnCart = cartHasCode(cart);
    /* a skeleton from the boot shell or a loading state gives way to rows */
    $$('.skeleton-item', itemsWrap).forEach(function(el) { el.remove(); });
    /* separate regular items from gift items — gifts render last. Shopify
       returns newest first; reversed, the newest sits at the foot of the list,
       directly above the rail, so an add never moves what is under a thumb */
    const regular = cart.items.filter(function(i) { return !isGiftItem(i); }).reverse();
    const gifts = cart.items.filter(function(i) { return isGiftItem(i); });
    const ordered = regular.concat(gifts);
    /* live units by identity; a unit left over is a line that has gone */
    const pool = new Map();
    Array.prototype.slice.call(itemsWrap.children).forEach(function(el) {
      if (el.classList.contains('item-sep') || el.classList.contains('is-removing')) return;
      const row = unitRow(el);
      if (!row) { el.remove(); return; }
      let ident = el._ssIdent;
      if (!ident) {
        /* a first-paint row from the Liquid: its key names a server line */
        const line = findByKey(cart, row.getAttribute('data-key')) || findByKey(_server, row.getAttribute('data-key'));
        ident = line ? identityKey(lineIdentity(line)) : 'unknown:' + row.getAttribute('data-key');
      }
      if (!pool.has(ident)) pool.set(ident, []);
      pool.get(ident).push(el);
    });
    const units = ordered.map(function(item) {
      const ident = identityKey(lineIdentity(item));
      const reuse = pool.get(ident);
      const fresh = buildUnit(item);
      let unit;
      if (reuse && reuse.length) {
        unit = patchUnit(reuse.shift(), item, fresh);
      } else {
        unit = fresh;
        const row = unitRow(unit);
        if (row && _justAdded.has(ident)) {
          row.classList.add('is-just-added');
          setTimeout(function() { if (row.isConnected) row.classList.remove('is-just-added'); }, DUR_TRANSITION);
        }
      }
      _justAdded.delete(ident);
      unit._ssIdent = ident;
      syncFold(unit, item);
      return unit;
    });
    /* lines that left: the row fades and the rows below close the gap */
    pool.forEach(function(list) {
      list.forEach(function(el) {
        const row = unitRow(el);
        const panel = el.querySelector && el.querySelector('.item-undo');
        if (panel) {
          panel.classList.add('is-committing');
          const undoControl = panel.querySelector('.item-undo__btn');
          if (undoControl) undoControl.remove();
        }
        fadeAndRemoveRow(row);
      });
    });
    /* order: move only what is out of place; separators between units */
    const live = Array.prototype.slice.call(itemsWrap.children).filter(function(el) {
      return !el.classList.contains('item-sep') && !el.classList.contains('is-removing');
    });
    const inPlace = live.length === units.length && live.every(function(el, i) { return el === units[i]; });
    if (!inPlace) {
      /* a node that is re-inserted restarts its CSS animations (a row still
         fading in from its add faded in again when a second add landed,
         cart-optimistic-proof k_two_adds), so only the out-of-place move */
      const next = function(node) {
        while (node && (node.nodeType !== 1 || node.classList.contains('item-sep') || node.classList.contains('is-removing'))) node = node.nextSibling;
        return node;
      };
      let cursor = next(itemsWrap.firstChild);
      units.forEach(function(unit) {
        if (cursor === unit) { cursor = next(unit.nextSibling); return; }
        itemsWrap.insertBefore(unit, cursor);
      });
    }
    let prevUnit = null;
    Array.prototype.slice.call(itemsWrap.children).forEach(function(el) {
      if (el.classList.contains('is-removing')) return;
      if (el.classList.contains('item-sep')) {
        const nextEl = el.nextElementSibling;
        if (!prevUnit || !nextEl || nextEl.classList.contains('item-sep')) el.remove();
        return;
      }
      if (prevUnit) {
        const before = el.previousElementSibling;
        if (!before || !before.classList.contains('item-sep')) {
          const sep = document.createElement('div');
          sep.className = 'item-sep';
          el.parentNode.insertBefore(sep, el);
        }
      }
      prevUnit = el;
    });
    /* a line the engine could not preview (no product data) waits as a skeleton */
    if (cart.__pendingUnknown > 0 && !ordered.length) renderLoadingState();
    /* The footer promise reads the cart's composition (all-pre-order or
       mixed); any change to it re-speaks the date. Memoized. */
    refreshDispatchIfPreorderChanged();
  }

  /* One canonical 180px thumbnail for every cart row, whatever the source URL
     carried: a `width=` query from a preview, or a `_300x` suffix from the
     upsell catalogue. The preview row and the settled row MUST resolve to the
     same bytes, or the settle re-fetches and the thumb flashes (2026-09-13:
     an upsell preview used to become `..._300x_180x.webp`). */
  function thumb180(url) {
    return String(url || '')
      /* One host for one photograph (2026-09-13). The row painted at the add
         carried `http://sacrasoul.com/cdn/shop/files/...`; the refresh a
         second later carried the same file as
         `https://cdn.shopify.com/s/files/1/.../files/...`. Different strings,
         so every settle re-fetched every thumbnail and the adoption below
         could not match: that was the whole-list blink Sho saw after an
         upsell add. Both spell the same bytes; the storefront's own /cdn/shop
         path is the first-party one (no second origin, no mixed content —
         WebKit logged the http:// form as insecure content on live). */
      .replace(/^(?:https?:)?\/\/cdn\.shopify\.com\/s\/files\/\d+\/\d+\/\d+\/\d+\/(files|products)\//, location.origin + '/cdn/shop/$1/')
      .replace(/^(?:https?:)?\/\/[^/]+\/cdn\/shop\//, location.origin + '/cdn/shop/')
      .replace(/([?&])width=\d+(&?)/, function(m, pre, post) { return post ? pre : ''; })
      .replace(/[?&]$/, '')
      .replace(/_\d+x(\.[a-z]+)(\?|$)/, '$1$2')
      .replace(/(\.[a-z]+)(\?|$)/, '_180x$1$2');
  }

  function cartItemRowHtml(item, withSep) {
      let html = '';
      const isGift = isGiftItem(item);
      const isUnavailable = item.__unavailable === true;
      if (withSep) html += '<div class="item-sep"></div>';
      if (!isGift) html += '<div class="item-swipe"><div class="item-swipe__action" aria-hidden="true">' + CFG.strings.remove + '</div>';
      html += '<div class="item' + (isGift ? ' item--gift' : '') + (isUnavailable ? ' item--unavailable is-unavailable' : '') + '" data-key="' + esc(item.key) + '" data-variant-id="' + item.variant_id + '" data-handle="' + esc(item.handle) + '">';

      /* image */
      const imgUrl = item.featured_image ? item.featured_image.url : (item.image || '');
      /* one canonical thumbnail URL for preview AND authoritative rows: strip
         any width= param the preview source carried before applying _180x,
         or the settle re-fetches different bytes and the thumb flashes */
      const imgSrc = imgUrl ? thumb180(imgUrl) : '';
      const imgAlt = item.featured_image ? (item.featured_image.alt || item.product_title) : item.product_title;
      html += '<div class="item__img">';
      /* eager: JS renders only happen while the drawer is open and the row
         is visible — lazy here just delays the thumbnail after re-renders */
      if (imgSrc) html += '<img src="' + esc(imgSrc) + '" alt="' + esc(imgAlt) + '" loading="eager" width="72" height="88">';
      html += '</div>';

      /* info */
      html += '<div class="item__info"><div class="item__top">';
      if (isGift) html += '<span class="item__gift-label">' + CFG.strings.complimentaryGift + '</span>';
      const isPreorder = lineIsPreorder(item);
      const dispatchNote = dispatchNoteForLine(item);
      html += '<div class="item__name-row">';
      html += '<a href="' + esc(item.url) + '" class="item__name">' + esc(item.product_title) + '</a>';
      if (isPreorder && !isGift && !dispatchNote) {
        html += '<span class="item__preorder"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg><span>' + (CFG.preorderShipDate ? localeText('Ships ' + CFG.preorderShipDate, 'Wysyłka ' + CFG.preorderShipDate, 'Odesíláme ' + CFG.preorderShipDate, 'Versand ' + CFG.preorderShipDate, 'Verzending ' + CFG.preorderShipDate) : CFG.strings.preorder) + '</span></span>';
      }
      html += '</div>';
      if (dispatchNote && !isGift) {
        html += '<span class="item__dispatch-note">' + esc(dispatchNote) + '</span>';
      }
      /* One description line, the same wrapper the Liquid first paint builds:
         "30 g · Grounds what frankincense lifts." (snippets/cart-item-line.liquid) */
      const itemLineText = isGift ? '' : (itemLine(item.handle) || (item.product_type ? upsellMeta(item.product_type) : ''));
      if (!isGift) {
        const hasSize = item.variant_title && item.variant_title !== 'Default Title';
        html += '<span class="item__desc">'
          + (hasSize ? '<span class="item__meta">' + esc(item.variant_title) + '</span>' : '')
          + (itemLineText ? '<span class="item__origin item__origin--line">' + esc(itemLineText) + '</span>' : '')
          + '</span>';
      }
      /* subscription line: name the cadence so it never looks like a duplicate one-time line */
      if (!isGift && item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        html += '<span class="item__meta item__meta--plan">' + esc(planDeliveryLabel(item.selling_plan_allocation.selling_plan)) + '</span>';
      }
      html += renderLineProperties(item.properties);
      /* bundle components — Shopify bundles render one parent line; surface what's inside */
      if (!isGift && BUNDLE_COMPONENTS[item.handle]) {
        const _bc = bundleComponentsFor(item.handle);
        html += '<span class="item__includes">' + esc(localeText(_bc.en, _bc.pl, _bc.cs || _bc.en, _bc.de || _bc.en, _bc.nl || _bc.en)) + '</span>';
      }
      html += setNoteHtml(item);
      /* unavailable / sold-out alert — surfaces inventory issues so checkout doesn't fail silently */
      if (isUnavailable && !isGift) {
        /* every locale word-for-word with the first paint in snippets/cart-drawer.liquid */
        const alertText = localeText('Sold out, please remove', 'Wyprzedane — usuń z koszyka', 'Vyprodáno, prosím odstraň', 'Ausverkauft, bitte entfernen', 'Uitverkocht, verwijder dit artikel');
        html += '<span class="item__sold-out" role="status">' + alertText + '</span>';
      } else if (!isGift) {
        const lowQty = lowStockCountFor(item);
        if (lowQty) {
          const lowText = localeText('Only ' + lowQty + ' left', 'Zostało tylko ' + lowQty + ' szt.', 'Skladem jen ' + lowQty + ' ks', 'Nur noch ' + lowQty + ' da', 'Nog maar ' + lowQty + ' over');
          html += '<span class="item__stock-low" role="status">' + esc(lowText) + '</span>';
        }
      }
      html += '</div>';

      /* controls */
      html += '<div class="item__controls">';
      if (!isGift) {
        const stockLimit = stockLimitFor(item);
        const atStockLimit = stockLimit != null && item.quantity >= stockLimit;
        html += '<div class="qty">';
        html += '<button class="qty__btn' + (item.quantity <= 1 ? ' qty__btn--at-min' : '') + '" data-action="decrease" data-key="' + esc(item.key) + '" aria-label="' + (item.quantity <= 1 ? CFG.strings.remove : CFG.strings.decreaseQty) + '">\u2212</button>';
        html += '<span class="qty__val" role="spinbutton" tabindex="0" aria-label="' + esc(CFG.strings.quantity) + '" aria-valuemin="0" aria-valuemax="' + (stockLimit != null ? Math.min(stockLimit, 99) : 99) + '" aria-valuenow="' + item.quantity + '">' + item.quantity + '</span>';
        html += '<button class="qty__btn" data-action="increase" data-key="' + esc(item.key) + '" aria-label="' + CFG.strings.increaseQty + '"' + ((isUnavailable || atStockLimit) ? ' disabled aria-disabled="true"' : '') + '>+</button>';
        html += '</div>';
      }
      html += '<div class="item__price-area">';
      html += renderItemPriceArea(item, isGift);
      html += '</div></div></div></div>';
      if (!isGift) html += '</div>'; /* close .item-swipe */
      return html;
  }

  /* ═══════════════════════════════════════
     DYNAMIC UPSELL ENGINE
  ═══════════════════════════════════════ */
  const FRANKINCENSE_HANDLES = ['amber-hojari', 'sultans-jade-frankincense', 'hojari-blend'];
  const RESIN_HANDLES = ['amber-hojari', 'sultans-jade-frankincense', 'hojari-blend', 'sacred-myrrh', 'dragon-blood'];
  const OIL_HANDLES = ['jade-oil', 'myrrh-essential-oil', 'rose-otto'];
  /* The burning tools sold on their own. A cart of only these has nothing to burn. */
  const ACCESSORY_HANDLES = ['terracotta-burner', 'quick-lighting-charcoal', 'tongs', 'mica-plates'];
  const BUNDLE_HANDLES = ['trinity', 'first-flame-bundle', 'first-flame', 'alchemist-bundle', 'alchemist', 'bundle-first-flame', 'bundle-trinity', 'bundle-alchemist', 'first-ritual', 'the-hearth', 'the-sanctum', 'king-queen'];
  /* Shopify-bundle components to surface under the parent line (cart.js gives has_components but not the parts) */
  const BUNDLE_COMPONENTS = {
    'king-queen': { en: 'Jade Frankincense Oil (6 ml) · Rose Otto (3 ml)', pl: 'Olejek z Zielonego Kadzidła (6 ml) · Róża Otto (3 ml)', cs: 'Olej ze zeleného kadidla (6 ml) · Růže otto (3 ml)', de: 'Jade-Weihrauchöl (6 ml) · Rose Otto (3 ml)', nl: 'Jade-wierookolie (6 ml) · Rose Otto (3 ml)' },
    'the-sanctum': { en: 'Jade Frankincense Oil (6 ml) · Inner Sanctum (3 ml)', pl: 'Olejek z Zielonego Kadzidła (6 ml) · Inner Sanctum (3 ml)', cs: 'Olej ze zeleného kadidla (6 ml) · Inner Sanctum (3 ml)', de: 'Jade-Weihrauchöl (6 ml) · Inner Sanctum (3 ml)', nl: 'Jade-wierookolie (6 ml) · Inner Sanctum (3 ml)' },
    'first-flame': { en: 'Hojari Blend (30 g) · Amber Hojari (30 g) · Terracotta Burner · Brass Tongs · Charcoal (2 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (30 g) · Bursztynowy Hojari (30 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (2 rolki) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (30 g) · Jantarový Hojari (30 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (2 role) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (30 g) · Bernstein-Weihrauch (30 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (2 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (30 g) · Amber Hojari (30 g) · Terracottabrander · Messingtang · Kooltjes (2 rollen) · Micaplaatjes (3-pack)' },
    'trinity': { en: 'Hojari Blend (60 g) · Amber Hojari (60 g) · Myrrh (60 g) · Terracotta Burner · Brass Tongs · Charcoal (3 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (60 g) · Bursztynowy Hojari (60 g) · Mirra (60 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (3 rolki) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (60 g) · Jantarový Hojari (60 g) · Myrha (60 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (3 role) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (60 g) · Bernstein-Weihrauch (60 g) · Myrrhe (60 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (3 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (60 g) · Amber Hojari (60 g) · Mirre (60 g) · Terracottabrander · Messingtang · Kooltjes (3 rollen) · Micaplaatjes (3-pack)' },
    'alchemist': { en: 'Hojari Blend (90 g) · Amber Hojari (90 g) · Myrrh (60 g) · Jade Frankincense Resin (50 g) · Dragon’s Blood (25 g) · Terracotta Burner · Brass Tongs · Charcoal (5 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (90 g) · Bursztynowy Hojari (90 g) · Mirra (60 g) · Żywica Zielonego Kadzidła Hojari (50 g) · Smocza Krew (25 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (5 rolek) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (90 g) · Jantarový Hojari (90 g) · Myrha (60 g) · Pryskyřice zeleného kadidla Hojari (50 g) · Dračí krev (25 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (5 rolí) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (90 g) · Bernstein-Weihrauch (90 g) · Myrrhe (60 g) · Jade-Weihrauchharz (50 g) · Drachenblut (25 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (5 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (90 g) · Amber Hojari (90 g) · Mirre (60 g) · Jade-wierookhars (50 g) · Drakenbloed van Socotra (25 g) · Terracottabrander · Messingtang · Kooltjes (5 rollen) · Micaplaatjes (3-pack)' },
    'first-ritual': { en: 'Jade Frankincense Oil (6 ml) · First Flame set', pl: 'Olejek z Zielonego Kadzidła (6 ml) · Zestaw Pierwszy Płomień', cs: 'Olej ze zeleného kadidla (6 ml) · Sada První plamen', de: 'Jade-Weihrauchöl (6 ml) · Set Erste Flamme', nl: 'Jade-wierookolie (6 ml) · First Flame-set' },
    'the-hearth': { en: 'Hojari Blend (90 g) · Amber Hojari (60 g) · Burner · Tongs · Charcoal (3 rolls)', pl: 'Hojari Blend (90 g) · Bursztynowy Hojari (60 g) · Kadzielnica · Szczypce · Węgielki (3 rolki)', cs: 'Hojari Blend (90 g) · Jantarový Hojari (60 g) · Kadidelnice · Kleště · Uhlíky (3 role)', de: 'Hojari-Mischung (90 g) · Bernstein-Weihrauch (60 g) · Räuchergefäß · Zange · Kohletabletten (3 Rollen)', nl: 'Hojari Blend (90 g) · Amber Hojari (60 g) · Brander · Tang · Kooltjes (3 rollen)' },
    'first-flame--hojari-swap': { en: 'Amber Hojari (60 g) · Terracotta Burner · Brass Tongs · Charcoal (2 rolls) · Mica plates (3-pack)', pl: 'Bursztynowy Hojari (60 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (2 rolki) · Płytki mikowe (3 szt.)', cs: 'Jantarový Hojari (60 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (2 role) · Slídové destičky (3 ks)', de: 'Bernstein-Weihrauch (60 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (2 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Amber-wierook (60 g) · Terracottabrander · Messingtang · Kooltjes (2 rollen) · Micaplaatjes (3-pack)' },
    'trinity--hojari-swap': { en: 'Amber Hojari (120 g) · Myrrh (60 g) · Terracotta Burner · Brass Tongs · Charcoal (3 rolls) · Mica plates (3-pack)', pl: 'Bursztynowy Hojari (120 g) · Mirra (60 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (3 rolki) · Płytki mikowe (3 szt.)', cs: 'Jantarový Hojari (120 g) · Myrha (60 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (3 role) · Slídové destičky (3 ks)', de: 'Bernstein-Weihrauch (120 g) · Myrrhe (60 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (3 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Amber-wierook (120 g) · Mirre (60 g) · Terracottabrander · Messingtang · Kooltjes (3 rollen) · Micaplaatjes (3-pack)' },
    'alchemist--hojari-swap': { en: 'Amber Hojari (180 g) · Myrrh (60 g) · Jade Frankincense Resin (50 g) · Dragon’s Blood (25 g) · Terracotta Burner · Brass Tongs · Charcoal (5 rolls) · Mica plates (3-pack)', pl: 'Bursztynowy Hojari (180 g) · Mirra (60 g) · Żywica Zielonego Kadzidła Hojari (50 g) · Smocza Krew (25 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (5 rolek) · Płytki mikowe (3 szt.)', cs: 'Jantarový Hojari (180 g) · Myrha (60 g) · Pryskyřice zeleného kadidla Hojari (50 g) · Dračí krev (25 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (5 rolí) · Slídové destičky (3 ks)', de: 'Bernstein-Weihrauch (180 g) · Myrrhe (60 g) · Jade-Weihrauchharz (50 g) · Drachenblut (25 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (5 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Amber-wierook (180 g) · Mirre (60 g) · Jade-wierookhars (50 g) · Drakenbloed van Socotra (25 g) · Terracottabrander · Messingtang · Kooltjes (5 rollen) · Micaplaatjes (3-pack)' },
    'first-flame--amber-swap': { en: 'Hojari Blend (60 g) · Terracotta Burner · Brass Tongs · Charcoal (2 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (60 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (2 rolki) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (60 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (2 role) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (60 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (2 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (60 g) · Terracottabrander · Messingtang · Kooltjes (2 rollen) · Micaplaatjes (3-pack)' },
    'trinity--amber-swap': { en: 'Hojari Blend (120 g) · Myrrh (60 g) · Terracotta Burner · Brass Tongs · Charcoal (3 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (120 g) · Mirra (60 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (3 rolki) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (120 g) · Myrha (60 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (3 role) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (120 g) · Myrrhe (60 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (3 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (120 g) · Mirre (60 g) · Terracottabrander · Messingtang · Kooltjes (3 rollen) · Micaplaatjes (3-pack)' },
    'alchemist--amber-swap': { en: 'Hojari Blend (180 g) · Myrrh (60 g) · Jade Frankincense Resin (50 g) · Dragon’s Blood (25 g) · Terracotta Burner · Brass Tongs · Charcoal (5 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (180 g) · Mirra (60 g) · Żywica Zielonego Kadzidła Hojari (50 g) · Smocza Krew (25 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (5 rolek) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (180 g) · Myrha (60 g) · Pryskyřice zeleného kadidla Hojari (50 g) · Dračí krev (25 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (5 rolí) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (180 g) · Myrrhe (60 g) · Jade-Weihrauchharz (50 g) · Drachenblut (25 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (5 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (180 g) · Mirre (60 g) · Jade-wierookhars (50 g) · Drakenbloed van Socotra (25 g) · Terracottabrander · Messingtang · Kooltjes (5 rollen) · Micaplaatjes (3-pack)' },
    'first-flame--amber-ladder': { en: 'Hojari Blend (30 g) · Myrrh (30 g) · Terracotta Burner · Brass Tongs · Charcoal (2 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (30 g) · Mirra (30 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (2 rolki) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (30 g) · Myrha (30 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (2 role) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (30 g) · Myrrhe (30 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (2 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (30 g) · Mirre (30 g) · Terracottabrander · Messingtang · Kooltjes (2 rollen) · Micaplaatjes (3-pack)' },
    'trinity--amber-ladder': { en: 'Hojari Blend (90 g) · Jade Frankincense Resin (25 g) · Myrrh (60 g) · Terracotta Burner · Brass Tongs · Charcoal (3 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (90 g) · Żywica Zielonego Kadzidła Hojari (25 g) · Mirra (60 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (3 rolki) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (90 g) · Pryskyřice zeleného kadidla Hojari (25 g) · Myrha (60 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (3 role) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (90 g) · Jade-Weihrauchharz (25 g) · Myrrhe (60 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (3 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (90 g) · Jade-wierookhars (25 g) · Mirre (60 g) · Terracottabrander · Messingtang · Kooltjes (3 rollen) · Micaplaatjes (3-pack)' },
    'alchemist--amber-ladder': { en: 'Hojari Blend (120 g) · Amber Hojari (60 g) · Myrrh (60 g) · Jade Frankincense Resin (50 g) · Dragon’s Blood (25 g) · Terracotta Burner · Brass Tongs · Charcoal (5 rolls) · Mica plates (3-pack)', pl: 'Hojari Blend (120 g) · Bursztynowy Hojari (60 g) · Mirra (60 g) · Żywica Zielonego Kadzidła Hojari (50 g) · Smocza Krew (25 g) · Terakotowa kadzielnica · Mosiężne szczypce · Węgielki (5 rolek) · Płytki mikowe (3 szt.)', cs: 'Hojari Blend (120 g) · Jantarový Hojari (60 g) · Myrha (60 g) · Pryskyřice zeleného kadidla Hojari (50 g) · Dračí krev (25 g) · Terakotová kadidelnice · Mosazné kleště · Uhlíky (5 rolí) · Slídové destičky (3 ks)', de: 'Hojari-Mischung (120 g) · Bernstein-Weihrauch (60 g) · Myrrhe (60 g) · Jade-Weihrauchharz (50 g) · Drachenblut (25 g) · Terrakotta-Räuchergefäß · Messingzange · Kohletabletten (5 Rollen) · Glimmerplättchen (3er-Pack)', nl: 'Hojari Blend (120 g) · Amber Hojari (60 g) · Mirre (60 g) · Jade-wierookhars (50 g) · Drakenbloed van Socotra (25 g) · Terracottabrander · Messingtang · Kooltjes (5 rollen) · Micaplaatjes (3-pack)' }
  };
  /* HOJARI BLEND OUT. The three '--hojari-swap' rows above are the kits as packed
     while settings.hojari_blend_swapped is on, key for key with the Liquid first
     paint in snippets/cart-drawer.liquid. The drawer's render says so through
     CFG.hojariSwapped; a config without it (an older cached page) or the switch
     off keeps the recipe rows. No comment goes inside the object literal: the
     Liquid/JS drift test in test_storefront_performance_contracts.py reads it
     entry by entry and a comment between entries hides the next one from it. */
  /* AMBER HOJARI OUT is the mirror: the '--amber-swap' rows (doubled Hojari
     Blend, no Amber) while CFG.amberSwapped is on. The Hojari Blend switch wins
     if both are on, as in the Liquid. tasks/amber-swap-guide-2026-10.md. */
  /* AMBER LADDER: CFG.amberSwapped is the string 'ladder' (settings.amber_swap_mode) when
     the Amber switch is on in ladder mode: Myrrh in First Flame, Jade in the Three Kings,
     Amber only in the Alchemist ('--amber-ladder' rows). The set cards need no override:
     each kit keeps its resin count. */
  function kitSwapSuffix() {
    if (CFG.hojariSwapped === true) return '--hojari-swap';
    if (CFG.amberSwapped === true) return '--amber-swap';
    if (CFG.amberSwapped === 'ladder') return '--amber-ladder';
    return '';
  }
  function bundleComponentsFor(handle) {
    const suffix = kitSwapSuffix();
    const swapped = suffix && BUNDLE_COMPONENTS[handle + suffix];
    return swapped || BUNDLE_COMPONENTS[handle];
  }
  const BUNDLE_META = {
    'trinity': {
      name: { en: 'The Three Kings', pl: 'Trzej Królowie', cs: 'Tři králové', de: 'Die Drei Könige', nl: 'De Drie Koningen' },
      line: { en: 'Three resins with burner, tongs, charcoal and mica plates.', pl: 'Trzy żywice z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Tři pryskyřice s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Drei Harze mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Drie harsen met brander, tang, kooltjes en micaplaatjes.' },
      count: 7
    },
    'first-flame': {
      name: { en: 'First Flame', pl: 'Pierwszy Płomień', cs: 'První plamen', de: 'Erste Flamme', nl: 'First Flame' },
      line: { en: 'Two resins with burner, tongs, charcoal and mica plates.', pl: 'Dwie żywice z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Dvě pryskyřice s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Zwei Harze mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Twee harsen met brander, tang, kooltjes en micaplaatjes.' },
      count: 6
    },
    'first-ritual': {
      name: { en: 'First Ritual', pl: 'Pierwszy Rytuał', cs: 'První rituál', de: 'Erstes Ritual', nl: 'Eerste ritueel' },
      line: { en: 'The oil for the skin, the fire for the home. The complete first evening.', pl: 'Olejek na skórę, ogień na dom. Kompletny pierwszy wieczór.', cs: 'Olej na kůži, oheň pro domov. Celý první večer.', de: 'Das Öl für die Haut, das Feuer für zu Hause. Der ganze erste Abend.', nl: 'De olie voor de huid, het vuur voor thuis. De complete eerste avond.' },
      count: 7
    },
    'the-sanctum': {
      name: { en: 'The Sanctum', pl: 'The Sanctum', cs: 'The Sanctum', de: 'The Sanctum', nl: 'The Sanctum' },
      line: { en: 'Jade Frankincense Oil and the Inner Sanctum attar, side by side.', pl: 'Olejek z Zielonego Kadzidła i attar Inner Sanctum, obok siebie.', cs: 'Olej ze zeleného kadidla a attar Inner Sanctum, bok po boku.', de: 'Jade-Weihrauchöl und das Inner Sanctum Attar, nebeneinander.', nl: 'Jade-wierookolie en de Inner Sanctum-attar, naast elkaar.' },
      count: 2
    },
    'king-queen': {
      name: { en: 'King & Queen', pl: 'Król i Królowa', cs: 'Král a královna', de: 'König & Königin', nl: 'Koning & Koningin' },
      line: { en: 'Two crowns. One pulse.', pl: 'Dwie korony. Jeden puls.', cs: 'Dvě koruny. Jeden puls.', de: 'Zwei Kronen. Ein Puls.', nl: 'Twee kronen. Eén polsslag.' },
      count: 2
    },
    'alchemist': {
      name: { en: 'Alchemist', pl: 'Alchemik', cs: 'Alchymista', de: 'Der Alchemist', nl: 'De Alchemist' },
      line: { en: 'Five resins with burner, tongs, charcoal and mica plates.', pl: 'Pięć żywic z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Pět pryskyřic s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Fünf Harze mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Vijf harsen met brander, tang, kooltjes en micaplaatjes.' },
      count: 9
    }
  };
  /* HOJARI BLEND OUT: the set cards' line and piece count for the kits as
     packed while CFG.hojariSwapped is on (no Blend, the Amber doubled). Same
     switch and same removal pass as the '--hojari-swap' component rows. */
  const BUNDLE_META_SWAP = {
    'trinity': { count: 6, line: { en: 'Two resins with burner, tongs, charcoal and mica plates.', pl: 'Dwie żywice z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Dvě pryskyřice s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Zwei Harze mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Twee harsen met brander, tang, kooltjes en micaplaatjes.' } },
    'first-flame': { count: 5, line: { en: 'Amber Hojari with burner, tongs, charcoal and mica plates.', pl: 'Bursztynowy Hojari z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Jantarový Hojari s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Bernstein-Weihrauch mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Amber-wierook met brander, tang, kooltjes en micaplaatjes.' } },
    'alchemist': { count: 8, line: { en: 'Four resins with burner, tongs, charcoal and mica plates.', pl: 'Cztery żywice z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Čtyři pryskyřice s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Vier Harze mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Vier harsen met brander, tang, kooltjes en micaplaatjes.' } }
  };
  /* AMBER HOJARI OUT: the same, for the mirror (no Amber, Hojari Blend doubled)
     while CFG.amberSwapped is on and CFG.hojariSwapped is not. */
  const BUNDLE_META_AMBER_SWAP = {
    'trinity': { count: 6, line: { en: 'Two resins with burner, tongs, charcoal and mica plates.', pl: 'Dwie żywice z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Dvě pryskyřice s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Zwei Harze mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Twee harsen met brander, tang, kooltjes en micaplaatjes.' } },
    'first-flame': { count: 5, line: { en: 'Hojari Blend with burner, tongs, charcoal and mica plates.', pl: 'Hojari Blend z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Hojari Blend s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Hojari-Mischung mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Hojari Blend met brander, tang, kooltjes en micaplaatjes.' } },
    'alchemist': { count: 8, line: { en: 'Four resins with burner, tongs, charcoal and mica plates.', pl: 'Cztery żywice z kadzielnicą, szczypcami, węgielkami i płytkami mikowymi.', cs: 'Čtyři pryskyřice s kadidelnicí, kleštěmi, uhlíky a slídovými destičkami.', de: 'Vier Harze mit Räuchergefäß, Zange, Kohle und Glimmerplättchen.', nl: 'Vier harsen met brander, tang, kooltjes en micaplaatjes.' } }
  };
  /* What each set actually holds, as catalogue handles. A Shopify bundle
     reaches cart.js as ONE parent line, so a King & Queen cart holds
     'king-queen' and nothing else; the engine, excluding by direct handle only,
     offered the Jade Oil and Rose Otto that were already inside the box
     (PostHog replay, 2026-09-06). This map is the eligibility truth; the
     localized BUNDLE_COMPONENTS strings above stay display text. Verified
     against the live product pages on 2026-09-07 (First Ritual and The Hearth
     are unpublished; their rows keep the code honest if they go live). Tongs
     and mica are never recommended, so they are not listed. Nested sets are
     flattened: First Ritual holds First Flame, so it holds its resins too. */
  const BUNDLE_CONTENTS = {
    'king-queen':   ['jade-oil', 'rose-otto'],
    'the-sanctum':  ['jade-oil', 'inner-sanctum-attar'],
    'first-flame':  ['hojari-blend', 'amber-hojari', 'terracotta-burner', 'quick-lighting-charcoal'],
    'trinity':      ['hojari-blend', 'amber-hojari', 'sacred-myrrh', 'terracotta-burner', 'quick-lighting-charcoal'],
    'alchemist':    ['hojari-blend', 'amber-hojari', 'sacred-myrrh', 'sultans-jade-frankincense', 'dragon-blood', 'terracotta-burner', 'quick-lighting-charcoal'],
    'first-ritual': ['jade-oil', 'first-flame', 'hojari-blend', 'amber-hojari', 'terracotta-burner', 'quick-lighting-charcoal'],
    'the-hearth':   ['hojari-blend', 'amber-hojari', 'terracotta-burner', 'quick-lighting-charcoal']
  };
  /* BUNDLE_HANDLES carries legacy aliases; they resolve to the canonical set. */
  const BUNDLE_ALIASES = {
    'first-flame-bundle': 'first-flame', 'bundle-first-flame': 'first-flame',
    'bundle-trinity': 'trinity',
    'alchemist-bundle': 'alchemist', 'bundle-alchemist': 'alchemist'
  };
  /* AMBER LADDER adds what the ladder kits newly hold, so the engine never offers Myrrh to
     a First Flame basket or Jade to a Three Kings one. Amber stays listed: it is scarce, and
     offering it beside those kits would only sell the bags the Alchemist needs. */
  const BUNDLE_CONTENTS_LADDER = {
    'first-flame': ['sacred-myrrh'],
    'trinity': ['sultans-jade-frankincense'],
    'first-ritual': ['sacred-myrrh']
  };
  function bundleContentsOf(handle) {
    const key = BUNDLE_ALIASES[handle] || handle;
    const base = BUNDLE_CONTENTS[key] || null;
    if (base && CFG.hojariSwapped !== true && CFG.amberSwapped === 'ladder' && BUNDLE_CONTENTS_LADDER[key]) {
      return base.concat(BUNDLE_CONTENTS_LADDER[key]);
    }
    return base;
  }

  /* SETS AND CODES. Every code (SACRA10, SACRA15, WELCOME10...) is scoped in
     Shopify to the "Code-eligible (everything except bundles)" collection: a
     set is already priced below its pieces, so a code stops at the sets. The
     drawer says so wherever that shows. Order #2430 (2026-10-09): a 1,505 zl
     basket, 1,205 zl of it two sets, and SACRA10 took 30 zl with no word why. */
  function isSetLine(item) {
    return !!item && !isGiftItem(item) && BUNDLE_HANDLES.indexOf(item.handle) >= 0;
  }
  function cartIsOnlySets(cart) {
    const paid = ((cart && cart.items) || []).filter(function(i) { return !isGiftItem(i); });
    return paid.length > 0 && paid.every(isSetLine);
  }
  function cartHasCode(cart) {
    if (!cart) return false;
    if ((cart.discount_codes || []).some(function(d) { return d && d.code && d.applicable !== false; })) return true;
    if ((cart.cart_level_discount_applications || []).some(function(d) { return d && d.type === 'discount_code'; })) return true;
    return (cart.items || []).some(function(item) {
      return (item.line_level_discount_allocations || []).some(function(a) {
        return a && Number(a.amount) > 0 && a.discount_application && a.discount_application.type === 'discount_code';
      });
    });
  }
  /* Set by renderCartItems for the rows it draws: a set carries its note only
     while a code is on the cart. Word for word with snippets/cart-drawer.liquid. */
  let _codeOnCart = false;
  function setNoteHtml(item) {
    if (!_codeOnCart || !isSetLine(item)) return '';
    return '<span class="item__set-note">' + esc(localeText('Already discounted as a set', 'Już przeceniony jako zestaw', 'Už zlevněno jako sada', 'Als Set bereits reduziert', 'Als set al afgeprijsd')) + '</span>';
  }
  function lineContainsPreorderBurner(item) {
    if (!item || isGiftItem(item)) return false;
    const handle = BUNDLE_ALIASES[item.handle] || item.handle;
    if (handle === 'terracotta-burner') return lineIsPreorder(item);
    const contents = bundleContentsOf(handle);
    return !!(contents
      && contents.indexOf('terracotta-burner') >= 0
      && ((CFG.preorderHandles && CFG.preorderHandles.indexOf('terracotta-burner') >= 0)
        || _liquidDispatchHandles.has(item.handle)));
  }
  function dispatchNoteForLine(item) {
    if (!lineContainsPreorderBurner(item)) return '';
    if (!CFG.preorderShipDate) return CFG.strings.dispatchBackorder;
    return String(CFG.strings.dispatchFrom || '').replace('{date}', CFG.preorderShipDate);
  }

  function onPreorderCatalogReady() {
    _liquidDispatchHandles.clear();
    if (!state.cart) return;
    /* the rows re-derive their dispatch notes in place */
    paint();
  }
  listen(document, 'sacrasoul:preorder-catalog-ready', onPreorderCatalogReady);
  /* Every product the cart already holds, on its own line or inside a set. */
  function coveredHandles(cartHandles) {
    const covered = new Set(cartHandles);
    cartHandles.forEach(function(h) {
      const contents = bundleContentsOf(h);
      if (contents) contents.forEach(function(c) { covered.add(c); });
    });
    return covered;
  }

  /* first-ritual is not published, so it stays out of recommendations until
     its product becomes live. The Sanctum is live but intentionally not an
     automatic upsell; its cart component truth is handled above. */
  const ALL_HANDLES = ['quick-lighting-charcoal', 'amber-hojari', 'sultans-jade-frankincense', 'hojari-blend', 'sacred-myrrh', 'dragon-blood', 'jade-oil', 'myrrh-essential-oil', 'rose-otto', 'inner-sanctum-attar', 'first-flame', 'terracotta-burner', 'trinity', 'king-queen'];
  /* The K&Q set card's static BUNDLE_META.line can't flex by cart, so the
     pairing nudge carries a directional line chosen by which half is already
     in cart. king = the King (jade-oil) is present, the Queen is missing;
     queen = the Queen (rose-otto) is present, the King is missing. */
  const KQ_COMPLETION_LINE = {
    king:  { en: 'The Queen completes the King.', pl: 'Królowa dopełnia Króla.', cs: 'Královna doplňuje Krále.', de: 'Die Königin vollendet den König.', nl: 'De Koningin maakt de Koning compleet.' },
    queen: { en: 'The King completes the Queen.', pl: 'Król dopełnia Królową.', cs: 'Král doplňuje Královnu.', de: 'Der König vollendet die Königin.', nl: 'De Koning maakt de Koningin compleet.' }
  };

  /* Hybrid catalog: Liquid-rendered first, AJAX fallback for missing products */
  let _upsellProducts = {};
  let _catalogReady = false;
  let _catalogPromise = null;
  let _catalogBootAwaited = false;
  const UPSELL_UNAVAILABLE_STORAGE_NAME = 'sacrasoul_upsell_unavailable_v1';
  const _unavailableUpsellHandles = (function() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(UPSELL_UNAVAILABLE_STORAGE_NAME) || '[]');
      return new Set(Array.isArray(stored) ? stored.filter(function(handle) { return typeof handle === 'string' && handle; }) : []);
    } catch (e) { return new Set(); }
  })();

  function markUpsellUnavailable(handle) {
    if (!handle) return;
    _unavailableUpsellHandles.add(handle);
    try { sessionStorage.setItem(UPSELL_UNAVAILABLE_STORAGE_NAME, JSON.stringify(Array.from(_unavailableUpsellHandles))); }
    catch (e) { /* the in-memory verdict still holds when storage is unavailable */ }
  }

  function readCatalogNode() {
    try {
      const el = document.getElementById('sacrasoul-upsell-catalog');
      if (el && el.textContent.trim() !== '{}' && el.textContent.trim() !== '{  }') {
        _upsellProducts = JSON.parse(el.textContent);
      }
    } catch (e) {
      console.warn('[SacrasoulCart] Liquid catalog parse failed, using AJAX only');
    }
  }

  function initCatalog() {
    if (_catalogPromise) return _catalogPromise;

    /* Step 1: the catalog node. Since #1934 the page ships it empty and boot
       fills it from /?sections=cart-upsell-data on cart intent, in parallel
       with this engine's own download. When the engine lands first the node
       is still `{}`, and reading it once here sent fourteen /products/x.js
       requests on a phone for a payload that arrived a beat later. Wait for
       boot's fetch (it is bounded to 2 s on the boot side) before deciding
       anything is missing. */
    readCatalogNode();
    const bootCatalog = window.__ssUpsellCatalogP;
    /* Once. Boot's promise settles even when its fetch failed or the section
       came back without a product, and a second wait on an already-settled
       promise would re-enter here in a microtask cycle with nothing left to
       wait for (review, 2026-09-12). After the one wait, whatever the node
       holds is the answer and the AJAX fallback below fills the rest. */
    if (!Object.keys(_upsellProducts).length && !_catalogBootAwaited && bootCatalog && typeof bootCatalog.then === 'function') {
      _catalogBootAwaited = true;
      _catalogPromise = Promise.resolve(bootCatalog).catch(function() {}).then(function() {
        _catalogPromise = null;
        return initCatalog();
      });
      return _catalogPromise;
    }

    /* Step 2: AJAX fallback for any missing products */
    const missing = ALL_HANDLES.filter(h => !_upsellProducts[h]);
    if (missing.length === 0) {
      _catalogReady = true;
      _catalogPromise = Promise.resolve(_upsellProducts);
      return _catalogPromise;
    }

    _catalogPromise = Promise.allSettled(
      missing.map(function(h) { return fetchProductByHandle(h); })
    ).then(results => {
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) {
          const p = r.value;
          const v = p.variants && p.variants.find(function(vr) { return vr.available; });
          if (v) {
            _upsellProducts[missing[i]] = {
              handle: p.handle, title: p.title, type: p.type || '',
              vid: v.id, price: v.price,
              compareAt: v.compare_at_price || 0,
              componentValue: 0,
              img: p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_300x$1$2') : '',
              url: p.url
            };
          }
        } else {
          console.warn('[SacrasoulCart] Failed to load:', missing[i], r.reason || r.status);
        }
      });
      _catalogReady = true;
      return _upsellProducts;
    });
    return _catalogPromise;
  }

  function loadUpsellCatalog() {
    if (_catalogReady) return Promise.resolve(_upsellProducts);
    return _catalogPromise || initCatalog();
  }

  function scheduleUpsellLoad() {
    if (_catalogReady || _catalogPromise) return;
    /* catalog data is already in the HTML (Liquid-rendered) — init immediately */
    initCatalog().then(() => {
      if (drawer.classList.contains('is-open') && state.cart) renderUpsells(state.cart);
    }).catch(() => {});
  }

  function getUpsellSignature(cart) {
    if (!cart || !cart.item_count) return 'empty';
    const handles = Array.from(new Set(
      (cart.items || [])
        .filter(function(item) { return !isGiftItem(item) && !item.__folded; })
        .map(function(item) { return item.handle; })
        .filter(Boolean)
    )).sort();
    /* The threshold-helper tag and heading depend on the cart AMOUNT, not just
       which products are in it. Fold the threshold decision state into the
       signature so a quantity or discount change that crosses (or approaches)
       free shipping / the gift re-renders the tags — otherwise "Unlocks free
       shipping" can stay on screen after shipping is already earned. Amounts
       only enter the signature inside the near-goal window (rounded to whole
       units), so idle browsing still never churns the carousel. */
    var amount = regularTotalCents(cart) / 100;
    var shipTh = Number(CFG.thresholdShipping) || 0;
    var giftTh = Number(CFG.thresholdGift) || 0;
    var tier = (shipTh > 0 && amount >= shipTh ? 's' : '') + (giftTh > 0 && amount >= giftTh ? 'g' : '');
    var near = '';
    if (shipTh > 0 && amount < shipTh && (shipTh - amount) <= shipTh * 0.35) near += 'ns' + Math.ceil(shipTh - amount);
    if (giftTh > 0 && amount < giftTh && (giftTh - amount) <= giftTh * 0.25) near += 'ng' + Math.ceil(giftTh - amount);
    /* The free-delivery nudge picks and prices its row from the whole gap, not
       only near the goal, so every sub-threshold amount is its own state. */
    if (shipTh > 0 && amount < shipTh && !(giftTh > 0 && giftTh < shipTh && amount < giftTh)) near += 'fd' + Math.round((shipTh - amount) * 100);
    return (handles.join('|') || 'empty') + '#' + tier + '#' + near;
  }

  function scheduleUpsellRender(cart, delay) {
    clearTimeout(_upsellRenderTimer);
    const snapshot = cloneCart(cart);
    if (delay === 0) {
      if (drawer.classList.contains('is-open') && snapshot) renderUpsells(snapshot).catch(function() {});
      else _upsellSignature = ''; /* render skipped — invalidate so reopen re-renders */
      return;
    }
    _upsellRenderTimer = setTimeout(function() {
      _upsellRenderTimer = null;
      if (!drawer.classList.contains('is-open') || !snapshot) {
        /* the signature was already committed for this render; dropping the
           render (drawer closed mid-hold) must not leave a stale carousel */
        _upsellSignature = '';
        return;
      }
      renderUpsells(snapshot).catch(function() {});
    }, delay || 16);
  }

  /* ── Context-aware upsell engine ── (rules as of 2026-09-23)
     Never offer what the cart owns (loose or inside a set) or what is sold out.
     1. Near a reward (free delivery / gift): the cheapest item that closes the
        gap leads, tagged with the reward. A resin set with nothing in that band
        is bridged by the cheapest loose resin it lacks (First Flame → Myrrh,
        Three Kings → Dragon's Blood).
     2. Resin set (First Flame, Three Kings, Alchemist): then Rose Otto and the
        other oils, dearest first, the attar, then the resins the set leaves out.
     3. Oil set (King & Queen, The Sanctum): the oils it lacks, the attar, then
        First Flame, the fire. No loose resin: they hold no burner.
     4. Loose oil or attar: its pairing (Jade → attar, Myrrh Oil), the other
        oils, First Flame. Loose resin: the Three Kings card, charcoal, oils.
     5. Tools only (burner, coal, tongs, mica): charcoal if missing, then resins.
     Shows max 4 rows; a 390x750 phone sees the first two. */
  function getUpsellOrder(cartHandles) {
    if (!_upsellProducts || !Object.keys(_upsellProducts).length) return [];
    const result = [];
    /* Two questions, two answers. `has` reads the lines the customer chose and
       classifies the cart (an oil cart, a set cart); `owns` reads what those
       lines contain and decides what we may still offer. A King & Queen cart
       is a set cart, not an oil cart, but it owns both oils. */
    const covered = coveredHandles(cartHandles);
    const has  = h => cartHandles.includes(h);
    const owns = h => covered.has(h);
    /* The threshold helper walks the whole catalogue for a price that bridges
       a gap; a set that repeats something the cart already owns is not a
       bridge. The contextual set cards keep offering overlapping sets on
       purpose: they are upgrades (a lone oil into its pair, loose resins into
       the Trinity), and that is a different promise. */
    const overlapsCart = h => {
      const contents = bundleContentsOf(h);
      return !!contents && contents.some(owns);
    };
    const p = h => _upsellProducts[h];

    const hasCharcoal = owns('quick-lighting-charcoal');
    const hasAttar   = owns('inner-sanctum-attar');
    const hasMyrrh   = owns('sacred-myrrh');
    const hasDragon  = owns('dragon-blood');

    const cartResins  = RESIN_HANDLES.filter(has);
    const cartFranks  = FRANKINCENSE_HANDLES.filter(has);
    const cartOils    = OIL_HANDLES.filter(has);
    const cartBundles = BUNDLE_HANDLES.filter(has);

    const hasAnyResin  = cartResins.length > 0;
    const hasAnyFrank  = cartFranks.length > 0;
    const hasAnyOil    = cartOils.length > 0;
    const hasAnyBundle = cartBundles.length > 0;

    /* Oils available to push, sorted by price DESC (premium first = bigger AOV lift) */
    const availOils = OIL_HANDLES
      .filter(h => !owns(h) && p(h))
      .sort((a, b) => ((p(b) && p(b).price) || 0) - ((p(a) && p(a).price) || 0));

    /* Frankincense ranked by price DESC */
    const availFranks = FRANKINCENSE_HANDLES
      .filter(h => !owns(h) && p(h))
      .sort((a, b) => ((p(b) && p(b).price) || 0) - ((p(a) && p(a).price) || 0));
    const bestFrank   = availFranks[0] || null;
    const secondFrank = availFranks[1] || null;

    /* ── Tag translations for localized upsell badges ── */
    const _tagPl = {
      'Perfect pair': 'Idealny duet',
      'Ancient pair': 'Pradawny duet',
      'Complete the trio': 'Dopełnij trio',
      'Complete the Trinity': 'Dopełnij Trójcę',
      'Upgrade': 'Premium',
      'Premium resin': 'Rzadka żywica',
      'Portable': 'Wersja podróżna',
      'Rare find': 'Rzadkość',
      '4.9★ rated': '4.9★',
      'Explore': 'Odkryj',
      'Best seller': 'Bestseller',
      'Grounding': 'Uziemienie',
      'Heart opener': 'Otwiera serce',
      'Protection': 'Ochrona',
      'Liquid sacred': 'Płynne sacrum',
      'Essential': 'Niezbędnik',
      'Complete the set': 'Dopełnij zestaw',
      'Complete the pairing': 'Dopełnij parę',
      'Worn, not burned': 'Noszony, nie palony'
    };
    const _tagCs = {
      'Perfect pair': 'Dokonalý pár',
      'Ancient pair': 'Prastarý pár',
      'Complete the trio': 'Doplň trio',
      'Complete the Trinity': 'Doplň Trojici',
      'Upgrade': 'Premium',
      'Premium resin': 'Vzácná pryskyřice',
      'Portable': 'Na cesty',
      'Rare find': 'Vzácný nález',
      '4.9★ rated': '4,9★',
      'Explore': 'Objev',
      'Best seller': 'Bestseller',
      'Grounding': 'Uzemnění',
      'Heart opener': 'Otevírá srdce',
      'Protection': 'Ochrana',
      'Liquid sacred': 'Tekuté posvátno',
      'Essential': 'Nezbytné',
      'Complete the set': 'Doplň sadu',
      'Complete the pairing': 'Doplň pár',
      'Worn, not burned': 'Nošený, ne pálený'
    };
    const _tagDe = {
      'Perfect pair': 'Perfektes Paar',
      'Ancient pair': 'Uraltes Paar',
      'Complete the trio': 'Mach das Trio komplett',
      'Complete the Trinity': 'Mach das Trio komplett',
      'Upgrade': 'Upgrade',
      'Premium resin': 'Edles Harz',
      'Portable': 'Für unterwegs',
      'Rare find': 'Seltener Fund',
      '4.9★ rated': '4,9★',
      'Explore': 'Entdecken',
      'Best seller': 'Bestseller',
      'Grounding': 'Erdend',
      'Heart opener': 'Öffnet das Herz',
      'Protection': 'Schutz',
      'Liquid sacred': 'Heiliges Öl',
      'Essential': 'Unverzichtbar',
      'Complete the set': 'Mach das Set komplett',
      'Complete the pairing': 'Mach das Paar komplett',
      'Worn, not burned': 'Getragen, nicht verbrannt'
    };
    const _tagNl = {
      'Perfect pair': 'Perfect duo',
      'Ancient pair': 'Eeuwenoud duo',
      'Complete the trio': 'Maak het trio compleet',
      'Complete the Trinity': 'Maak De Drie Koningen compleet',
      'Upgrade': 'Upgrade',
      'Premium resin': 'Edele hars',
      'Portable': 'Voor onderweg',
      'Rare find': 'Zeldzame vondst',
      '4.9★ rated': '4,9★',
      'Explore': 'Ontdekken',
      'Best seller': 'Bestseller',
      'Grounding': 'Aardend',
      'Heart opener': 'Opent het hart',
      'Protection': 'Bescherming',
      'Liquid sacred': 'Heilige olie',
      'Essential': 'Onmisbaar',
      'Complete the set': 'Maak de set compleet',
      'Complete the pairing': 'Maak het duo compleet',
      'Worn, not burned': 'Gedragen, niet gebrand'
    };
    const tTag = (t) => {
      if (CFG.locale === 'pl') return _tagPl[t] || t;
      if (CFG.locale === 'cs') return _tagCs[t] || t;
      if (CFG.locale === 'de') return _tagDe[t] || t;
      if (CFG.locale === 'nl') return _tagNl[t] || t;
      return t;
    };

    /* Helper: push if product exists and not already in cart */
    /* showTag: a row badge is rendered ONLY when the tag names a cart-specific
       reason (threshold unlocks, completions). Generic labels (Best seller,
       Grounding…) stay internal — ordering signal, not on-screen noise. */
    /* Every row carries a reason, and every reason is shown. These were computed
       and localized and then withheld on all but three tags, which left rows
       that looked arbitrary: a resin and a kit under a heading, with nothing
       saying why either followed from the cart. A recommendation without its
       reason is just a shelf. */
    const push = (handle, tag, tagStyle) => {
      if (!owns(handle) && p(handle)) result.push({ handle, tag: tTag(tag), tagStyle, showTag: !!tag });
    };

    /* Pick a contextual tag for an oil being cross-sold.
       If the customer already owns 2 of the 3 oils, the missing one earns
       the stronger "Complete the trio" framing — the third oil completes
       the frank/myrrh/rose set, which reads as curation, not pushiness.
       Owned counts what a set holds too, so "Complete the trio" is only ever
       claimed when the offered oil really is the missing third; a set buyer
       with no oil at all sees Jade under its own honest tag instead. */
    const oilsInCart = OIL_HANDLES.filter(owns).length;
    const oilTag = (handle, idx) => {
      if (oilsInCart === 2) return 'Complete the trio';
      if (idx === 0) return 'Perfect pair';
      if (handle === 'rose-otto') return 'Heart opener';
      if (handle === 'myrrh-essential-oil') return 'Liquid sacred';
      return 'Portable';
    };

    /* ── Threshold-aware upsell ──
       If the customer is close to free shipping or the free gift, surface
       a product whose price closes the gap. This is the most useful kind of
       recommendation because the reward is already shown above; we're just
       helping the customer cross the line they already want to cross.

       Rules:
       • Only fires when the gap is achievable with a single product
         we sell (gap ≤ max upsell price).
       • Skips when shipping is already unlocked and gift threshold is far
         (over 30% of gift threshold away — generic recs are better there).
       • Picks the cheapest item that bridges the gap, so the customer isn't
         oversold.
       • Tagged "Unlocks free shipping" / "Unlocks gift" — the real reason. */
    var thresholdHelper = null;
    try {
      var cartCents = (state && state.cart) ? regularTotalCents(state.cart) : 0;
      var cartAmount = cartCents / 100;
      var shipTh = Number(CFG.thresholdShipping) || 0;
      var giftTh = Number(CFG.thresholdGift) || 0;
      var gapToShip = shipTh - cartAmount;
      var gapToGift = giftTh - cartAmount;

      /* Bridge the next not-yet-earned reward in threshold order. This keeps
         island markets honest, where the gift unlocks before free shipping. */
      var goalGap = null;
      var goalTag = '';
      /* which reward, as data: goalTag is display copy on its way to translation */
      var goalKind = '';
      var rewardGoals = [];
      if (shipTh > 0 && gapToShip > 0) rewardGoals.push({ kind: 'shipping', threshold: shipTh, gap: gapToShip, fraction: 0.35, tag: 'Unlocks free shipping' });
      if (giftTh > 0 && gapToGift > 0) rewardGoals.push({ kind: 'gift', threshold: giftTh, gap: gapToGift, fraction: 0.25, tag: 'Unlocks your gift' });
      rewardGoals.sort(function(a, b) { return a.threshold - b.threshold; });
      if (rewardGoals.length && rewardGoals[0].gap <= rewardGoals[0].threshold * rewardGoals[0].fraction) {
        goalGap = rewardGoals[0].gap;
        goalTag = rewardGoals[0].tag;
        goalKind = rewardGoals[0].kind;
      }

      if (goalGap !== null) {
        /* Find cheapest available, not-in-cart product whose price ≥ goalGap.
           Walk the upsell catalogue, ignore the bundle (its preview math
           differs), include only single-product entries. */
        var candidates = Object.keys(_upsellProducts)
          .filter(function(h) { return !owns(h) && !overlapsCart(h); })
          .map(function(h) { return _upsellProducts[h]; })
          .filter(function(prod) {
            if (!prod) return false;
            if (_availabilityCache.get(prod.vid) === false) return false;
            var priceAmount = (prod.price || 0) / 100;
            return priceAmount >= goalGap && priceAmount <= goalGap + (goalGap * 0.6);
          })
          .sort(function(a, b) { return (a.price || 0) - (b.price || 0); });

        /* A RESIN SET SHORT OF A REWARD IS BRIDGED WITH A RESIN IT DOES NOT
           HOLD (2026-09-23, supersedes #2560). Nothing lands inside the band
           above for a set: every set already holds the charcoal, and tongs and
           mica are not in the rail. So a First Flame cart 29 zł short of free
           delivery and a Three Kings cart 50 zł short of the gift were shown
           Rose Otto and Jade Oil first, and the reward line above them went
           unanswered. Orders, 25 Jul-23 Sep: 47 First Flame baskets, the most
           common thing added beside it is a loose Myrrh (6); rail adds
           on set carts shown oils first: 0 in 143 sessions (First Flame 87,
           Three Kings 42, Alchemist 14). First Flame at its old 199 zł price
           was bridged by Myrrh and took 1 add in 8.
           The bridge is the cheapest loose resin the cart does not already
           own whose price clears the gap (ties keep RESIN_HANDLES order, so
           Myrrh before Dragon's Blood): Myrrh 108 zł for First Flame, Dragon's
           Blood 108 zł for the Three Kings. Only for a set that holds a
           burner, so a resin is never offered to a cart that cannot burn it. */
        if (!candidates.length && hasAnyBundle && owns('terracotta-burner')) {
          candidates = RESIN_HANDLES
            .filter(function(h) { return !owns(h) && p(h); })
            .map(function(h) { return p(h); })
            .filter(function(prod) {
              if (_availabilityCache.get(prod.vid) === false) return false;
              return (prod.price || 0) / 100 >= goalGap;
            })
            .sort(function(a, b) { return (a.price || 0) - (b.price || 0); });
        }

        if (candidates.length) {
          var pick = candidates[0];
          var tlPl = { 'Unlocks your gift': 'Z tym masz prezent' };
          var tlCs = { 'Unlocks your gift': 'Díky tomu získáš dárek' };
          var tlDe = { 'Unlocks your gift': 'Schaltet dein Geschenk frei' };
          var tlNl = { 'Unlocks your gift': 'Hiermee krijg je je cadeau' };
          var localizedTag = goalTag;
          if (goalKind === 'shipping') localizedTag = CFG.strings.unlockFreeShipping || goalTag;
          else if (CFG.locale === 'pl') localizedTag = tlPl[goalTag] || goalTag;
          else if (CFG.locale === 'cs') localizedTag = tlCs[goalTag] || goalTag;
          else if (CFG.locale === 'de') localizedTag = tlDe[goalTag] || goalTag;
          else if (CFG.locale === 'nl') localizedTag = tlNl[goalTag] || goalTag;
          thresholdHelper = { handle: pick.handle, tag: localizedTag, tagStyle: 'save', priority: true, showTag: true };
        }
      }
    } catch (e) { /* never break the cart drawer over a soft upsell hint */ }

    if (thresholdHelper) result.push(thresholdHelper);

    /* One complete-set nudge at most. It sits behind any threshold helper but
       ahead of the ordinary contextual rows, and never competes with a set
       the customer has already chosen. */
    var setCardAdded = false;
    if (!hasAnyBundle) {
      var bundleCandidates = [];
      /* Jade-oil + attar buyer has chosen depth — offer The Sanctum (its own
         availability filter drops it while draft), which suppresses the K&Q
         nudge rather than double-nudging. */
      if (has('jade-oil') && hasAttar) bundleCandidates = ['the-sanctum'];
      else if (has('jade-oil') && has('rose-otto')) bundleCandidates = ['first-ritual'];
      else if (has('jade-oil')) bundleCandidates = ['king-queen', 'first-ritual'];
      else if (has('rose-otto')) bundleCandidates = ['king-queen'];
      else if (has('inner-sanctum-attar')) bundleCandidates = ['the-sanctum'];
      else if (hasAnyResin) bundleCandidates = ['trinity'];

      var bundleCartCents = 0;
      try {
        bundleCartCents = (state && state.cart) ? regularTotalCents(state.cart) : 0;
      } catch (e) { /* a soft nudge must never break the drawer */ }

      for (var bundleIndex = 0; bundleIndex < bundleCandidates.length; bundleIndex++) {
        var bundleHandle = bundleCandidates[bundleIndex];
        var bundleProduct = p(bundleHandle);
        if (!bundleProduct || has(bundleHandle)) continue;
        /* A set the cart already holds in full is not an upgrade, it is the
           same two bottles again: Jade Oil plus the attar were being offered
           The Sanctum, which is Jade Oil plus the attar. A set the cart holds
           in part is still offered where it adds something (loose resins into
           the Trinity), except the pair below, which is handled as a tag. */
        var setAll = bundleContentsOf(bundleHandle) || [];
        var setContents = setAll.filter(function(c) { return p(c); });
        if (setContents.length && setContents.every(owns)) continue;
        /* One half of a pair in the cart (a lone King, a lone attar): "Add
           the pair" would add a second bottle of what they already chose. The
           nudge lives on the missing half's own row instead (tagged below),
           which adds the Queen, not the pair. Swapping the loose half into
           the pair at the pair's price is a product decision, not made here. */
        if (setAll.length === 2 && setAll.some(has)) continue;
        /* Skip a candidate already known unavailable — otherwise it latches
           setCardAdded, gets dropped by the final availability filter, and
           suppresses both the next candidate and the first-flame fallback. */
        if (_availabilityCache.get(bundleProduct.vid) === false) continue;
        if ((bundleProduct.price || 0) <= bundleCartCents + 4000) continue;
        var setRow = { handle: bundleHandle, tag: tTag('Complete the set'), tagStyle: 'bundle', isSet: true };
        /* The K&Q pairing card earns the completion tag + a directional line
           that names the missing half. */
        if (bundleHandle === 'king-queen') {
          if (has('jade-oil'))       { setRow.tag = tTag('Complete the pairing'); setRow.setLine = KQ_COMPLETION_LINE.king; }
          else if (has('rose-otto')) { setRow.tag = tTag('Complete the pairing'); setRow.setLine = KQ_COMPLETION_LINE.queen; }
        }
        result.push(setRow);
        setCardAdded = true;
        break;
      }
    }

    /* Tool upsell — offer charcoal when resin is in cart and charcoal is missing. */
    if (hasAnyResin && !hasCharcoal && p('quick-lighting-charcoal')) {
      push('quick-lighting-charcoal', 'Essential', 'bundle');
    }

    if (hasAnyOil) {
      /* ═══ OIL BUYER — oils are the AOV engine, cross-sell them hard ═══ */

      /* 0. Bundle-system v2: the oil buyer's missing half is the FIRE.
         First Flame ("Complete the set") leads, then the Hojari Blend
         resin. Both no-op if the product is missing/unavailable (push()
         checks the catalog, and initCatalog tolerates 404s). */
      if (!hasAnyBundle && !setCardAdded) push('first-flame', 'Complete the set', 'bundle');

      /* Jade and the Inner Sanctum are the same frankincense answered twice:
         one burned or worn as oil, one worn on skin as attar. For a jade cart
         the attar is the single most natural next thing we sell, and it was
         sitting behind two other oils and a loose resin where a two-row drawer
         never reached it. It leads for jade now, in either form. */
      if (!hasAttar && (has('jade-oil') || has('sultans-jade-frankincense'))) {
        push('inner-sanctum-attar', 'Worn, not burned', 'pair');
      }

      /* 1-2. Other oils first — RELATIONSHIP-based ordering (not price-based).
         Frankincense + Myrrh are the sacred pair, traditionally burned together
         since antiquity. So a Jade Oil buyer sees Myrrh oil first; a Myrrh oil
         buyer sees Jade first. Rose Otto is the heart-opening third oil.
         This trades a small AOV lift (Rose is highest priced) for a stronger,
         more natural-feeling recommendation that reads as curation, not upsell. */
      let oilOrder;
      if (has('jade-oil'))                     oilOrder = ['myrrh-essential-oil', 'rose-otto'];
      else if (has('myrrh-essential-oil'))     oilOrder = ['jade-oil', 'rose-otto'];
      else if (has('rose-otto'))               oilOrder = ['jade-oil', 'myrrh-essential-oil'];
      else                                     oilOrder = ['jade-oil', 'myrrh-essential-oil', 'rose-otto'];
      oilOrder.filter(h => p(h)).forEach((h, i) => push(h, oilTag(h, i), 'pair'));

      /* 3. Attar — the closest thing to the oil they already chose: the same
         material, worn on skin rather than burned. It sat fifth, behind a loose
         resin, so it almost never reached a visible row. */
      if (!hasAttar) push('inner-sanctum-attar', '4.9★ rated', 'bundle');

      /* 4. Then the resin — the fire under the oil, house blend first */
      push('hojari-blend', 'Best seller', 'loved');

      /* 5. Best frankincense resin */
      if (bestFrank) result.push({ handle: bestFrank, tag: tTag('Best seller'), tagStyle: 'loved', showTag: true });

      /* 5. Myrrh resin */
      if (!hasMyrrh) push('sacred-myrrh', 'Grounding', 'loved');

      /* 6. Dragon blood */
      if (!hasDragon) push('dragon-blood', 'Rare find', 'loved');

    } else if (hasAnyBundle) {
      /* ═══ SET BUYER — the set already holds its resins; the oils are the AOV ═══
         Any bridge above leads. Then the oils, dearest first: Rose Otto is the
         highest-margin thing we sell, and not a single thing in a resin set
         repeats it. Oil sets (King & Queen, The Sanctum) hold no burner, so
         their fire is First Flame, never a loose resin they cannot burn; that
         row used to be Dragon's Blood for every set. */

      /* 1-3. All three oils — bundle buyers are committed, oils add 170-500 zł AOV */
      availOils.forEach((h, i) => push(h, oilTag(h, i), 'pair'));

      /* 4. Attar — luxury AOV lift */
      if (!hasAttar) push('inner-sanctum-attar', '4.9★ rated', 'bundle');

      /* 5. Resin sets: the resins the set leaves out. Oil sets: the fire. */
      if (owns('terracotta-burner')) {
        if (!hasDragon) push('dragon-blood', 'Rare find', 'loved');
        push('sultans-jade-frankincense', 'Upgrade', 'save');
      } else {
        push('first-flame', 'Complete the set', 'bundle');
      }

    } else if (hasAnyResin) {
      /* ═══ RESIN BUYER — need tools (handled above) + natural companions + oils for AOV ═══ */

      /* 0. Jade resin and the Inner Sanctum are the same material in two forms.
         The attar led nothing here; it was step five. */
      if (!hasAttar && has('sultans-jade-frankincense')) {
        push('inner-sanctum-attar', 'Worn, not burned', 'pair');
      }

      /* 1. Oil — pick the most aligned companion for the resin in cart.
         Frank/myrrh/dragon resins pair best with jade-oil (frankincense oil, $70, same scent family).
         It's also the most accessible oil price point — stronger conversion than $500 rose otto. */
      push('jade-oil', hasAnyFrank || hasAttar ? 'Perfect pair' : 'Portable', 'pair');

      /* 1b. Bundle-system v2: a resin buyer without a burner can't burn.
         Offer the terracotta burner right after the oil when absent. */
      if (!has('terracotta-burner')) push('terracotta-burner', 'Essential', 'bundle');

      /* 2. Best frankincense resin */
      if (bestFrank) {
        if (hasMyrrh && !hasAnyFrank) {
          result.push({ handle: bestFrank, tag: tTag('Ancient pair'), tagStyle: 'pair', showTag: true });
        } else if (hasAnyFrank) {
          result.push({ handle: bestFrank, tag: tTag('Upgrade'), tagStyle: 'save', showTag: true });
        } else {
          result.push({ handle: bestFrank, tag: tTag('Premium resin'), tagStyle: 'loved', showTag: true });
        }
      }

      /* 3. Myrrh — grounding complement to frankincense */
      if (!hasMyrrh && hasAnyFrank) push('sacred-myrrh', 'Grounding', 'loved');

      /* 4. Dragon Blood — Trinity completer or rare find */
      if (!hasDragon) {
        if (hasAnyFrank && hasMyrrh) push('dragon-blood', 'Complete the Trinity', 'loved');
        else push('dragon-blood', 'Rare find', 'loved');
      }

      /* 5. Attar — luxury tier */
      if (!hasAttar) push('inner-sanctum-attar', '4.9★ rated', 'bundle');

      /* 6. Myrrh oil — resin-to-oil upgrade, strong complement for myrrh buyers */
      push('myrrh-essential-oil', hasMyrrh ? 'Perfect pair' : 'Liquid sacred', 'pair');

      /* 7. Rose otto — heart opener, rounds out the trio */
      push('rose-otto', 'Heart opener', 'pair');

      /* 8. Second frankincense */
      if (secondFrank && !has(secondFrank)) {
        result.push({ handle: secondFrank, tag: tTag('Explore'), tagStyle: 'loved' });
      }

    } else if (hasAttar) {
      /* ═══ ATTAR ONLY (no resin, no oil) ═══ */

      /* 1-2. Oils — natural companions to attar */
      availOils.forEach((h, i) => push(h, oilTag(h, i), 'pair'));

      /* 3. Best frankincense */
      if (bestFrank) result.push({ handle: bestFrank, tag: tTag('Best seller'), tagStyle: 'loved' });

      /* 4. Myrrh resin */
      if (!hasMyrrh) push('sacred-myrrh', 'Grounding', 'loved');

      /* 5. Dragon blood */
      if (!hasDragon) push('dragon-blood', 'Rare find', 'loved');

    } else if (cartHandles.some(h => ACCESSORY_HANDLES.indexOf(h) >= 0)) {
      /* ═══ TOOLS ONLY — a burner, coal or plates with nothing to burn ═══
         These carts were shown Sultan's Jade, then Jade Oil, the attar and
         Rose Otto: 1 rail add in 83 sessions (a Jade Oil, 25 Jul-23 Sep).
         What they went on to add themselves was coal and resin (burner carts:
         charcoal 12 of 19; coal and mica carts: myrrh, Sultan's Jade, Dragon's
         Blood). So: the coal if it is missing, then the resins, best seller
         first (Myrrh: 47 orders, the most of any loose resin), and the
         Jade Oil keeps a row. */
      if (!hasCharcoal) push('quick-lighting-charcoal', 'Essential', 'bundle');
      push('sacred-myrrh', 'Best seller', 'loved');
      if (bestFrank) result.push({ handle: bestFrank, tag: tTag('Upgrade'), tagStyle: 'save', showTag: true });
      push('jade-oil', 'Portable', 'pair');
      push('dragon-blood', 'Rare find', 'loved');

    } else {
      /* ═══ EMPTY CART — bestsellers + highest-AOV mix ═══ */

      if (bestFrank) result.push({ handle: bestFrank, tag: tTag('Best seller'), tagStyle: 'loved' });
      push('jade-oil',              'Portable', 'pair');
      push('inner-sanctum-attar',   '4.9★ rated',     'bundle');
      push('rose-otto',             'Heart opener',    'pair');
      push('sacred-myrrh',          'Grounding',       'loved');
      if (secondFrank && !has(secondFrank)) {
        result.push({ handle: secondFrank, tag: tTag('Explore'), tagStyle: 'loved' });
      }
      push('myrrh-essential-oil',   'Liquid sacred',   'pair');
      push('dragon-blood',          'Rare find',       'loved');
    }

    /* Jade's three, in order. Sho's call, and the reasoning holds: the attar is
       the same frankincense worn instead of burned, the myrrh is the pair it
       has been burned with since antiquity, and First Flame is the fire that
       makes either of them a ritual rather than a bottle. Anything the engine
       ranked above them — a set card, a loose resin — falls in behind, because
       a two or three row drawer only ever shows the top of this list. */
    function leadFor(handles) {
      const lead = [];
      for (let i = 0; i < handles.length; i++) {
        const idx = result.findIndex(function(r) { return r.handle === handles[i]; });
        if (idx >= 0) lead.push(result.splice(idx, 1)[0]);
      }
      for (let i = lead.length - 1; i >= 0; i--) result.unshift(lead[i]);
    }
    if (has('jade-oil') || has('sultans-jade-frankincense')) {
      leadFor(['inner-sanctum-attar', 'myrrh-essential-oil', 'first-flame']);
    }

    /* A pair's completion nudge, carried by the missing half's own row: the
       Queen completes the King, the attar completes the Jade. Only when
       exactly one half is in the cart and the pair itself is not; a threshold
       bridge keeps its own, truer reason. */
    Object.keys(BUNDLE_CONTENTS).forEach(function(setHandle) {
      var halves = BUNDLE_CONTENTS[setHandle];
      if (halves.length !== 2 || owns(setHandle) || !p(setHandle)) return;
      if (has(halves[0]) === has(halves[1])) return;
      var missingHalf = has(halves[0]) ? halves[1] : halves[0];
      var halfRow = result.find(function(r) { return r.handle === missingHalf; });
      if (halfRow && !halfRow.priority) { halfRow.tag = tTag('Complete the pairing'); halfRow.tagStyle = 'pair'; halfRow.showTag = true; }
    });

    /* Deduplicate, exclude items already in cart, and skip anything whose
       variant has gone unavailable since the catalog was fetched. Keep the
       drawer focused. */
    const seen = new Set();
    const order = result.filter(r => {
      if (seen.has(r.handle)) return false;
      if (owns(r.handle)) return false;
      const p = _upsellProducts[r.handle];
      if (!p) return false;
      if (_availabilityCache.get(p.vid) === false) return false;
      seen.add(r.handle);
      return true;
    }).slice(0, 4);

    /* ── THE FREE-DELIVERY LINE (2026-09-25) ──
       Polish carts that open below free delivery check out far less often:
       <150 zł 36% (143 sessions), 150-278 zł 55%, at or over 279 zł 63-65%
       (PostHog, 30 days to 25 Sep). The curated order above stays exactly as
       it is; nothing is moved or added for price. When free delivery is the
       next reward the basket has not earned, the FIRST row in that order whose
       price closes the gap carries one line naming the payoff from the live
       cart, in the market's own money ("+25 zł i dostawa gratis"). If no row
       closes it, no row says anything: the journey line above already says
       how far free delivery is. A gift that comes before free delivery (CH,
       CY, MT) is not this line's business. */
    try {
      if (typeof rewardGoals !== 'undefined' && rewardGoals.length && rewardGoals[0].kind === 'shipping') {
        const gapCents = Math.round(Number(CFG.thresholdShipping) * 100) - regularTotalCents(state.cart);
        const at = gapCents > 0 ? order.findIndex(function(r) {
          const prod = _upsellProducts[r.handle];
          return !!prod && !isGiftVariantId(prod.vid) && (prod.price || 0) >= gapCents;
        }) : -1;
        if (at >= 0) {
          order[at].nudge = { gapCents: gapCents, priceCents: _upsellProducts[order[at].handle].price, rowIndex: at };
          /* the rail's own "unlocks free shipping" badge would say it twice */
          if (order[at].priority) order[at].showTag = false;
        }
      }
    } catch (e) { /* a soft line must never break the rail */ }
    return order;
  }

  function updateUpsellHeading(cartHandles, order) {
    if (!upsellTitle) return;
    const has = h => cartHandles.includes(h);
    const hasAnyResin = RESIN_HANDLES.some(has);
    /* The attar is an oil worn on the skin, and the rail under an attar cart is
       oils. It was missing here, so an Inner Sanctum cart fell to the fallback
       heading and told the shopper a 500 zł Rose Otto was "what you need"
       (live, 2026-09-23). It takes the oil heading its rows already imply. */
    const hasAnyOil = OIL_HANDLES.some(has) || has('inner-sanctum-attar');
    const hasAnyBundle = BUNDLE_HANDLES.some(has);
    /* A threshold pick leads for its own reason — the heading must tell that
       truth, not claim "essentials for your resin" on an oils-only cart.
       "Essentials" fires only when a tool leads AND a resin is actually there. */
    const firstIsThreshold = order && order[0] && order[0].priority;
    const TOOL_HANDLES = ['quick-lighting-charcoal'];
    const startsWithTools =
      order && order[0] && TOOL_HANDLES.indexOf(order[0].handle) >= 0 && hasAnyResin;
    let text;
    if (CFG.locale === 'pl') {
      if (firstIsThreshold) text = 'Już blisko';
      else if (startsWithTools) text = 'Niezbędne do żywic';
      else if (hasAnyOil) text = 'Pasuje do olejku';
      else if (hasAnyResin || hasAnyBundle) text = 'Pasuje do koszyka';
      else text = 'Co będzie potrzebne';
    } else if (CFG.locale === 'cs') {
      if (firstIsThreshold) text = 'Už jen kousek';
      else if (startsWithTools) text = 'Nezbytné k pryskyřici';
      else if (hasAnyOil) text = 'Doplň svůj olej';
      else if (hasAnyResin || hasAnyBundle) text = 'Hodí se ke košíku';
      else text = 'Co k tomu potřebuješ';
    } else if (CFG.locale === 'de') {
      /* German had no branch here and fell through to the English strings, so a
         German cart read "Pairs with your cart" under a German drawer. */
      if (firstIsThreshold) text = 'Fast geschafft';
      else if (startsWithTools) text = 'Unentbehrlich für dein Harz';
      else if (hasAnyOil) text = 'Passt zu deinem Öl';
      else if (hasAnyResin || hasAnyBundle) text = 'Passend zu deiner Auswahl';
      else text = 'Was du dazu brauchst';
    } else if (CFG.locale === 'nl') {
      /* Dutch had no branch here and fell through to English, so the drawer
         rendered "Wat je erbij nodig hebt" from the Liquid and the first cart
         mutation replaced it with "Pairs with your cart". Same fault German
         had, found the same way. The default matches
         sacrasoul.cart_drawer.upsell_heading_default in locales/nl.json. */
      if (firstIsThreshold) text = 'Je bent er bijna';
      else if (startsWithTools) text = 'Onmisbaar bij je hars';
      else if (hasAnyOil) text = 'Past bij je olie';
      else if (hasAnyResin || hasAnyBundle) text = 'Past bij je winkelwagen';
      else text = 'Wat je erbij nodig hebt';
    } else {
      if (firstIsThreshold) text = 'You’re close';
      else if (startsWithTools) text = 'Essentials for your resin';
      else if (hasAnyOil) text = 'Pairs with your oil';
      else if (hasAnyResin || hasAnyBundle) text = 'Pairs with your cart';
      else text = 'What you’ll need';
    }
    if (upsellTitle.textContent !== text) upsellTitle.textContent = text;
  }

  function findProduct(handle) {
    return (_upsellProducts && _upsellProducts[handle])
      || (_upsellError && _upsellError.handle === handle ? _upsellError.product : null);
  }

  function upsellMeta(type) {
    if (!type) return '';
    var t = type.toLowerCase();
    if (t.indexOf('resin') > -1 || t.indexOf('\u017cywic') > -1) return CFG.strings.typeResin;
    if (t.indexOf('attar') > -1) return CFG.strings.typeAttar;
    if (t.indexOf('incense') > -1 || t.indexOf('kadzid') > -1) return CFG.strings.typeIncense;
    if (t.indexOf('oil') > -1 || t.indexOf('olej') > -1) return CFG.strings.typeOil;
    if (t.indexOf('tool') > -1 || t.indexOf('narz\u0119d') > -1) return CFG.strings.typeTools;
    return type;
  }

  /* One quiet line under each pairing name \u2014 what it is to the ritual, not a
     category tag. Written per product ("The resin behind the oil"), with the
     generic type label as the fallback for anything unmapped. */
  const UPSELL_LINES = {
    'jade-oil':                 { en: 'The green Hojari, drop by drop.',     pl: 'Zielone Hojari, kropla po kropli.',      cs: 'Zelen\u00e9 Hojari, kapka po kapce.', de: 'Gr\u00fcnes Hojari, Tropfen f\u00fcr Tropfen.', nl: 'De groene Hojari, druppel voor druppel.' },
    'rose-otto':                { en: 'The queen beside the king.',          pl: 'Kr\u00f3lowa u boku kr\u00f3la.',        cs: 'Kr\u00e1lovna po boku kr\u00e1le.', de: 'Die K\u00f6nigin an der Seite des K\u00f6nigs.', nl: 'De koningin naast de koning.' },
    'myrrh-essential-oil':      { en: "Frankincense's ancient companion.",   pl: 'Odwieczna towarzyszka kadzid\u0142a.',   cs: 'Odv\u011bk\u00e1 spole\u010dnice kadidla.', de: 'Die Gef\u00e4hrtin des Weihrauchs, seit jeher.', nl: 'De oude metgezel van wierook.' },
    'inner-sanctum-attar':      { en: 'The golden attar, worn on skin.',     pl: 'Z\u0142oty attar, noszony na sk\u00f3rze.', cs: 'Zlat\u00fd attar na k\u016f\u017ei.', de: 'Der goldene Attar, auf der Haut.', nl: 'De gouden attar, op de huid gedragen.' },
    'amber-hojari':             { en: 'Warm amber, slow-burning.',           pl: 'Ciep\u0142y bursztyn, powoli si\u0119 tli.', cs: 'Tepl\u00fd jantar, pomalu doutn\u00e1.', de: 'Warmer Bernstein, langsam glimmend.', nl: 'Warme amber, brandt langzaam.' },
    'sultans-jade-frankincense':{ en: 'The resin behind the oil.',           pl: '\u017bywica, z kt\u00f3rej rodzi si\u0119 olejek.', cs: 'Pryskyřice, z níž vzniká olej.', de: 'Das Harz, aus dem das \u00d6l kommt.', nl: 'De hars waar de olie uit komt.' },
    'hojari-blend':             { en: 'White and black Hojari, for every day.', pl: 'Bia\u0142e i czarne Hojari, na co dzie\u0144.', cs: 'B\u00edl\u00e9 a \u010dern\u00e9 Hojari, pro ka\u017ed\u00fd den.', de: 'Wei\u00dfer und schwarzer Hojari, f\u00fcr jeden Tag.', nl: 'Witte en zwarte Hojari, voor elke dag.' },
    'sacred-myrrh':             { en: 'Grounds what frankincense lifts.',    pl: 'Uziemia to, co kadzid\u0142o unosi.',    cs: 'Uzemn\u00ed, co kadidlo pozved\u00e1.', de: 'Erdet, was der Weihrauch hebt.', nl: 'Aardt wat de wierook verheft.' },
    'dragon-blood':             { en: 'The rarest resin, from Socotra.',     pl: 'Najrzadsza \u017cywica, z Sokotry.',     cs: 'Nejvz\u00e1cn\u011bj\u0161\u00ed prysky\u0159ice ze Sokotry.', de: 'Das seltenste Harz, aus Sokotra.', nl: 'De zeldzaamste hars, van Socotra.' },
    'quick-lighting-charcoal':  { en: 'Lights in seconds, burns clean.',     pl: 'Zapala si\u0119 w sekundy, pali czysto.', cs: 'Zap\u00e1l\u00ed se za p\u00e1r vte\u0159in.', de: 'Geht in Sekunden an, brennt sauber.', nl: 'In seconden aan, brandt schoon.' },
    'first-flame':              { en: 'The fire for the home, complete.',    pl: 'Ogie\u0144 dla domu, w komplecie.',      cs: 'Oheň pro domov, se vším všudy.', de: 'Das Feuer f\u00fcrs Zuhause, alles dabei.', nl: 'Het vuur voor thuis, compleet.' },
    'terracotta-burner':        { en: 'Handmade in Oman, holds the coal.',   pl: 'R\u0119cznie robiona w Omanie.',         cs: 'Ru\u010dn\u011b vyroben\u00e1 v Om\u00e1nu.', de: 'Von Hand im Oman gemacht, h\u00e4lt die Kohle.', nl: 'Met de hand gemaakt in Oman, draagt het kooltje.' }
  };
  /* The one line per product, from snippets/cart-item-line.liquid via the config.
     UPSELL_LINES stays only as a fallback for an older cached config. */
  function itemLine(handle) {
    /* with the config map present (every current page) never touch
       UPSELL_LINES: rows can render during boot, before that const exists */
    const lines = CFG.itemLines;
    if (lines) return lines[handle] ? String(lines[handle]) : '';
    /* no map (a page older than its script): the local copy, guarded because a
       render during boot can run before that const is initialised */
    try {
      const l = UPSELL_LINES[handle];
      return l ? localeText(l.en, l.pl, l.cs, l.de, l.nl) : '';
    } catch (e) { return ''; }
  }
  function upsellLine(handle, type) {
    const own = itemLine(handle);
    if (own) return own;
    const l = UPSELL_LINES[handle];
    if (l) return localeText(l.en, l.pl, l.cs, l.de, l.nl);
    return upsellMeta(type);
  }

  /* The free-delivery line, from the live cart in the market's money shape:
     "+25 zł i dostawa gratis". Only ever on a row whose price closes the gap. */
  function nudgeText(n) {
    return String(CFG.strings.nudgeFreeDelivery || '').replace('{amount}', formatMoney(n.priceCents));
  }

  function nudgeBehaviorProperties(n) {
    return {
      source: 'free_delivery_nudge',
      product_handle: n.handle,
      variant_id: n.vid,
      gap: Math.round(n.gapCents) / 100,
      gap_cents: n.gapCents,
      price_cents: n.priceCents,
      row_index: n.rowIndex,
      threshold: Number(CFG.thresholdShipping) || 0,
      in_view: n.inView,
      currency: CFG.currency || null,
      country: CFG.country || null,
      page: window.location.pathname
    };
  }
  function noteNudgePainted(visibleOrder) {
    const at = visibleOrder.findIndex(function(item) { return !!item.nudge; });
    const lead = at >= 0 ? visibleOrder[at] : null;
    const p = lead && CFG.strings.nudgeFreeDelivery ? findProduct(lead.handle) : null;
    if (!p) { _nudgeLive = null; return; }
    const key = p.handle + '|' + lead.nudge.gapCents;
    const fresh = key !== _nudgeShownKey;
    let inView = _nudgeLive && _nudgeLive.key === key ? _nudgeLive.inView : null;
    if (fresh) {
      try {
        const row = upsellTrack.children[at];
        const r = row.getBoundingClientRect();
        const b = body.getBoundingClientRect();
        inView = r.top < b.bottom && r.bottom > b.top;
      } catch (e) { inView = null; }
    }
    _nudgeLive = {
      key: key, vid: Number(p.vid), handle: p.handle, inView: inView,
      gapCents: lead.nudge.gapCents, priceCents: lead.nudge.priceCents, rowIndex: at
    };
    /* An impression is only an impression while the drawer is open: a
       repaint behind a closed drawer (a resize, a late add failure) keeps
       _nudgeLive current for the next open but neither counts nor spends the
       key, so that open still reports its own showing once. */
    if (fresh && drawer.classList.contains('is-open')) {
      _nudgeShownKey = key;
      emitBehavior('cart_nudge_shown', nudgeBehaviorProperties(_nudgeLive));
    }
  }

  /* One pairing = a full-width ROW (image | name+meta | price | add). */
  function renderUpsellRow(item) {
    const p = findProduct(item.handle);
    if (!p) return '';
    const soldOut = _unavailableUpsellHandles.has(p.handle);
    const errorHtml = !soldOut && _upsellError && _upsellError.handle === p.handle
      ? '<span class="upsell-add-error" role="alert">' + esc(_upsellError.text) + '</span>'
      : '';
    if (item.isSet && BUNDLE_META[p.handle]) {
      const _metaSwap = CFG.hojariSwapped === true ? BUNDLE_META_SWAP : (CFG.amberSwapped === true ? BUNDLE_META_AMBER_SWAP : null);
      const bundleSwap = _metaSwap && _metaSwap[p.handle];
      const bundle = bundleSwap ? Object.assign({}, BUNDLE_META[p.handle], bundleSwap) : BUNDLE_META[p.handle];
      const bundleName = localeText(bundle.name.en, bundle.name.pl, bundle.name.cs, bundle.name.de, bundle.name.nl);
      /* A directional pairing card (K&Q) overrides the static set line and
         labels itself with the completion tag instead of the generic SET. */
      const bundleLine = item.setLine
        ? localeText(item.setLine.en, item.setLine.pl, item.setLine.cs, item.setLine.de, item.setLine.nl)
        : localeText(bundle.line.en, bundle.line.pl, bundle.line.cs, bundle.line.de, bundle.line.nl);
      /* On a set card the payoff is one more segment of the price line
         ("399 zł · 745 zł osobno · dostawa gratis"): the
         description stays, and the price is not said twice. */
      const setNudge = item.nudge ? String(CFG.strings.nudgeSetFreeDelivery || '') : '';
      /* DE/NL: sacrasoul.cart_drawer.bundle_badge ("Set"); the tag is set in capitals by CSS */
      const bundleTag = (item.setLine && item.tag) ? item.tag : localeText('SET', 'ZESTAW', 'SADA', 'SET', 'SET');
      /* DE "Teile" and NL "in de doos" (7 in de doos), both 2026-09-23 */
      const bundleInside = localeText('inside', 'w środku', 'uvnitř', 'Teile', 'in de doos');
      /* K&Q pairing card (setLine present): never "set", never a strikethrough —
         the pair is priced as the natural whole (no-discount doctrine).
         DE/NL: the pair wording of the K&Q button on the jade and rose PDPs;
         the set wording is cart_drawer.add_bundle, whose Polish this already is. */
      const bundleAdd = item.setLine
        ? localeText('Add the pair →', 'Dodaj parę →', 'Přidat pár →', 'Das Paar hinzufügen →', 'Voeg het paar toe →')
        : localeText('Add the set →', 'Dodaj zestaw →', 'Přidat sadu →', 'Set hinzufügen →', 'Set toevoegen →');
      const bundleButtonText = soldOut ? CFG.strings.soldOut : bundleAdd;
      /* A bundle's component basket is a value comparison, never a former
         selling price. Keep it separate from compareAt so live product
         revalidation cannot rewrite it or turn it into a sale claim. */
      const bundleValueLabel = localeText('full value', 'osobno', 'při koupi zvlášť', 'Einzelwert', 'losse waarde');
      const bundleValue = (!item.setLine && p.componentValue && p.componentValue > p.price)
        ? '<span class="upsell--set__value">' + formatMoney(p.componentValue) + ' ' + esc(bundleValueLabel) + '</span>'
        : '';
      return '<div class="urow upsell--set' + (setNudge ? ' urow--nudge' : '') + '" data-variant-id="' + esc(p.vid) + '" data-handle="' + esc(p.handle) + '">'
        + '<a class="upsell--set__img" href="' + esc(p.url) + '" data-action="navigate" tabindex="-1" aria-hidden="true">'
        + (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy">' : '')
        + '</a>'
        + '<div class="upsell--set__b">'
        + '<span class="upsell--set__tag">' + esc(bundleTag) + '</span>'
        + '<a class="upsell--set__name" href="' + esc(p.url) + '" data-action="navigate">' + esc(bundleName) + '</a>'
        + '<span class="upsell--set__line">' + esc(bundleLine) + '</span>'
        + '<span class="upsell--set__prices"><span class="upsell--set__now">' + formatMoney(p.price) + '</span>' + bundleValue + (setNudge
          /* the payoff takes the piece count's slot so the price line stays one
             line at 390px; the description already names what is inside */
          ? '<span class="upsell--set__count urow__nudge">· ' + esc(setNudge) + '</span>'
          : '<span class="upsell--set__count">· ' + bundle.count + ' ' + esc(bundleInside) + '</span>') + '</span>'
        + errorHtml
        + '</div>'
        + '<button class="upsell--set__add' + (soldOut ? ' is-sold-out' : '') + '" data-action="add-upsell" data-variant-id="' + esc(p.vid) + '" aria-label="' + esc(soldOut ? CFG.strings.soldOut + ': ' + bundleName : bundleAdd + (setNudge ? ', ' + setNudge : '')) + '"' + (soldOut ? ' disabled aria-disabled="true"' : '') + '>' + esc(bundleButtonText) + '</button>'
        + '</div>';
    }
    const plus = '<svg viewBox="0 0 24 24"><path d="M12 6v12M6 12h12"/></svg>';
    let priceHtml;
    if (p.compareAt && p.compareAt > p.price) {
      priceHtml = '<span class="urow__was">' + formatMoney(p.compareAt) + '</span><span class="urow__p">' + formatMoney(p.price) + '</span>';
    } else {
      priceHtml = '<span class="urow__p">' + formatMoney(p.price) + '</span>';
    }
    /* The free-delivery nudge says its payoff in place of the ritual line:
       one line, the same height as every other row. */
    const nudgeLine = item.nudge ? nudgeText(item.nudge) : '';
    const meta = nudgeLine || upsellLine(p.handle, p.type);
    /* The engine's cart-specific reasons (Unlocks your gift / free shipping,
       Complete the trio…) were computed and localized but never rendered on
       plain rows — the single most persuasive line in the drawer was invisible.
       Render them as a quiet badge; generic tags stay off-screen. */
    const rowTag = (item.showTag && item.tag)
      ? '<span class="urow__tag' + (item.tagStyle === 'save' ? ' urow__tag--save' : '') + '">' + esc(item.tag) + '</span>'
      : '';
    return '<div class="urow' + (nudgeLine ? ' urow--nudge' : '') + '" data-variant-id="' + p.vid + '" data-handle="' + esc(p.handle) + '">'
      + '<a href="' + esc(p.url) + '" class="urow__img" data-action="navigate" tabindex="-1" aria-hidden="true">'
      + (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy">' : '')
      + '</a>'
      + '<div class="urow__b">'
      + rowTag
      + '<a href="' + esc(p.url) + '" class="urow__n" data-action="navigate">' + esc(p.title) + '</a>'
      + (meta ? '<span class="urow__d' + (nudgeLine ? ' urow__nudge' : '') + '">' + esc(meta) + '</span>' : '')
      + errorHtml
      + '</div>'
      /* the line already says the price ("+25 zł i dostawa gratis"), so the
         column stands down on that row unless it has a former price to show */
      + ((nudgeLine && !(p.compareAt && p.compareAt > p.price)) ? '' : '<span class="urow__prices">' + priceHtml + '</span>')
      + '<button class="urow__add' + (soldOut ? ' is-sold-out' : '') + '" data-action="add-upsell" data-variant-id="' + p.vid + '" aria-label="' + esc(soldOut ? CFG.strings.soldOut + ': ' + p.title : CFG.strings.addText + ' ' + p.title + (nudgeLine ? ', ' + nudgeLine : '')) + '"' + (soldOut ? ' disabled aria-disabled="true"' : '') + '>' + (soldOut ? esc(CFG.strings.soldOut) : plus) + '</button>'
      + '</div>';
  }

  /* The pairing rows are a list, not a carousel.
     Arrows, dots and a horizontal swipe all promised a sideways move through
     more products. The track has been a vertical column for a while, so the
     gesture and the glyphs disagreed with the thing they controlled, and the
     second row still landed under the fold — a product cut in half by the
     subtotal bar reads as broken, not as "there is more".
     Two rows, ranked, always whole. Nothing to page, nothing to discover.
     If the engine has more to say it says it on the next open, when the cart
     has changed and the ranking with it. */
  /* Show exactly two rows from the window; page for the rest; dots per page. */
  const _warmedThumbs = new Set();
  function renderUpsellPage() {
    if (!upsellTrack) return;
    const order = _upsellOrder;
    let visibleOrder = order.slice(0, _upsellRows);
    if (_upsellError && !visibleOrder.some(function(item) { return item.handle === _upsellError.handle; })) {
      visibleOrder = [_upsellError.item].concat(visibleOrder).slice(0, _upsellRows);
    }
    let html = '';
    visibleOrder.forEach(function(item) { html += renderUpsellRow(item); });
    upsellTrack.innerHTML = html;
    try { noteNudgePainted(visibleOrder); } catch (e) { /* analytics never breaks the rail */ }
    /* The tile paints a 300px photo; the row an upsell add creates paints the
       180px thumb, different bytes. Warm that thumb now, while the shopper is
       still looking at the shelf, so the optimistic row lands with its
       photograph already decoded instead of an empty box for ~300 ms
       (measured live, 2026-09-13: img-loading for 154-309 ms after the tap). */
    if (!(navigator.connection && navigator.connection.saveData)) {
      visibleOrder.forEach(function(item) {
        const p = findProduct(item.handle);
        if (!p || !p.img) return;
        const src = thumb180(p.img);
        if (_warmedThumbs.has(src)) return;
        _warmedThumbs.add(src);
        const warm = new Image(); warm.decoding = 'async'; warm.src = src;
      });
    }
    /* a heading with a rule and no rows under it is a section that failed.
       If nothing survived the ranking, the block leaves rather than lingers. */
    const upsellsEl = document.getElementById('sacrasoul-upsells');
    if (upsellsEl) upsellsEl.style.display = visibleOrder.length ? '' : 'none';
    /* Only the rows the customer can see are checked now. */
    revalidateProductHandles(visibleOrder.map(function(item) { return item.handle; })).catch(function() {});
  }

  /* How many rows fit. A layout READ — see scheduleUpsellPaint for where it is
     allowed to run. The pool's top edge does not depend on the pool's own
     contents, so this answer is stable whether it is asked before or after the
     rows are drawn. */
  function upsellRowsThatFit() {
    try {
      const pool = document.getElementById('sacrasoul-upsells');
      if (!pool || !body) return 2;
      const head = pool.querySelector('.upsells__head');
      const avail = body.getBoundingClientRect().bottom
        - pool.getBoundingClientRect().top
        - (head ? head.offsetHeight + 12 : 32)
        - 34; /* the pool's own vertical padding */
      if (!(avail > 0)) return 2;
      return Math.max(2, Math.min(MAX_UPSELL_ROWS, Math.floor(avail / UPSELL_ROW_H)));
    } catch (e) { return 2; }
  }

  /* Draw the pairing rows on the first task that runs with the drawer already
     on screen — and draw them ONCE, at the measured count.

     They used to be measured and drawn inside renderUpsells, which is reached
     from the microtask an add-to-cart tap leaves behind. Both halves sat on the
     interaction's critical path: the getBoundingClientRect forced a full layout
     of the just-written drawer, and the innerHTML that followed put four rows'
     worth of style, layout and paint into the very frame the customer is
     waiting on. The tap asked for the cart. It did not ask for what pairs with
     the cart, and INP charges it for both.

     rAF alone would not move it — a rAF callback still runs inside the frame
     the interaction is waiting on. rAF -> setTimeout(0) was not enough either:
     MEASURED on the live PDP, that task still landed before the interaction's
     paint in 3 of 5 runs, because on a page this heavy the paint itself is late
     and the deferred draw was one of the things making it late. rAF -> idle,
     with a 400ms ceiling so a permanently busy thread cannot starve it, lands
     after the paint every run. Safari has never shipped requestIdleCallback
     in a stable release, so its fallback must carry the post-paint guarantee
     on its own: a SECOND rAF, then the task. The second rAF callback runs at
     the top of the NEXT frame — by which point the frame the interaction was
     waiting on has presented — and the task it schedules lands after that
     frame's own rendering too. Later than idle would have been, but on the
     right side of the paint, which is the invariant.

     MEASURED, live EN Dragon Blood PDP, throttled phone (390x844 DPR3, 4x CPU),
     interleaved loads, /cart/add.js and /cart.js intercepted:

                            BEFORE          AFTER
       handler processing   med 189ms       med  77ms
       tap -> paint         med 424ms       med 376ms  (p75 448 -> 384)
       rows drawn at        +249..326ms     +515..663ms  (paint is ~+376ms)

     Honest about what is left: the handler's own blocking is more than halved
     and the draw is off the interaction entirely, but the interaction's paint
     is still gated by the drawer's own style + layout + paint on open, which is
     a bigger piece of work than this one and is not touched here. This change
     alone is not expected to carry the page's 744ms p75 field INP under 500ms.

     ONE write, at the measured count. Drawing at the count already in hand and
     correcting afterwards was tried on 2026-08-10 and rejected in review:
     `_upsellRows` carries over from another viewport, another cart or the
     module's initial 2, so the correction pops a row of product in or out under
     the customer's eyes after the drawer is open — the exact jitter the
     2026-07-26 pass removed. The pool ships display:none and stays that way
     until this task, so nothing flashes in the meantime. (2026-08-10) */
  function scheduleUpsellPaint() {
    if (_upsellPaintFrame) return;
    const raf = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : function (fn) { return setTimeout(fn, 16); };
    const yieldToIdle = typeof window.requestIdleCallback === 'function'
      ? function (fn) { window.requestIdleCallback(fn, { timeout: 400 }); }
      : function (fn) { raf(function () { setTimeout(fn, 0); }); };
    _upsellPaintFrame = raf(function () {
      yieldToIdle(function () {
        _upsellPaintFrame = 0;
        /* dropped (drawer closed, order withdrawn) — invalidate the signature
           so the next open renders rather than trusting a paint that never was */
        if (!upsellTrack || !drawer.classList.contains('is-open') || !_upsellOrder.length) {
          _upsellSignature = '';
          return;
        }
        /* a paint queued before the tap must not wipe the tick; the beat's own
           timer asks again */
        if (railAckActive()) { _upsellSignature = ''; return; }
        /* Reveal, THEN measure, THEN draw — all three in this one task, so the
           customer sees them as one paint. A display:none pool answers every
           rect with zero, and upsellRowsThatFit would then measure from the top
           of the viewport instead of from the pool's own top: 4 rows on a strip
           that seats 3 (live Dragon Blood PDP, 2026-08-10). */
        const pool = document.getElementById('sacrasoul-upsells');
        if (pool) pool.style.display = '';
        _upsellRows = Math.min(upsellRowsThatFit(), _upsellOrder.length);
        renderUpsellPage();
      });
    });
  }

  async function renderUpsells(cart) {
    if (!upsellTrack) return;
    const renderEpoch = _paintSeq;
    await loadUpsellCatalog();
    /* the snapshot predates the catalog await — if any tap moved the cart
       meanwhile, derive from the live state instead of painting the past */
    if (renderEpoch !== _paintSeq) {
      if (!state.cart) return;
      cart = cloneCart(state.cart);
    }
    clearLandedUpsellError(cart);
    const upsellsEl = document.getElementById('sacrasoul-upsells');
    /* The free gift is not a signal. A Jade Oil buyer whose gift happened to be
       a resin was being read as a resin buyer: the engine offered charcoal to
       burn it, and that row took one of the two visible slots from the oils and
       the attar that actually follow from what they chose. We recommend against
       what the customer picked, never against what we put in the bag. */
    const cartHandles = cart.items.filter(function(i) { return !hasGiftProperty(i) && !i.__folded; }).map(i => i.handle);
    const order = getUpsellOrder(cartHandles);
    updateUpsellHeading(cartHandles, order);
    if ((!order.length && !_upsellError) || cart.item_count === 0) {
      /* clearing the order is what withdraws a paint already queued for this
         pool — without it, a scheduled draw could re-show what was just hidden */
      _upsellOrder = [];
      _nudgeLive = null;
      upsellTrack.innerHTML = '';
      if (upsellsEl) upsellsEl.style.display = 'none';
      return;
    }
    _upsellOrder = order;
    scheduleUpsellPaint();
  }

  /* backward-compat alias */
  function updateUpsellStates(cart, options) {
    /* The rail used to wait for the money mask to clear, so after an add the
       recommendations arrived a full round trip after the row did (on a cold
       phone, ~4 s after the tap). The rail is derived from which handles are
       in the cart, and the optimistic paint already knows that. */
    if (!drawer.classList.contains('is-open')) return;
    /* a tick is being read at the finger; its own timer re-derives the rail */
    if (railAckActive()) return;
    const signature = getUpsellSignature(cart);
    if (!(options && options.force) && signature === _upsellSignature) return;
    _upsellSignature = signature;
    if (!_catalogReady && !_catalogPromise) {
      scheduleUpsellLoad();
      return;
    }
    scheduleUpsellRender(cart, options && options.immediate ? 0 : 16);
  }

  function clearLandedUpsellError(cart) {
    if (!_upsellError || !cart || !cart.items) return false;
    const landed = cart.items.some(function(item) {
      return !item.__preview && (item.handle === _upsellError.handle || Number(item.variant_id) === Number(_upsellError.vid));
    });
    if (landed) _upsellError = null;
    return landed;
  }

  /* THE TAPPED CARD ANSWERS AT THE FINGER (2026-09-26). Filmed live the day
     before: Róża Otto tapped in the pairing rail, the row already in the
     basket at 100ms — and the rail still offering Róża Otto with its "+" at
     the finger, because the rail re-derives through a 16ms render timer and
     an idle-time paint (scheduleUpsellPaint, kept off the add's critical path
     for INP). On a throttled phone that idle slot comes late, so the card the
     customer touched did not visibly react. Sho: "it didn't show up".

     So the card answers in the tap's own frame: its button turns to a tick
     and says added. The rail holds still for one short beat so the tick can
     be read, then re-derives from the cart and the card makes way. */
  const RAIL_ACK_MS = 700;
  let _railAck = null;
  let _railAckTimer = null;
  function railAckActive() {
    return !!(_railAck && Date.now() < _railAck.until);
  }
  function clearRailAck() {
    clearTimeout(_railAckTimer);
    _railAckTimer = null;
    _railAck = null;
  }
  function acknowledgeRailTap(vid) {
    if (!upsellTrack) return;
    const btn = upsellTrack.querySelector('[data-action="add-upsell"][data-variant-id="' + vid + '"]');
    if (!btn) return;
    const card = btn.closest('.urow, .upsell--set');
    const added = localeText('Added', 'Dodano', 'Přidáno', 'Hinzugefügt', 'Toegevoegd');
    if (card) card.classList.add('is-added');
    btn.classList.add('is-added');
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-label', added);
    if (btn.classList.contains('urow__add')) btn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7"/></svg>';
    else btn.textContent = added;
    clearTimeout(_railAckTimer);
    _railAck = { vid: Number(vid), until: Date.now() + RAIL_ACK_MS };
    _railAckTimer = setTimeout(function() {
      _railAckTimer = null;
      _railAck = null;
      if (state.cart) updateUpsellStates(state.cart, { force: true });
    }, RAIL_ACK_MS);
  }

  /* ═══════════════════════════════════════
     GLOBAL UI UPDATE
  ═══════════════════════════════════════ */
  function applyUIState(cart, prevCart) {
    const count = visibleItemCount(cart);
    /* Remove the last paid line and the gift that line earned leaves with it,
       in the same frame: a cart with nothing paid in it is empty, and says so
       (for ~850ms it once showed a free gift, a blank subtotal and a live
       chooser — a state the customer could neither check out nor keep). */
    const empty = !cart.__pendingUnknown && (count === 0 || cart.items.every(function(i) { return i.__folded || hasGiftProperty(i); }));
    const isOpen = drawer.classList.contains('is-open');
    if (empty) state.selectedGift = -1;
    state.giftInCart = cart.items.some(function(i) { return !i.__folded && hasGiftProperty(i); });

    /* one count everywhere: the header badge, its screen-reader text and this
       line all say Shopify's item_count, gift included — the gift is visible
       in the picker, and three different numbers for one cart read as a bug
       (PL walk, 2026-09-02) */
    if (headerCt) {
      const header = count + ' ' + pluralize(count, 'item');
      if (headerCt.textContent !== header) headerCt.textContent = header;
    }
    /* subtotal — gift lines are free and never counted */
    updateSubtotal(regularTotalCents(cart));
    updateCheckoutBadge();

    /* Drawer closed: only what is visible (bubble, header count, subtotal).
       The rows, the discount lines and the rail wait for the open. */
    if (!isOpen) {
      updateBubbleCount(count + (cart.__pendingUnknown || 0));
      updateSyncUi();
      return;
    }

    /* render cart-level discount allocations (e.g. automatic discounts visible at cart level) */
    if (cartDiscountsJs) {
      /* hide the Liquid-rendered version (stale after AJAX updates) */
      if (cartDiscountsLiquid) cartDiscountsLiquid.style.display = 'none';
      const discApps = (cart.cart_level_discount_applications || []).filter(function(disc) {
        /* only render discounts with valid title and amount */
        return disc && disc.title && disc.total_allocated_amount != null && !isNaN(Number(disc.total_allocated_amount));
      });
      let dhtml = '';
      if (discApps.length > 0 && !empty) {
        discApps.forEach(function(disc) {
          const amount = Number(disc.total_allocated_amount) || 0;
          const isCode = disc.type === 'discount_code';
          /* only the code chip copies — never the remove pill beside it */
          const code = isCode ? discountCodeOf(disc.title) : '';
          dhtml += '<div class="cart-discounts__item">'
            + '<svg viewBox="0 0 12 12"><path d="M3.5 1.5h2.12l4.88 4.88-3.12 3.12L2.5 4.62V2.5a1 1 0 011-1z"/><circle cx="5" cy="4" r=".5"/></svg>'
            + copyChipHtml(code, esc(disc.title) + ' (-' + formatMoney(amount) + ')', 'cart-discounts__code')
            + (isCode ? '<button class="cart-discounts__remove" data-action="remove-discount" aria-label="' + /* DE and NL translated 2026-09-23 */ localeText('Remove code', 'Usuń kod', 'Odstranit kód', 'Code entfernen', 'Code verwijderen') + '"><svg viewBox="0 0 10 10"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" fill="none"/></svg></button>' : '')
            + '</div>';
        });
      }
      /* written only when it changes: a chip being copied keeps its node */
      if (cartDiscountsJs.innerHTML !== dhtml) cartDiscountsJs.innerHTML = dhtml;
      cartDiscountsJs.style.display = dhtml ? '' : 'none';
      /* always show discount toggle — user can add/remove codes freely */
      if (discountWrap) discountWrap.style.display = '';
    }
    syncWelcomeDiscountOffer(cart);

    /* empty state — smooth transition */
    drawer.classList.toggle('cart--empty', empty);
    if (emptyEl) {
      emptyEl.classList.toggle('is-visible', empty);
      emptyEl.setAttribute('aria-hidden', empty ? 'false' : 'true');
    }
    if (footerEl) footerEl.style.display = empty ? 'none' : '';
    if (divider) divider.style.display = empty ? 'none' : '';

    renderCartItems(cart);
    updateJourney(cart);
    updateUpsellStates(cart);
    updateBubbleCount(count + (cart.__pendingUnknown || 0));
    scheduleUndoToastOffset();
    /* a clean paint clears an error — once it has had its readable moment */
    if (state.status.source === 'error' && _statusErrorAt && Date.now() - _statusErrorAt >= STATUS_ERROR_MIN_VISIBLE_MS) {
      clearStatus('error');
    }
    updateSyncUi();
  }

  var _lastSubtotalCents = -1;
  function updateSubtotal(cents) {
    if (cents === _lastSubtotalCents) return;
    _lastSubtotalCents = cents;
    if (subtotalEl) subtotalEl.textContent = formatMoney(cents);
  }

  /* CTA badge shows the payable total — the customer's final confirmation
     of what they're about to pay, right where the finger commits. */
  var _lastBadgeCents = -1;
  function updateCheckoutBadge() {
    if (!ctaBadge) return;
    var cents = state.cart ? regularTotalCents(state.cart) : 0;
    if (cents === _lastBadgeCents) return;
    _lastBadgeCents = cents;
    ctaBadge.textContent = formatMoney(cents);
  }

  /* update any cart count bubbles elsewhere on the page */
  let _lastBubbleCount = -1;
  function updateBubbleCount(count) {
    /* skip entirely if count hasn't changed */
    if (count === _lastBubbleCount) return;
    /* ensure the bubble container exists (Liquid omits it when cart is empty) */
    if (count > 0) {
      const iconBubble = document.getElementById('cart-icon-bubble');
      if (iconBubble && !iconBubble.querySelector('.cart-count-bubble')) {
        const bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true">' + count + '</span><span class="visually-hidden">' + count + ' ' + pluralize(count, 'item') + '</span>';
        iconBubble.appendChild(bubble);
      }
    }
    $$('[data-cart-count], .cart-count-bubble span:not(.visually-hidden), .cart-count').forEach(el => {
      el.textContent = count;
    });
    /* the screen-reader count beside the badge went stale after a quantity
       change (PL walk, 2026-09-02); it follows the same number and noun */
    $$('.cart-count-bubble .visually-hidden').forEach(el => {
      el.textContent = count + ' ' + pluralize(count, 'item');
    });
    /* hide bubble when empty */
    $$('.cart-count-bubble').forEach(el => {
      el.style.display = count > 0 ? '' : 'none';
    });
    /* The badge is confirmation only while the drawer is closed. An open
       drawer already confirms the change with the added/updated row. */
    if (_lastBubbleCount >= 0 && !drawer.classList.contains('is-open')) {
      $$('.cart-count-bubble').forEach(el => {
        el.classList.remove('sacrasoul-pop');
        void el.offsetWidth; /* force reflow for re-trigger */
        el.classList.add('sacrasoul-pop');
      });
    }
    _lastBubbleCount = count;
  }

  /* optimistic badge increment — called immediately before any network request */
  function optimisticBubbleIncrement(qty) {
    const spans = $$('[data-cart-count], .cart-count-bubble span:not(.visually-hidden), .cart-count');
    let current = 0;
    if (spans.length) {
      current = parseInt(spans[0].textContent, 10) || 0;
    }
    updateBubbleCount(current + qty);
  }

  function fadeAndRemoveRow(row) {
    if (!row) return Promise.resolve();
    const target = row.closest('.item-swipe') || row;
    const children = Array.from(itemsWrap ? itemsWrap.children : []);
    const targetIndex = children.indexOf(target);
    const previous = target.previousElementSibling;
    const next = target.nextElementSibling;
    const separator = previous && previous.classList.contains('item-sep')
      ? previous
      : (next && next.classList.contains('item-sep') ? next : null);
    const movers = targetIndex >= 0
      ? children.slice(targetIndex + 1).filter(function(el) { return el !== separator; })
      : [];
    const before = new Map();
    movers.forEach(function(el) { before.set(el, el.getBoundingClientRect().top); });

    target.classList.add('is-removing');
    return new Promise(function(resolve) {
      setTimeout(function() {
        if (!target.isConnected) { resolve(); return; }
        if (separator && separator.isConnected) separator.remove();
        target.remove();

        if (!_reducedMotion) {
          const moved = [];
          movers.forEach(function(el) {
            if (!el.isConnected) return;
            const delta = before.get(el) - el.getBoundingClientRect().top;
            if (!delta) return;
            el.style.transition = 'none';
            el.style.transform = 'translateY(' + delta + 'px)';
            moved.push(el);
          });
          if (moved.length) {
            requestAnimationFrame(function() {
              requestAnimationFrame(function() {
                moved.forEach(function(el) {
                  el.style.transition = 'transform ' + DUR_TRANSITION + 'ms var(--ss-ease-out)';
                  el.style.transform = '';
                });
                setTimeout(function() {
                  moved.forEach(function(el) {
                    if (el.isConnected) {
                      el.style.transition = '';
                      el.style.transform = '';
                    }
                  });
                }, DUR_TRANSITION);
              });
            });
          }
        }
        resolve();
      }, _reducedMotion ? 0 : DUR_FEEDBACK);
    });
  }

  function undoLabel() {
    const fromToast = undoBtn && undoBtn.textContent && undoBtn.textContent.trim();
    return fromToast || localeText('Undo', 'Cofnij', 'Zpět', 'Rückgängig', 'Ongedaan maken');
  }

  /** Tween the swipe slot between two heights so the fold and the restore read
      as one movement instead of a jump. No-op under reduced motion. */
  function tweenSlotHeight(swipe, fromHeight) {
    if (_reducedMotion || !swipe || !swipe.isConnected || !fromHeight) return;
    const toHeight = swipe.getBoundingClientRect().height;
    if (Math.abs(toHeight - fromHeight) < 1) return;
    swipe.style.height = fromHeight + 'px';
    void swipe.offsetHeight;
    swipe.style.transition = 'height ' + DUR_TRANSITION + 'ms var(--ss-ease-out)';
    swipe.style.height = toHeight + 'px';
    setTimeout(function() {
      if (!swipe.isConnected) return;
      swipe.style.height = '';
      swipe.style.transition = '';
    }, DUR_TRANSITION + 30);
  }

  function showUndoToast(title) {
    if (!undoToast) return;
    clearUndoTimer();
    if (_undoHideTimer) {
      clearTimeout(_undoHideTimer);
      _undoHideTimer = null;
    }
    scheduleUndoToastOffset();
    if (undoMsg) undoMsg.textContent = removedFromCartText(title);
    undoToast.hidden = false;
    undoToast.removeAttribute('inert');
    requestAnimationFrame(function() {
      if (!undoToast.hidden) undoToast.classList.add('is-visible');
    });
    announce(CFG.strings.removedUndoAvailable);
    if (undoProg) {
      undoProg.style.transition = 'none';
      undoProg.style.transform = 'scaleX(1)';
      requestAnimationFrame(() => {
        undoProg.style.transition = 'transform 5s linear';
        undoProg.style.transform = 'scaleX(0)';
      });
    }
    state.undoTimer = setTimeout(() => {
      state.undoTimer = null;
      hideUndoToast();
      state.undoData = null;
    }, 5000);
  }

  function hideUndoToast() {
    if (!undoToast) return;
    undoToast.classList.remove('is-visible');
    undoToast.setAttribute('inert', '');
    if (_undoHideTimer) clearTimeout(_undoHideTimer);
    _undoHideTimer = setTimeout(function() {
      _undoHideTimer = null;
      if (!undoToast.classList.contains('is-visible')) undoToast.hidden = true;
    }, DUR_TRANSITION);
  }

  function clearUndoTimer() {
    if (state.undoTimer) { clearTimeout(state.undoTimer); state.undoTimer = null; }
    hideUndoToast();
  }

  /* ═══════════════════════════════════════
     GIFT CHOOSER
  ═══════════════════════════════════════ */
  function syncGiftSelectionUi(focusIndex) {
    if (!giftPick) return;
    const options = $$('.gopt', giftPick);
    const visible = options.filter(function(opt) { return !opt.hidden; });
    let roving = visible.find(function(opt) {
      const choice = opt.getAttribute('data-gift2-choice');
      return choice ? choice === state.selectedGift2
        : parseInt(opt.dataset.giftIndex, 10) === state.selectedGift;
    }) || visible.find(function(opt) {
      return parseInt(opt.dataset.giftIndex, 10) === focusIndex;
    }) || visible[0];
    options.forEach(function(opt) {
      /* A tier-2 chip answers to the tier-2 choice, not to the tier-1 index —
         the "both tier-1 gifts" chip has no variant of its own to be indexed by. */
      const choice = opt.getAttribute('data-gift2-choice');
      const chosen = choice
        ? choice === state.selectedGift2
        : parseInt(opt.dataset.giftIndex, 10) === state.selectedGift;
      opt.classList.toggle('is-chosen', chosen);
      opt.setAttribute('aria-checked', chosen ? 'true' : 'false');
      opt.tabIndex = opt === roving ? 0 : -1;
    });
  }

  /* ── Gift availability (live) ── */
  function giftIsAvailable(index) {
    if (index == null || index < 0) return false;
    const info = CFG.giftProducts ? CFG.giftProducts[index] : null;
    if (info && info.available === false) return false;
    const gv = CFG.giftVariants ? CFG.giftVariants[index] : null;
    if (gv == null) return false;
    return _availabilityCache.get(Number(gv)) !== false;
  }

  /* The first tier-1 chip that can still be given. Deliberately blind to the
     tier-2 slot: a tier-1 default that fell through to the tier-2 gift would
     hand out the bigger reward for the smaller basket. */
  function firstAvailableTier1Gift() {
    for (var i = 0; i < TIER1_SLOTS; i++) {
      var gv = CFG.giftVariants ? CFG.giftVariants[i] : null;
      if (gv != null && giftIsAvailable(i)) return i;
    }
    return -1;
  }

  function anyGiftAvailable() {
    return firstAvailableTier1Gift() >= 0;
  }

  /* Hide gift tiles that sold out after the page was rendered; keep the
     grid columns honest; release a selection pointing at a dead gift.
     The gift already IN the customer's cart keeps its tile — they own it,
     and it anchors the "Your gift" section — even if new stock ran out. */
  /* Has the basket earned tier 2? Read from the paid lines only, like every
     other reward question in this file. */
  function giftTier2Earned() {
    if (!tier2Configured() || !state.cart) return false;
    return regularTotalCents(state.cart) / 100 >= Number(CFG.thresholdGift2);
  }

  let _lastGiftVisibleCount = -1;
  function syncGiftAvailabilityUi() {
    if (!giftPick) return;
    /* Which tier the grid is showing. The tiers REPLACE each other: above tier 2
       the tier-1 chips step aside for the tier-2 pair, because a basket over
       tier 2 gets the tier-2 reward instead of the tier-1 one, not as well. */
    const tier2 = giftTier2Earned();
    const amberBlocked = tier2Configured() && giftTier2AmberPaidInCart(state.cart);
    let visible = 0;
    $$('.gopt', giftPick).forEach(function(opt) {
      const tier = opt.getAttribute('data-gift-tier');
      const choice = opt.getAttribute('data-gift2-choice');
      let ok;
      if (tier === '2') {
        if (choice === 'amber') ok = tier2 && !amberBlocked && giftIsAvailable(GIFT2_INDEX);
        else ok = tier2 && tier1GiftVids().length > 1;
      } else if (tier2) {
        ok = false;
      } else {
        const idx = parseInt(opt.dataset.giftIndex, 10);
        const inCart = state.giftInCart && state.selectedGift === idx;
        ok = inCart || giftIsAvailable(idx);
      }
      opt.hidden = !ok;
      if (ok) visible++;
    });
    /* Withheld Amber leaves the pair as the only tier-2 choice; say so in state,
       not only on screen, or the entitlement would still be trying to give Amber. */
    if (tier2 && amberBlocked) state.selectedGift2 = 'both';
    const grid = $('.gift-pick__grid', giftPick);
    if (grid) {
      grid.classList.toggle('gift-pick__grid--cols-2', visible === 2);
      grid.classList.toggle('gift-pick__grid--cols-1', visible === 1);
    }
    if (!visible) giftPick.classList.remove('is-open');
    if (state.selectedGift >= 0 && !state.giftInCart && !giftIsAvailable(state.selectedGift)) {
      state.selectedGift = -1;
    }
    syncGiftTier2Hint();
    syncGiftSelectionUi();
    /* availability changed what the drawer can promise — recompute the
       journey message and gift chooser past their memo guards */
    if (visible !== _lastGiftVisibleCount) {
      _lastGiftVisibleCount = visible;
      _lastJourneyPct = -1;
      if (drawer.classList.contains('is-open') && state.cart) updateJourney(state.cart);
    }
  }

  /* The locked hint. Between the two tiers it names what the next milestone
     buys. It is a sentence, not a chip: nothing here can be chosen yet, and a
     disabled-looking chip reads as a bug rather than as an invitation. */
  function syncGiftTier2Hint() {
    if (!giftHint) return;
    const paid = state.cart ? regularTotalCents(state.cart) / 100 : 0;
    const show = tier2Configured() && paid >= Number(CFG.thresholdGift) && !giftTier2Earned();
    giftHint.hidden = !show;
    if (!show) return;
    const gap = Math.max(Number(CFG.thresholdGift2) - paid, 0);
    const text = String(CFG.strings.giftTier2Hint || '').replace('{amount}', formatThresholdAmount(gap));
    if (giftHint.textContent !== text) giftHint.textContent = text;
  }

  /* Choosing between the two tier-2 rewards. It is a different question from the
     tier-1 chips — one tier-2 gift, or the two tier-1 gifts together — so it has
     its own state. The cart is then reconciled to whatever the new answer entitles:

  /* ═══════════════════════════════════════
     ADD & OPEN — the row at the tap, the write behind it
  ═══════════════════════════════════════ */
  function addAndOpen(variantId, qty, properties, previewItem, sellingPlan, opts) {
    if (_destroyed) return Promise.reject(new Error('cart engine destroyed'));
    qty = qty || 1;
    /* Never let a customer buy control add a £0 gift variant (owner rule
       2026-07-24): the reward variants exist only as the cart's free lines,
       added by the gift sync — which never routes through here. */
    if (isGiftVariantId(variantId)) {
      if (window.console && console.warn) console.warn('[SacrasoulCart] blocked a customer add of gift variant', variantId);
      return Promise.resolve(state.cart);
    }
    /* Explicit null means the producer could not capture honest product data:
       the drawer shows a pending row, never a guessed or stale one (the
       2026-07-23 walk: "empty-state + stale enabled checkout ~1.45s").
       Undefined is the public convenience path and builds from the page. */
    if (previewItem === undefined) previewItem = buildPreviewItem(null, variantId, qty, properties, sellingPlan);
    if (previewItem && sellingPlan && !sellingPlanId(previewItem)) {
      previewItem.selling_plan = sellingPlan;
      previewItem.selling_plan_allocation = {
        selling_plan: {
          id: Number(sellingPlan) || sellingPlan,
          name: sellingPlanName(null, sellingPlan),
          months: sellingPlanMonths(sellingPlan)
        }
      };
    }
    clearStatus('error');
    const source = /\/products\//.test(window.location.pathname) ? 'product_atc' : 'quick_add';
    const it = addIntent({ vid: parseInt(variantId, 10), qty: qty, props: properties, plan: sellingPlan, preview: previewItem || null, source: source });
    try {
      if (previewItem) {
        const ident = identityKey(lineIdentity(previewItem));
        const existing = state.cart && findLine(state.cart, lineIdentity(previewItem));
        if (existing) _rollHint = { ident: ident, dir: 1 };
        else _justAdded.add(ident);
      }
      /* Hybrid add feedback (Sho, 2026-09-12): a quick-add tile outside the
         PDP hero never owns the whole drawer. */
      if (opts && opts.quiet) {
        paint();
        try {
          document.dispatchEvent(new CustomEvent('sacrasoul:quick-add', { detail: { origin: opts.origin || null } }));
        } catch (_) {}
      } else {
        openDrawer({ forceLoading: !previewItem && !visibleItemCount(state.cart), fromAdd: true });
      }
      _rollHint = null;
    } catch (paintError) {
      console.error('[SacrasoulCart] preview paint failed', paintError); reportCartError('paint', paintError, { fatal: false });
    }
    return it.promise;
  }

  /* ═══════════════════════════════════════
     ADD UPSELL
  ═══════════════════════════════════════ */
  /* build a cart-like preview item from upsell catalog data */
  function buildUpsellPreview(vid, productData) {
    if (!productData) return null;
    return normalizeCartItem({
      id: vid,
      variant_id: vid,
      key: 'preview:' + (++_previewId),
      quantity: 1,
      properties: {},
      product_title: productData.title,
      /* the size line ("25 g"), so a row added from the rail reads like its
         neighbours from the first frame (catalog "meta", 2026-09-25) */
      variant_title: productData.meta || null,
      title: productData.title,
      handle: productData.handle || '',
      url: productData.url || '/products/' + (productData.handle || ''),
      product_type: productData.type || '',
      featured_image: productData.img ? { url: productData.img, alt: productData.title } : null,
      image: productData.img || '',
      price: productData.price,
      final_price: productData.price,
      /* Shopify cart original_price means pre-line-discount price, not a
         product compare-at or a bundle's bought-separately value. The preview
         must agree with the settled cart line or a false strike flashes. */
      original_price: productData.price,
      line_price: productData.price,
      final_line_price: productData.price,
      original_line_price: productData.price,
      line_level_discount_allocations: [],
      __preview: true
    });
  }

  /* The rail's whole verdict rule, in one place so a test can hold it still.
     addInventoryVerdict decides; the settle's own committed post-refusal
     /cart.js read (or null) is the only admissible holdings evidence. With no
     settled read, EVERY holdings-dependent verdict clears: a cap read off a
     stale held=true would show cap copy for a true sellout, exactly as a
     sold_out read off a stale held=false would session-kill a cap. The one
     verdict that stands alone is a sellout confirmed by fresh availability
     === false, which never consulted holdings. */
  function railInventoryVerdict(e, healChecked, vid, healAvailability, settledCart) {
    /* A settle that is not a readable cart is no settle at all. */
    const settled = settledCart && Array.isArray(settledCart.items) ? settledCart : null;
    let verdict = addInventoryVerdict(e, healChecked, vid, healAvailability, settled || state.cart);
    if (!settled && verdict && !(verdict === 'sold_out' && healAvailability === false)) {
      verdict = null;
    }
    return verdict;
  }

  function addUpsell(variantId) {
    /* Same £0-gift backstop as the main add path (owner rule 2026-07-24). */
    if (isGiftVariantId(variantId)) {
      if (window.console && console.warn) console.warn('[SacrasoulCart] blocked an upsell add of gift variant', variantId);
      return Promise.resolve(state.cart);
    }
    const vid = parseInt(variantId, 10);
    const pData = Object.values(_upsellProducts).find(function(p) { return p.vid === vid; })
      || (_upsellError && _upsellError.vid === vid ? _upsellError.product : null);
    /* a definitive sellout retires the handle for the session */
    if (pData && _unavailableUpsellHandles.has(pData.handle)) return Promise.resolve(state.cart);
    /* a second tap on a row already painted, or already on its way, is the same ask */
    if (state.cart && state.cart.items.some(function(i) { return Number(i.variant_id) === vid && !i.__folded; })) return Promise.resolve(state.cart);
    if (_intents.some(function(i) { return i.kind === 'add' && Number(i.vid) === vid; })) return Promise.resolve(state.cart);
    if (_upsellError && _upsellError.vid === vid) _upsellError = null;
    clearStatus('error');
    const nudge = _nudgeLive && _nudgeLive.vid === vid ? Object.assign({}, _nudgeLive) : null;
    try { acknowledgeRailTap(vid); } catch (ackError) { /* presentation never blocks the add */ }
    const preview = buildUpsellPreview(vid, pData);
    const it = addIntent({ vid: vid, qty: 1, preview: preview, source: 'upsell', pData: pData, nudge: nudge });
    if (preview) _justAdded.add(identityKey(lineIdentity(preview)));
    paint();
    announce(CFG.strings.adding);
    return it.promise;
  }

  /* ═══════════════════════════════════════
     AVAILABILITY + INVENTORY — flags items as unavailable (out of stock or
     archived) and stores live inventory so the drawer can surface "Only N
     left" urgency. Reuses the same /products.js fetch, so no extra network.
  ═══════════════════════════════════════ */
  const _availabilityCache = new Map();  /* variant_id → boolean available */
  const _inventoryCache = new Map();     /* variant_id → { qty, managed, allowOversell } */

  async function checkAvailability(cart) {
    if (!cart || !cart.items.length) return;
    /* find variant ids we don't have in the cache */
    const toCheck = new Map();  /* handle → [variant_ids] */
    cart.items.forEach(function(item) {
      if (isGiftItem(item)) return;
      if (_availabilityCache.has(item.variant_id)) return;
      if (!item.handle) return;
      if (!toCheck.has(item.handle)) toCheck.set(item.handle, []);
      toCheck.get(item.handle).push(item.variant_id);
    });
    if (!toCheck.size) {
      applyAvailabilityFlags();
      return;
    }
    /* concurrent calls are safe: Map.set is idempotent, and applyAvailabilityFlags
       always reads state.cart so it can't mutate a stale reference replaced by a
       parallel updateUI during the await. */
    try {
      const handles = [...toCheck.keys()];
      const results = await Promise.allSettled(
        handles.map(function(h) { return fetchProductByHandle(h); })
      );
      results.forEach(function(r) {
        if (r.status !== 'fulfilled' || !r.value || !r.value.variants) return;
        r.value.variants.forEach(function(v) {
          _availabilityCache.set(v.id, !!v.available);
          _inventoryCache.set(v.id, {
            qty: typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null,
            managed: v.inventory_management === 'shopify',
            allowOversell: v.inventory_policy === 'continue'
          });
        });
      });
    } catch (e) { /* silent — availability is a hint, not blocking */ }
    applyAvailabilityFlags();
  }

  /* A line waits for the pre-order batch if the page said so at render
     (CFG.preorderHandles — right for everything present at load) OR live
     inventory says so now (tracked, oversell allowed, none on hand) — which
     catches a pre-order product added client-side after load, which the
     render-time list can never contain. */
  function lineIsPreorder(item) {
    if (!item || isGiftItem(item)) return false;
    if (CFG.preorderHandles && item.handle && CFG.preorderHandles.indexOf(item.handle) >= 0) return true;
    /* Inventory fallback is best-effort only: Shopify's storefront product
       JSON exposes neither inventory_quantity nor inventory_policy, so this
       almost never has data — the render-time store-wide metafield list in
       CFG.preorderHandles is the real signal (verified live 2026-07-30 when
       a client-side add showed no badge and a false arrival date). */
    var inv = _inventoryCache.get(item.variant_id);
    return !!(inv && inv.managed && inv.allowOversell && typeof inv.qty === 'number' && inv.qty <= 0);
  }

  /* A line "waits for the batch" if it IS the pre-order product, or if it is
     a bundle/set that CONTAINS one (First Flame, Trinity, ...). The line-item
     badge (dispatchNoteForLine, above) already reasons this way — a bundle
     holding the Terracotta Burner shows "Dispatches from {date}" on its own
     row. The footer arrival estimate must agree with the row it sits under:
     before this, a cart holding ONLY a pre-order bundle read as
     cartAllPreorder() === false (the bundle's own handle is never in
     CFG.preorderHandles, only its ingredient handle is), so the footer fell
     through to the fast same-day/next-day estimate and promised arrival
     before, or on, the dispatch date it had just printed on the line above
     it (found live 2026-09-12, PL reviewer, First Flame). */
  function lineWaitsForBatch(item) {
    return lineIsPreorder(item) || lineContainsPreorderBurner(item);
  }

  /* True only when every PAID line waits for the batch. One in-stock line
     keeps the mixed-cart ruling: the fast date speaks, badges carry the
     exceptions. */
  function cartAllPreorder() {
    var cart = state.cart;
    if (!cart || !cart.items || !cart.items.length) return false;
    var sawReal = false;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftItem(it)) continue;
      sawReal = true;
      if (!lineWaitsForBatch(it)) return false;
    }
    return sawReal;
  }

  /* A mixed cart (one line waits for the batch, another is on the shelf) ships
     as one parcel, so the footer must not promise the shelf item's fast date
     while the row above it says "Dispatches from {date}". Any waiting line
     makes the whole order a batch order. (2026-09-12) */
  function cartAnyWaitsForBatch() {
    var cart = state.cart;
    if (!cart || !cart.items || !cart.items.length) return false;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftItem(it)) continue;
      if (lineWaitsForBatch(it)) return true;
    }
    return false;
  }

  var _lastAllPreorder = null;
  function refreshDispatchIfPreorderChanged() {
    var ap = cartAllPreorder();
    if (ap !== _lastAllPreorder) {
      _lastAllPreorder = ap;
      updateDispatch();
    }
  }

  function stockLimitFor(item) {
    if (!item || isGiftItem(item)) return null;
    const inv = _inventoryCache.get(item.variant_id);
    if (!inv || !inv.managed || inv.allowOversell) return null;
    if (typeof inv.qty !== 'number' || inv.qty <= 0) return null;
    return inv.qty;
  }

  /* Compute the low-stock count to show on a cart line, or null if no
     urgency should appear. Truthful: only fires for shopify-tracked
     variants that don't oversell. Threshold is configurable per section. */
  function lowStockCountFor(item) {
    if (!CFG.showStockUrgency) return null;
    const stockLimit = stockLimitFor(item);
    if (stockLimit == null) return null;
    const threshold = Number(CFG.stockUrgencyThreshold) || 5;
    if (stockLimit > threshold) return null;
    return stockLimit;
  }

  /* Availability and stock read from the caches at every paint; a change in
     what they say re-draws the rows in place. */
  function applyAvailabilityFlags() {
    paint();
    refreshDispatchIfPreorderChanged();
  }

  /* ── Live catalog revalidation ──
     The Liquid-rendered upsell catalog and gift tiles are availability-
     filtered only at page render time. Cached pages (bfcache, CDN, an
     open tab) can therefore recommend products that sold out afterwards —
     tapping those returns 422 and nothing enters the cart. These helpers
     re-check the live store so the drawer always offers what it can
     actually deliver, however stale the page HTML is. */

  /* Keep the Liquid-provided gift flags honest in both directions so a
     temporary sellout can't hide a restocked gift for the whole session. */
  function syncGiftFlagsForHandle(handle, available) {
    (CFG.giftProducts || []).forEach(function(g) {
      if (g && g.handle === handle) g.available = available;
    });
  }

  /* Fetch live product state; refresh the availability/inventory caches, the
     upsell catalog entry (price, current variant) and gift flags. Returns
     { vid, sole } for the first available variant (sole = single-variant
     product), or null when the product can't be sold right now. Network
     failures change nothing and return null. */
  async function freshProductState(handle, options) {
    if (!handle) return null;
    let product = null;
    try {
      product = await fetchProductByHandle(handle, options);
    } catch (e) {
      /* 404 = unpublished/deleted — definitively gone */
      if (Number(e && e.status) === 404) {
        delete _upsellProducts[handle];
        syncGiftFlagsForHandle(handle, false);
      }
      return null;
    }
    if (!product) return null;

    /* A 200 is not by itself a product answer. A proxy page, an app's redirect,
       a locale root that does not resolve — each can hand back a body with no
       `variants` key at all, and "no available variant" read off that body is a
       verdict the shop never gave. It retires the upsell row and flips the gift
       product's flag to sold out, and now that every configured gift handle is
       re-checked on open rather than one, a single bad response could take the
       whole gift programme off for the rest of the session. Only a body that
       actually lists variants may speak about this product; a real product
       always lists at least one. Saying nothing is the same answer a network
       failure gets above, and the same one the caller already handles. */
    const listed = Array.isArray(product.variants) ? product.variants : null;
    if (!listed || !listed.length) return null;

    listed.forEach(function(v) {
      _availabilityCache.set(v.id, !!v.available);
      _inventoryCache.set(v.id, {
        qty: typeof v.inventory_quantity === 'number' ? v.inventory_quantity : null,
        managed: v.inventory_management === 'shopify',
        allowOversell: v.inventory_policy === 'continue'
      });
    });

    /* Which variants THIS fetch actually answered about. _availabilityCache is
       a long-lived Map keyed by variant id, so "it has an entry" is not the same
       claim as "the live product still lists it" — a variant deleted or
       unpublished mid-session keeps whatever an earlier fetch wrote. Readers
       that turn availability into a customer-facing verdict need the stronger
       claim, and only the response can make it. */
    const ids = listed.map(function(v) { return Number(v.id); });

    /* A configured gift variant that THIS response did not list is gone —
       deleted in admin, unpublished, or a settings id that never belonged to
       this product. The loop above can only speak for variants the response
       carries, so a dead id keeps whatever an earlier fetch wrote, which for an
       id the shop has never listed is nothing at all; giftIsAvailable then reads
       `undefined !== false` as "offer it" and the drawer posts an id Shopify
       answers 422 to. The product-level flag below cannot catch it: the gift is
       one £0 variant of a product whose paid variants keep selling, so
       syncGiftFlagsForHandle says "available" about the parent and nothing about
       the gift. Same rule addInventoryVerdict already keeps for cart lines —
       only the response may speak, and it just did. Sticky only until a restock:
       the loop above writes `true` back the moment the id is listed again. */
    (CFG.giftVariants || []).forEach(function(gv, i) {
      if (gv == null) return;
      const giftInfo = CFG.giftProducts ? CFG.giftProducts[i] : null;
      if (!giftInfo || giftInfo.handle !== handle) return;
      if (ids.indexOf(Number(gv)) < 0) _availabilityCache.set(Number(gv), false);
    });

    const live = listed.find(function(v) { return v.available; });
    syncGiftFlagsForHandle(handle, !!live);
    if (!live) {
      /* nothing sellable — stop recommending it */
      delete _upsellProducts[handle];
      return null;
    }
    const entry = _upsellProducts[handle];
    if (entry) {
      entry.vid = live.id;
      entry.price = live.price;
      entry.compareAt = live.compare_at_price || 0;
    } else if (ALL_HANDLES.indexOf(handle) >= 0) {
      /* restocked since the page was rendered — bring it back */
      _upsellProducts[handle] = {
        handle: product.handle, title: product.title, type: product.type || '',
        vid: live.id, price: live.price,
        compareAt: live.compare_at_price || 0,
        componentValue: 0,
        img: product.featured_image ? product.featured_image.replace(/(\.[a-z]+)(\?|$)/, '_300x$1$2') : '',
        url: product.url
      };
    }
    return { vid: live.id, sole: listed.length === 1, ids: ids };
  }

  function catalogSignature() {
    return Object.keys(_upsellProducts).sort().map(function(h) {
      const p = _upsellProducts[h];
      return h + ':' + p.vid + ':' + p.price + ':' + (p.compareAt || 0) + ':' + (p.componentValue || 0);
    }).join('|');
  }

  /* EVERY configured gift handle, not just the one the chooser would offer next.
     Naming a single handle made this check blind in exactly the direction it
     exists to cover. The handle it picked was the first slot whose flag was not
     already false — so the instant that slot was retired the chooser fell
     through to slot n+1, a slot no response had ever spoken about, and
     giftIsAvailable reads an id it has never heard of as offerable. The
     fallthrough gift was therefore posted UNVALIDATED: one settings rebuild
     that rotted several PLN 0 variants at once paid a 422 per slot, in order,
     each one a customer-visible "that gift just sold out" over a free line the
     shopper had already been shown.

     There are at most four handles, they share the one-minute product cache
     with the cart's own lines and the visible upsell rows (a gift product is
     usually one of those anyway), and probing all of them also means a restock
     in ANY slot is seen on the next open — which the single-handle version only
     managed for the default. */
  function giftHandlesForRevalidation() {
    const handles = [];
    const want = function(handle) {
      if (handle && handles.indexOf(handle) < 0) handles.push(handle);
    };
    /* The gift already in the cart first: it is the one the shopper owns. */
    (state.cart && state.cart.items || []).forEach(function(item) {
      if (hasGiftProperty(item)) want(item.handle);
    });
    (CFG.giftProducts || []).forEach(function(gift) { if (gift) want(gift.handle); });
    return handles;
  }

  function visibleUpsellHandles(cart) {
    if (!upsellTrack || !cart || !cart.item_count || !cart.items) return [];
    const cartHandles = cart.items.filter(function(item) { return !hasGiftProperty(item); }).map(function(item) { return item.handle; });
    const order = getUpsellOrder(cartHandles);
    return order.slice(0, 2).map(function(item) { return item.handle; });
  }

  function revalidateProductHandles(handles) {
    const uniqueHandles = new Set((handles || []).filter(Boolean));
    if (!uniqueHandles.size) return Promise.resolve(false);
    const before = catalogSignature();
    return Promise.allSettled([...uniqueHandles].map(function(h) { return freshProductState(h); })).then(function() {
      syncGiftAvailabilityUi();
      applyAvailabilityFlags();
      const changed = catalogSignature() !== before;
      if (changed && drawer.classList.contains('is-open') && state.cart) {
        updateUpsellStates(state.cart, { force: true });
      }
      return changed;
    });
  }

  /* Drawer-open truth is deliberately scoped to what can affect the current
     decision: cart lines, every configured gift (see giftHandlesForRevalidation
     for why it is all of them and not just the next one offered) and the two
     visible upsell rows. The shared one-minute cache makes reopen cheap;
     renderUpsellPage checks each later carousel pair when it is first shown. */
  function revalidateCatalog() {
    return loadUpsellCatalog().then(function() {
      const handles = new Set();
      const cart = state.cart;
      (cart && cart.items || []).forEach(function(item) {
        if (item.handle) handles.add(item.handle);
      });
      giftHandlesForRevalidation().forEach(function(handle) { handles.add(handle); });
      visibleUpsellHandles(cart).forEach(function(handle) { handles.add(handle); });
      return revalidateProductHandles([...handles]);
    });
  }

  /* ═══════════════════════════════════════
     DISPATCH TIMER
  ═══════════════════════════════════════ */

  /* Working days, not calendar days. The transit window the rate table
     publishes is counted in WORKING days, so adding calendar days to a ship
     date shortens every promise it makes — a 5-working-day window landing on a
     Thursday would be printed as the following Tuesday instead of the Thursday
     after. One helper serves both legs: the dispatch hop (always one working
     day) and the transit. */
  function addWorkingDays(date, days) {
    var result = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      var d = result.getDay();
      if (d !== 0 && d !== 6) added++;
    }
    return result;
  }

  function updateDispatch() {
    if (!dispatchMsg) return;
    /* A pre-order line does NOT set the arrival for the whole cart. Doing that
       took the slowest item's date and applied it to everything, so three
       items that are ready were dragged down by one that is not — a promise
       that under-sells the order and persuades nobody.
       The general line keeps the real, fast date for what is in stock. The
       exception is attached to the line it belongs to, as a badge on that row,
       where the customer is already looking at the thing that waits. Two
       facts, each in its own place, and neither one lying. */

    /* THE TRANSIT WINDOW IS NOT WRITTEN HERE (2026-09-17).
       Until today this function carried a SHIPPING_DAYS table in calendar days,
       with a comment claiming Sacrasoul ships from Poland "and California (US
       fulfilment)" and a US row computed on the California-side route. Sho
       retired that premise in #1992: for now every order ships from Olsztyn and
       the American warehouse is not open. The table disagreed with the live
       delivery profile on every one of the 31 countries we ship to — US 3-6
       against 4-6, CA 4-8 against 5-7, GB 4-7 against 3-5, DE/CZ/SK a flat 3
       against 2-4, CH 4-5 against 5-7, BG/GR 4-5 against 5-8 — and it quoted
       arrival to Oman, Australia and Brazil, where checkout answers
       "Country/region not supported".
       CFG.transitMin/Max now come from snippets/shipping-rate-value.liquid via
       snippets/cart-drawer.liquid, in working days, for CFG.transitCountry. */
    var country = String(CFG.country || '').toUpperCase();
    var transitCountry = String(CFG.transitCountry || '').toUpperCase();
    var maxDays = Number(CFG.transitMax);
    /* No window, or a window rendered for a different country than the one the
       drawer settled on (a market switch heals CFG.country from
       window.Shopify.country after this page was rendered): say nothing. The
       whole assurance column goes, rather than an icon standing beside a blank. */
    if (!isFinite(maxDays) || maxDays <= 0 || !country || country !== transitCountry) {
      var etaCell = dispatchMsg.closest ? dispatchMsg.closest('[data-assurance-eta]') : null;
      if (etaCell) {
        etaCell.setAttribute('hidden', '');
        /* the row is a three-track grid; it has to re-count or the two
           survivors leave a third of the width empty beside them */
        if (etaCell.parentNode && etaCell.parentNode.classList) {
          etaCell.parentNode.classList.add('assurance--no-eta');
        }
      } else {
        dispatchMsg.textContent = '';
      }
      return;
    }

    /* Always use Polish timezone — store ships from Poland (3PL in Olsztyn). */
    var now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Warsaw' }));
    var isPl = CFG.locale === 'pl';
    var isCs = CFG.locale === 'cs';
    var isDe = CFG.locale === 'de';
    var isNl = CFG.locale === 'nl';

    /* DISPATCH IS THE NEXT WORKING DAY, ALWAYS.
       This used to ship same-day before a 15:00 Warsaw cut-off, which put every
       arrival date it printed one working day early and contradicted the
       shipping page ("Orders leave Olsztyn the next working day", #1992). The
       cut-off setting is gone with it, and so is the same-day countdown pill it
       drove: a countdown to a deadline that changes nothing is a lie with a
       timer on it. Friday and the weekend dispatch Monday; Monday dispatches
       Tuesday. */
    var shipDate = addWorkingDays(now, 1);

    /* An all-pre-order cart has exactly one honest promise: the batch date.
       The mixed-cart ruling above stands (the footer speaks for the soonest
       item that can reach them) — but when EVERY paid line waits for the
       batch, the fast date is a lie about the whole order.
       SEMANTICS: the pre-order date setting was read as the date the batch
       ARRIVES in Poland (owner correction, 2026-07-30) until that was reversed
       on 2026-09-12 — it is the day the batch LEAVES Olsztyn, which is what the
       row above it says ("Dispatches from {date}"). Arrival is that day plus
       this market's full transit, for every market including PL. */
    var allPreorderArrival = null;
    if (CFG.preorderShipIso && cartAnyWaitsForBatch()) {
      var poDate = new Date(String(CFG.preorderShipIso) + 'T12:00:00');
      /* A batch date already behind us (local calendar) is a stale setting —
         the batch has shipped and the normal fast path speaks again. Only a
         live batch date suppresses it. */
      var todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
      if (!isNaN(poDate.getTime()) && poDate.getTime() >= todayMid.getTime()) {
        allPreorderArrival = poDate;
      }
    }

    /* One date, not a spread. "Arrives Jul 31–Aug 3" made the promise read
       broad and vague (the owner blamed a slow product in the cart; it was
       only the carrier window talking). A single upper bound is a promise
       that can be kept and read in one glance — under-promise, over-deliver.
       (owner direction, 2026-07-26) */
    var latest;
    if (allPreorderArrival) {
      /* The batch date is the day the parcel LEAVES Olsztyn (the row above
         says "Dispatches from {date}"), so arrival is batch date + this
         market's full transit. Before 2026-09-12 PL was promised the batch
         date itself, which put arrival on the dispatch day.
         There is no North America exception any more: US and Canada leave
         Olsztyn with everything else, so the Poland-batch arithmetic is
         theirs too (#1992). */
      latest = addWorkingDays(allPreorderArrival, maxDays);
      var fastLatest = addWorkingDays(shipDate, maxDays);
      /* A batch promise can never beat the fast path: resin that is not in
         the country cannot arrive sooner than goods already on the shelf.
         This also covers the batch date's final days, when the raw batch
         promise would undercut what the next working day could deliver. */
      if (latest.getTime() < fastLatest.getTime()) latest = fastLatest;
    } else {
      latest = addWorkingDays(shipDate, maxDays);
    }
    var lDay = latest.getDate();
    var lMon = latest.getMonth();
    var plM = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca', 'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];
    /* Czech has no three-letter month abbreviations ("30. zář" reads as a typo);
       the genitive is the form a Czech date takes: "U tebe do 30. září". */
    var csM = ['ledna', 'února', 'března', 'dubna', 'května', 'června', 'července', 'srpna', 'září', 'října', 'listopadu', 'prosince'];
    var deM = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
    var enM = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    var nlM = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

    /* "U Ciebie do 3 sierpnia" — the panel's native shape: at your place by the
       date, not a logistics verb. Same intent in each tongue. */
    var arrivalText = isPl ? ('U Ciebie do ' + lDay + ' ' + plM[lMon])
      : isCs ? ('U tebe do ' + lDay + '. ' + csM[lMon])
      : isDe ? ('Bis ' + lDay + '. ' + deM[lMon] + ' bei dir')
      : isNl ? ('Bij je vóór ' + lDay + ' ' + nlM[lMon])
      : ('Arrives by ' + lDay + ' ' + enM[lMon]);

    dispatchMsg.textContent = arrivalText;
  }

  /* ═══════════════════════════════════════
     EVENT DELEGATION
  ═══════════════════════════════════════ */
  function initEvents() {
    /* close button + overlay */
    if (closeBtn) listen(closeBtn, 'click', closeDrawer);
    if (overlay) listen(overlay, 'click', closeDrawer);

    /* click outside drawer to close — uses mousedown to avoid race condition
       with optimistic re-renders that remove elements from DOM before click bubbles.
       Skip on touch devices: overlay click already handles outside-tap,
       and mousedown firing alongside touch events can cause accidental closes. */
    listen(document, 'mousedown', (e) => {
      if ('ontouchstart' in window) return;
      if (!drawer.classList.contains('is-open')) return;
      if (drawer.contains(e.target)) return;
      if (overlay && overlay.contains(e.target)) return;
      closeDrawer();
    });

    /* keyboard */
    listen(document, 'keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    listen(drawer, 'keydown', (e) => {
      const qtyVal = e.target.closest && e.target.closest('.qty__val');
      if (!qtyVal) return;
      const row = qtyVal.closest('.item');
      if (!row) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activateQtyInput(qtyVal);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        changeQty(row.dataset.key, e.key === 'ArrowUp' ? 1 : -1);
      }
    });

    /* A pending soft remove must survive leaving the page. The drawer is full
       of real links — the product row, the cross-sells, the free-gift picks —
       and any of them navigates away with the removal still only drawn, not
       written. The customer then pays for the thing they removed.
       Capture phase, so it runs before any handler can stop the click, and no
       preventDefault: the link still goes, the write just leaves first.
       Capture runs BEFORE the undo button's own handler, so the strip is
       excluded first and by element, not by guessing at the target: a click
       that starts inside the undo line is the one click that must never make
       the removal real, whatever it happens to sit under. Only a click that
       will actually navigate — a real href, not the same page anchor — earns
       the commit. */
    listen(drawer, 'click', function(e) {
      if (!_fold) return;
      const target = e.target;
      if (!target || !target.closest) return;
      /* the way back can never be the thing that closes the way back */
      if (target.closest('.item-undo')) return;
      const link = target.closest('a[href]');
      if (!link) return;
      /* an in-page jump or a JS-handled link is not a departure */
      const href = link.getAttribute('href') || '';
      if (!href || href.charAt(0) === '#' || href.indexOf('javascript:') === 0) return;
      if (link.hasAttribute('data-action') && link.getAttribute('data-action') !== 'navigate') return;
      commitFold();
    }, true);

    /* Last-chance backstop for the same fault on unload (tab closed, back
       button, an external link, a form post). A normal fetch dies with the
       document; keepalive outlives it. Every quantity still owed — the fold
       in its window above all — leaves in one absolute /cart/update.js, so
       sending it twice (the page comes back from the bfcache and the queue
       sends it again) lands the same cart. Never throws: a listener that fails here
       takes the unload with it. */
    listen(window, 'pagehide', function() {
      try {
        if (_fold) { const it = _fold; _fold = null; clearTimeout(it.timer); it.fold = false; }
        /* the write on the wire now is included: WebKit cancels in-flight
           fetches with the document, and an absolute target sent twice lands
           the same cart. Only an answer already applied (doneSeq) is left out. */
        const updates = {};
        _intents.forEach(function(it) {
          if (it.kind !== 'qty' || it.fold || it.doneSeq != null) return;
          const line = findLine(_server, it.identity);
          if (line && typeof line.key === 'string' && line.key.indexOf('preview:') !== 0) updates[line.key] = it.qty;
        });
        if (!Object.keys(updates).length) return;
        fetch(CFG.updateUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates }),
          keepalive: true
        }).catch(function() {});
      } catch (e) {}
    });

    /* click delegation inside drawer */
    listen(drawer, 'click', (e) => {
      if (_suppressItemClickUntil > Date.now() && e.target.closest('.item-swipe')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      /* a printed code is a chip you can lift — checked before [data-action] so
         it never competes with apply or remove */
      const copyChip = e.target.closest('[data-copy-code]');
      if (copyChip) {
        e.preventDefault();
        e.stopPropagation();
        haptic(6);
        copyDiscountCode(copyChip);
        return;
      }

      /* tap qty value to type directly */
      const qtyVal = e.target.closest('.qty__val');
      if (qtyVal) { e.preventDefault(); activateQtyInput(qtyVal); return; }

      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      const key = btn.dataset.key;
      const variantId = btn.dataset.variantId;

      switch (action) {
        case 'decrease':
          e.preventDefault();
          haptic(8);
          changeQty(key, -1);
          break;
        case 'increase':
          e.preventDefault();
          haptic(8);
          changeQty(key, 1);
          break;
        case 'remove': {
          e.preventDefault();
          haptic(12);
          /* A keyboard removal hands focus to Undo, because the caret has
             nowhere else to be. A thumb tap must not — moving focus would move
             the page under the thumb. :focus-visible is the honest tell. */
          let viaKeyboard = false;
          try { viaKeyboard = btn.matches(':focus-visible'); } catch (_) {}
          softRemoveItem(key, { keyboard: viaKeyboard });
          break;
        }
        case 'add-upsell':
          e.preventDefault();
          haptic(8);
          addUpsell(variantId);
          break;
        case 'remove-discount':
          e.preventDefault();
          haptic(8);
          removeDiscount();
          break;
        case 'navigate':
          /* let the <a> link work naturally — close cart so page loads cleanly */
          closeDrawer();
          break;
      }
    });

    /* gift chooser selection — selectGift already triggers addGiftToCart when threshold met */
    if (giftPick) {
      listen(giftPick, 'click', (e) => {
        const opt = e.target.closest('.gopt');
        if (!opt) return;
        haptic(8);
        const choice = opt.getAttribute('data-gift2-choice');
        if (choice) selectGift2(choice);
        else selectGift(parseInt(opt.dataset.giftIndex, 10));
      });
      listen(giftPick, 'keydown', (e) => {
        const opt = e.target.closest('.gopt');
        if (!opt) return;
        const options = $$('.gopt', giftPick).filter(function(candidate) { return !candidate.hidden; });
        const current = options.indexOf(opt);
        let next = current;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (current + 1) % options.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (current - 1 + options.length) % options.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = options.length - 1;
        else if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        const target = options[next] || opt;
        const targetChoice = target.getAttribute('data-gift2-choice');
        if (targetChoice) selectGift2(targetChoice);
        else selectGift(parseInt(target.dataset.giftIndex, 10));
        target.focus();
      });
    }

    /* undo button */
    if (undoBtn) listen(undoBtn, 'click', undoRemove);

    /* intercept add-to-cart forms across the site.

       action$= not action= : sections/product-hero.liquid posts to
       {{ routes.cart_add_url }}, which is /cart/add on the ccTLD markets but
       becomes /en/cart/add or /pl/cart/add the moment a locale-path market is
       enabled. An exact match would silently stop intercepting the PDP.

       FormData(form, submitter) not FormData(form) : a submit button is only a
       successful control when it is the submitter, so the one-argument form
       drops it. The mobile drawer's size chips carry the variant on the button
       (`<button type="submit" name="id" value="...">`,
       snippets/mobile-menu-shop-categories.liquid:364-378) with no hidden id
       input, so `id` came back null, this handler returned after having already
       called preventDefault, and the tap added nothing — while the drawer's own
       handler painted a ✓ on the chip claiming it had worked. */
    listen(document, 'submit', async (e) => {
      const form = e.target.closest('form[action$="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      let formData;
      try { formData = new FormData(form, e.submitter); }
      catch (_) { formData = new FormData(form); }
      let id = formData.get('id');
      if (!id && e.submitter && e.submitter.name === 'id') id = e.submitter.value;
      const qty = parseInt(formData.get('quantity') || '1', 10);
      if (!id) return;

      /* collect properties */
      const props = {};
      for (const [k, v] of formData.entries()) {
        if (k.startsWith('properties[')) {
          const propName = k.replace('properties[', '').replace(']', '');
          props[propName] = v;
        }
      }

      /* use unified addAndOpen for optimal speed; a subscription form's
         selling_plan must survive this hop or the cart gets a one-time line */
      const sellingPlan = formData.get('selling_plan') || undefined;
      const previewItem = buildPreviewItem(form, id, qty, Object.keys(props).length ? props : undefined, sellingPlan);
      /* data-quick-add marks every card/mega-menu/mobile-drawer add form that
         is NOT the PDP hero (browse-buy.liquid, browse-kit-card.liquid,
         mobile-menu-shop-categories.liquid, header-mega-cell.liquid — see
         ss-quick-add-feedback.js). The PDP forms route through their own
         submit handlers with stopPropagation and never reach this listener,
         so this is a positive allowlist, never an exclusion guess. */
      const isQuickAdd = !!form.closest('[data-quick-add]');
      const addOpts = isQuickAdd ? { quiet: true, origin: e.submitter || form } : undefined;
      addAndOpen(id, qty, Object.keys(props).length ? props : undefined, previewItem, sellingPlan, addOpts);
    });

    /* intercept cart icon / links — matches /cart, /en/cart, etc. */
    listen(document, 'click', (e) => {
      const link = e.target.closest('#cart-icon-bubble') || e.target.closest('a[href$="/cart"]');
      if (!link) return;
      /* let through if modifier keys held (open in new tab) */
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      /* openDrawer schedules its own reconcile — a second one here raced it */
      openDrawer({ trigger: link });
    });

    /* checkout button */
    if (checkoutBtn) {
      listen(checkoutBtn, 'click', (e) => {
        e.preventDefault();
        proceedToCheckout();
      });
      /* Drawer open already preconnects checkout.shopify.com. Avoid
         prefetching a checkout document before queued cart writes settle. */
    }
  }

  /* ═══════════════════════════════════════
     OFFLINE RESILIENCE BANNER
     Uses actual fetch failure tracking instead of navigator.onLine alone.
     Shows after 2 consecutive failures, hides on first success.
  ═══════════════════════════════════════ */
  let _offlineBanner = null;
  let _fetchFailCount = 0;
  const _OFFLINE_THRESHOLD = 2;

  function showOfflineBanner() {
    if (_offlineBanner && _offlineBanner.hidden) {
      _offlineBanner.hidden = false;
      _offlineBanner.setAttribute('aria-hidden', 'false');
    }
  }
  function hideOfflineBanner() {
    if (_offlineBanner && !_offlineBanner.hidden) {
      _offlineBanner.hidden = true;
      _offlineBanner.setAttribute('aria-hidden', 'true');
    }
  }

  /** Call after every cart API response to track real connectivity */
  function trackNetworkHealth(ok) {
    if (ok) {
      _fetchFailCount = 0;
      hideOfflineBanner();
    } else {
      _fetchFailCount++;
      if (_fetchFailCount >= _OFFLINE_THRESHOLD) showOfflineBanner();
    }
  }

  function initOfflineBanner() {
    _offlineBanner = document.createElement('div');
    _offlineBanner.className = 'offline-banner';
    _offlineBanner.setAttribute('role', 'alert');
    _offlineBanner.textContent = CFG.strings.offline;
    _offlineBanner.hidden = true;
    _offlineBanner.setAttribute('aria-hidden', 'true');
    if (body) body.insertBefore(_offlineBanner, body.firstChild);

    /* browser events as fast hints — but actual fetch tracking is authoritative */
    _onOffline = function() { showOfflineBanner(); };
    _onOnline = function() {
      /* Coming back online restarts the queue at once: whatever it still owes
         leaves now, not at the end of a backoff priced for a dead wire. */
      wakeQueue();
      pump();
      if (drawer.classList.contains('is-open')) readCart({ mirror: true }).catch(function() {});
    };
    listen(window, 'offline', _onOffline);
    listen(window, 'online', _onOnline);
  }

  /* ═══════════════════════════════════════
     ITEM SWIPE-TO-REMOVE (mobile)
  ═══════════════════════════════════════ */
  function initItemSwipe() {
    if (!itemsWrap) return;
    let swipe = null;

    function clearSwipe(animate) {
      const current = swipe;
      swipe = null;
      if (!current) return;
      try { current.container.releasePointerCapture(current.pointerId); } catch (_) {}
      current.container.classList.remove('is-armed');
      if (current.item._ssSwipeResetTimer) clearTimeout(current.item._ssSwipeResetTimer);
      if (animate && !_reducedMotion) {
        const easing = getComputedStyle(drawer).getPropertyValue('--ss-ease-out');
        current.item.style.transition = 'transform ' + DUR_TRANSITION + 'ms ' + easing;
        current.item.style.transform = '';
        if (current.action) {
          current.action.style.transition = 'opacity ' + DUR_TRANSITION + 'ms ' + easing;
          current.action.style.opacity = '0';
        }
        current.item._ssSwipeResetTimer = setTimeout(function() {
          current.item.style.transition = '';
          current.item.style.transform = '';
          if (current.action) { current.action.style.transition = ''; current.action.style.opacity = ''; }
          current.container.classList.remove('is-swiping', 'is-armed');
          current.item._ssSwipeResetTimer = null;
        }, DUR_TRANSITION);
        return;
      }
      current.item.style.transition = '';
      current.item.style.transform = '';
      if (current.action) { current.action.style.transition = ''; current.action.style.opacity = ''; }
      current.container.classList.remove('is-swiping', 'is-armed');
    }

    listen(itemsWrap, 'pointerdown', function(e) {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      if (swipe) clearSwipe(false);
      const container = e.target.closest('.item-swipe');
      if (!container) return;
      if (container.classList.contains('is-soft-removed')) return;
      if (e.target.closest('.qty, .item__remove, .qty__input')) return;
      const item = container.querySelector('.item');
      if (!item) return;
      if (item._ssSwipeResetTimer) { clearTimeout(item._ssSwipeResetTimer); item._ssSwipeResetTimer = null; }
      const swiped = findByKey(state.cart, item.dataset.key);
      swipe = {
        identity: swiped ? lineIdentity(swiped) : null,
        container: container, item: item, action: container.querySelector('.item-swipe__action'),
        pointerId: e.pointerId, startX: e.clientX, startY: e.clientY,
        startedAt: Date.now(), width: item.offsetWidth, axis: null
      };
    });

    listen(itemsWrap, 'pointermove', function(e) {
      if (!swipe || e.pointerId !== swipe.pointerId) return;
      const dx = e.clientX - swipe.startX;
      const dy = e.clientY - swipe.startY;
      if (!swipe.axis) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        /* Vertical movement wins the axis lock and remains native scrolling. */
        if (dx >= 0 || Math.abs(dy) >= Math.abs(dx)) { clearSwipe(false); return; }
        swipe.axis = 'x';
        try { swipe.container.setPointerCapture(e.pointerId); } catch (_) {}
        swipe.container.classList.add('is-swiping');
        swipe.item.style.transition = 'none';
        _suppressItemClickUntil = Date.now() + DUR_REVEAL;
      }
      const maxReveal = Math.min(112, swipe.width * 0.42);
      const offset = Math.max(-maxReveal, Math.min(0, dx));
      const threshold = Math.min(132, swipe.width * 0.34);
      swipe.item.style.transform = 'translateX(' + offset + 'px)';
      swipe.container.classList.toggle('is-armed', Math.abs(dx) > threshold);
      if (swipe.action) swipe.action.style.opacity = String(Math.min(Math.abs(offset) / (swipe.width * 0.3), 1));
      if (e.cancelable) e.preventDefault();
    }, { passive: false });

    listen(itemsWrap, 'pointerup', function(e) {
      if (!swipe || e.pointerId !== swipe.pointerId) return;
      const current = swipe;
      const dx = e.clientX - current.startX;
      const elapsed = Math.max(Date.now() - current.startedAt, 1);
      const threshold = Math.min(132, current.width * 0.34);
      const remove = current.axis === 'x' && (Math.abs(dx) > threshold || (dx < -72 && dx / elapsed < -0.45));
      _suppressItemClickUntil = Date.now() + DUR_REVEAL;
      const key = current.item.dataset.key;
      clearSwipe(!remove);
      if (remove && (current.identity || key)) {
        haptic(12);
        softRemoveItem(current.identity || key);
      }
    });

    listen(itemsWrap, 'pointercancel', function() {
      clearSwipe(false);
      _suppressItemClickUntil = 0;
    });
  }

  /* ═══════════════════════════════════════
     TAP QTY TO TYPE DIRECTLY
  ═══════════════════════════════════════ */
  function activateQtyInput(span) {
    if (span.dataset.editing) return;
    const row = span.closest('.item');
    if (!row) return;
    const key = row.dataset.key;
    /* the line by identity, taken now: a server answer may rename it while
       the field is open */
    const opened = findByKey(state.cart, key);
    const identity = opened ? lineIdentity(opened) : null;
    /* aria-valuenow, not textContent: a roll in flight parks an aria-hidden
       ghost digit inside the span for ~160ms, and textContent would read both */
    const currentQty = parseInt(span.getAttribute('aria-valuenow') || span.textContent, 10) || 1;

    const input = document.createElement('input');
    input.type = 'number';
    input.className = 'qty__input';
    input.value = currentQty;
    input.min = 0;
    input.max = 99;
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('pattern', '[0-9]*');
    input.setAttribute('aria-label', CFG.strings.quantity);

    span.dataset.editing = '1';
    span.style.display = 'none';
    span.parentNode.insertBefore(input, span.nextSibling);
    input.focus();
    input.select();

    function commit() {
      if (input._committed) return;
      input._committed = true;
      let newQty = parseInt(input.value, 10);
      if (!Number.isFinite(newQty)) newQty = currentQty;
      if (newQty < 0) newQty = 0;
      if (newQty > 99) newQty = 99;

      span.textContent = newQty;
      span.setAttribute('aria-valuenow', String(newQty));
      span.style.display = '';
      delete span.dataset.editing;
      if (input.parentNode) input.parentNode.removeChild(input);

      if (newQty === 0) {
        softRemoveItem(identity || key, { keyboard: true });
        return;
      }

      const item = identity ? findLine(state.cart, identity) : findByKey(state.cart, key);
      const stockLimit = stockLimitFor(item);
      if (stockLimit != null && newQty > stockLimit) {
        newQty = stockLimit;
        span.textContent = newQty;
        span.setAttribute('aria-valuenow', String(newQty));
        announce(localeText('Only ' + stockLimit + ' left', 'Zostało tylko ' + stockLimit + ' szt.', 'Skladem jen ' + stockLimit + ' ks', 'Nur noch ' + stockLimit + ' da', 'Nog maar ' + stockLimit + ' over'));
      }
      if (item && newQty !== Number(item.quantity)) {
        if (newQty < Number(item.quantity)) noteUnitFreed(item);
        setLineQty(item, newQty, 0);
      }
      /* the span is a tabindex=0 spinbutton now — a keyboard commit must not
         drop focus to body */
      span.focus();
    }

    input.addEventListener('blur', commit);
    input.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); input.value = currentQty; commit(); }
    });
  }

  /* ═══════════════════════════════════════
     FOCUS TRAP (modal accessibility)
  ═══════════════════════════════════════ */
  function initFocusTrap() {
    listen(document, 'keydown', function(e) {
      if (e.key !== 'Tab' || !drawer.classList.contains('is-open')) return;
      const focusable = Array.from(drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function(el) {
        if (el.hidden || el.getAttribute('aria-hidden') === 'true' || el.getAttribute('aria-disabled') === 'true') return false;
        if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0;
      });
      if (!focusable.length) {
        /* Nothing focusable: the contents gate in snippets/cart-drawer.liquid is
           holding every control hidden until the sheet lands. Keep focus on the
           dialog rather than letting Tab walk out behind the open drawer. */
        e.preventDefault();
        if (document.activeElement !== drawer) drawer.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === drawer || !drawer.contains(document.activeElement)) {
          e.preventDefault(); last.focus();
        }
      } else {
        if (document.activeElement === last || !drawer.contains(document.activeElement)) {
          e.preventDefault(); first.focus();
        }
      }
    });
  }

  /* ═══════════════════════════════════════
     MOBILE TOUCH — LIVE SWIPE-TO-DISMISS
  ═══════════════════════════════════════ */
  function initTouch() {
    let startX = 0, startY = 0, startTime = 0;
    let tracking = false, committed = false;
    let drawerWidth = 0;

    listen(drawer, 'touchstart', function(e) {
      if (!drawer.classList.contains('is-open')) return;
      /* cool-off period: if drawer just opened (< 600ms), ignore swipe tracking
         so the user's tap-through from the add button doesn't accidentally
         register as a swipe-to-close on mobile. */
      if (_drawerOpenedAt && (Date.now() - _drawerOpenedAt) < 600) return;
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      startTime = Date.now();
      committed = false;

      /* blocked zones: interactive / scrollable areas */
      const tag = e.target.tagName;
      const blockedZone = e.target.closest('.upsells__track, .drawer-discount, .cart-cta-sticky, .qty, .gift-pick, .item__remove, .urow, .item, .cart-cta, .cart-cta-sticky__cta, button, a');
      tracking = (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT' && tag !== 'BUTTON' && tag !== 'A' && !blockedZone);
      if (tracking) drawerWidth = drawer.offsetWidth;
    }, { passive: true });

    listen(drawer, 'touchmove', function(e) {
      if (!tracking) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);

      /* dead zone: wait for enough movement to decide swipe vs scroll.
         Raised from 10px to 24px so jittery finger taps don't commit. */
      if (!committed) {
        if (Math.abs(dx) < 24 && dy < 24) return;
        if (dx > 0 && dx > dy * 1.5) {
          /* horizontal rightward swipe — commit to tracking */
          committed = true;
          drawer.classList.add('is-dragging');
          if (overlay) overlay.classList.add('is-dragging');
        } else {
          /* vertical scroll or leftward — bail */
          tracking = false;
          return;
        }
      }

      /* clamp: drawer can't go left of resting position */
      const clampedDx = Math.max(0, dx);
      drawer.style.transform = 'translateX(' + clampedDx + 'px)';

      /* proportional overlay fade */
      if (overlay) {
        const progress = Math.min(clampedDx / drawerWidth, 1);
        overlay.style.opacity = String(1 - progress);
      }

      /* prevent page scroll while swiping the drawer */
      e.preventDefault();
    }, { passive: false });

    listen(drawer, 'touchend', function(e) {
      if (!tracking || !committed) {
        tracking = false;
        committed = false;
        return;
      }

      const t = e.changedTouches[0];
      const dx = Math.max(0, t.clientX - startX);
      const elapsed = Date.now() - startTime;
      const velocity = dx / Math.max(elapsed, 1);

      /* clean up dragging state */
      drawer.classList.remove('is-dragging');
      if (overlay) overlay.classList.remove('is-dragging');
      drawer.style.transform = '';
      if (overlay) overlay.style.opacity = '';

      /* velocity > 0.5 px/ms OR past 40% of drawer width → close */
      if (velocity > 0.5 || dx > drawerWidth * 0.4) {
        closeDrawer();
      }
      /* otherwise: spring back (CSS transition handles the return to translateX(0)) */

      tracking = false;
      committed = false;
    }, { passive: true });
  }


  /* ═══════════════════════════════════════
     INIT
  ═══════════════════════════════════════ */
  let _dispatchInterval = null;
  /* A tab return re-reads the cart only while the drawer is open (a shut
     drawer's read was the homepage's own error group: 25 of 1752 sessions,
     seven days to 2026-09-12, cancelled by the next link tap). Whatever was
     still owed is sent either way. The read is a mirror: it never decides a
     gift. */
  function _onVisibilityChange() {
    if (document.visibilityState !== 'visible') return;
    /* the arrival line reads the clock, not the cart */
    updateDispatch();
    if (_pauseTimer || _parked) { wakeQueue(); pump(); }
    if (!state.cart || !drawer.classList.contains('is-open')) return;
    readCart({ mirror: true }).catch(function() {});
  }

  /* The boot read exists because a served document's cart snapshot can be
     older than the cart: a prefetch, the HTTP cache, a restored document. A
     document the browser fetched from the network for THIS navigation was
     rendered from the session cookie, so its snapshot is live truth.
     deliveryType is Chromium-only; an unanswered question is not a yes. */
  function documentCameFromTheNetwork() {
    try {
      if (!window.performance || typeof performance.getEntriesByType !== 'function') return false;
      var nav = performance.getEntriesByType('navigation')[0];
      if (!nav || typeof nav.deliveryType !== 'string') return false;
      if (nav.deliveryType !== '') return false;
      if (nav.type !== 'navigate' && nav.type !== 'reload') return false;
      if (nav.activationStart) return false;
      return true;
    } catch (e) { return false; }
  }

  function init() {
    initAnnounce();
    initEvents();
    initTouch();
    initItemSwipe();
    initFocusTrap();
    initOfflineBanner();
    updateDispatch();
    listen(document, 'visibilitychange', _onVisibilityChange);
    if (body) {
      listen(body, 'scroll', scheduleScrollAffordance, { passive: true });
      if (window.ResizeObserver) {
        try { keepObserver(new ResizeObserver(function() { scheduleScrollAffordance(); })).observe(body); } catch (e) {}
      }
    }
    if (window.ResizeObserver && footerEl) {
      try { keepObserver(new ResizeObserver(function() { scheduleUndoToastOffset(); })).observe(footerEl); } catch (e) {}
    }
    /* the pool's height moves with the drawer opening, the footer resizing and
       the viewport rotating; each of those is a chance to seat one more row */
    if (window.ResizeObserver) {
      const pool = document.getElementById('sacrasoul-upsells');
      let poolResizeFrame = 0;
      if (pool) {
        try {
          keepObserver(new ResizeObserver(function() {
            /* a queued first paint measures for itself; never write the rows
               inside the observer's own callback (WebKit's "ResizeObserver loop
               completed with undelivered notifications", 2026-09-25) */
            if (_upsellPaintFrame || poolResizeFrame) return;
            poolResizeFrame = requestAnimationFrame(function() {
              poolResizeFrame = 0;
              if (_upsellPaintFrame || !_upsellOrder.length || railAckActive()) return;
              const want = Math.min(upsellRowsThatFit(), _upsellOrder.length);
              if (want !== _upsellRows) { _upsellRows = want; renderUpsellPage(); }
            });
          })).observe(pool);
        } catch (e) {}
      }
    }
    /* Another tab wrote the cart (its cache write lands here as a storage
       event): mirror it. A burst collapses into one read. */
    listen(window, 'storage', function(ev) {
      if (!ev || ev.key !== _CART_CACHE_KEY) return;
      _siblingWrites += 1;
      readCart({ mirror: true }).catch(function() {});
    });
    syncUndoToastOffset();
    /* Read the cache BEFORE painting: the first apply below saves the cache,
       which would make the staleness check trivially true on every load. */
    const cacheBeforePaint = loadCartCache();
    /* Boot handover: a shell the boot script opened for a queued add stays as
       it is until that add paints (the next thing to run); painting the
       pre-add Liquid snapshot over it restored cart--empty (the flash). */
    const bootPendingHandover = drawer.classList.contains('is-open') && drawer.classList.contains('is-totals-pending');
    _server = INITIAL_CART;
    if (!bootPendingHandover) paint();
    else syncSettled();
    /* A tap on the boot shell's checkout carries over: it runs once the
       queued work has been handed to the engine. */
    if (window.__ssCheckoutIntent) {
      window.__ssCheckoutIntent = false;
      setTimeout(function() { if (state.cart && visibleItemCount(state.cart)) proceedToCheckout(); }, 0);
    }
    /* immediate cleanup: strip any Liquid-rendered gift labels on non-gift items */
    if (itemsWrap) $$('.item:not(.item--gift) .item__gift-label', itemsWrap).forEach(function(el) { el.remove(); });
    /* Check the served snapshot against live truth once, at idle — unless that
       is provably a no-op: the engine's last cached cart already agrees with
       it, or there is no cache and this document came from the network. */
    const snapshotIsLiveTruth = !cacheBeforePaint && documentCameFromTheNetwork();
    if (!snapshotIsLiveTruth && !cartDataMatches(cacheBeforePaint, INITIAL_CART)) {
      /* moot once any server cart has landed since boot (an open's read, a
         write's answer): that cart already replaced the snapshot */
      const idleRead = function() { if (!_lastReadAt) readCart({}).catch(function() {}); };
      if ('requestIdleCallback' in window) requestIdleCallback(idleRead, { timeout: 4000 });
      else setTimeout(idleRead, 1500);
    }
    listen(window, 'resize', syncUndoToastOffset);
    _dispatchInterval = setInterval(updateDispatch, 60000);
  }

  /* deep compare two carts: items, quantities, prices, totals */
  function cartDataMatches(a, b) {
    if (!a || !b) return false;
    if (a.item_count !== b.item_count) return false;
    if (a.total_price !== b.total_price) return false;
    if (a.items.length !== b.items.length) return false;
    var mapA = {};
    a.items.forEach(function(item) { mapA[item.key] = item; });
    return b.items.every(function(item) {
      var other = mapA[item.key];
      return other
        && other.quantity === item.quantity
        && other.final_line_price === item.final_line_price
        && other.final_price === item.final_price;
    });
  }

  /* Release everything this engine holds: every listener (one signal),
     every observer, the timers and the sibling channel. After it, nothing
     new is queued or sent. */
  function destroy() {
    _destroyed = true;
    if (_life) _life.abort();
    _observers.splice(0).forEach(function(o) { try { o.disconnect(); } catch (e) {} });
    disconnectSiblingChannel();
    if (_dispatchInterval) { clearInterval(_dispatchInterval); _dispatchInterval = null; }
    if (_undoHideTimer) { clearTimeout(_undoHideTimer); _undoHideTimer = null; }
    if (_pauseTimer) { clearTimeout(_pauseTimer); _pauseTimer = null; }
    clearTimeout(_qtyTimer); _qtyTimer = null;
    clearTimeout(_openReadTimer); _openReadTimer = null;
    clearTimeout(_giftThrottleTimer);
    if (_fold) clearTimeout(_fold.timer);
    _jobs.length = 0;
    document.removeEventListener('visibilitychange', _onVisibilityChange);
    document.removeEventListener('sacrasoul:preorder-catalog-ready', onPreorderCatalogReady);
    window.removeEventListener('resize', syncUndoToastOffset);
    if (_onOffline) window.removeEventListener('offline', _onOffline);
    if (_onOnline) window.removeEventListener('online', _onOnline);
  }

  /* run on DOM ready */
  if (document.readyState === 'loading') {
    listen(document, 'DOMContentLoaded', init);
  } else {
    init();
  }

  /* ═══════════════════════════════════════
     PUBLIC API
  ═══════════════════════════════════════ */
  window.SacrasoulCart = {
    open: openDrawer,
    close: closeDrawer,
    /* a read through the queue; resolves with the cart on screen, or null
       when it could not read. { silent: true } is a background read: its
       failure is filed non-fatal and never painted at the shopper. */
    refresh: function(options) { return readCart({ visible: !(options && options.silent) }); },
    /* Attributes-only cart write, so assets/ss-attribution.js never POSTs
       /cart/update.js behind the queue's back. Without Web Locks a new
       background attribution write is refused rather than race a quantity
       change in another tab; a deletion-only write (consent withdrawn) still
       goes through the in-document queue. */
    setAttributes: (attributes, options) => {
      if (typeof navigator === 'undefined' || !navigator.locks ||
          typeof navigator.locks.request !== 'function') {
        const deletionOnly = attributes && Object.keys(attributes).length &&
          Object.keys(attributes).every((key) => attributes[key] === '');
        if (!deletionOnly) return Promise.reject(new Error('cross-tab cart lock unavailable'));
      }
      return writeCartUpdate(
        { attributes: attributes },
        'attributes',
        options && typeof options.guard === 'function' ? options.guard : null
      );
    },
    reconcileRestore: reconcileRestore,
    addAndOpen: addAndOpen,
    selectGift: selectGift,
    addUpsell: addUpsell,
    buildPreview: buildPreviewItem,
    getState: () => state,
    /* The stable settle interface for proofs and tools: nothing owed to
       Shopify, nothing in flight, no checkout under way. The same answer is
       on the drawer as data-settled. */
    isSettled: isSettled,
    CART_READ_ABORT_MS: CART_READ_ABORT_MS,
    CART_MUTATION_ABORT_MS: CART_MUTATION_ABORT_MS,
    bumpBadge: optimisticBubbleIncrement,
    destroy: destroy
  };

})();
